import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import * as React from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { Loader2, ChevronDown, X, Check, Minus, ChevronRight, ChevronLeft, Calendar as Calendar$1, Search, ChevronFirst, ChevronLast, Circle, ArrowUp, ArrowDown, ChevronsUpDown, SlidersHorizontal, AlertCircle, Sparkles, MoreHorizontal, PanelLeftOpen, PanelLeftClose, Sun, Moon } from 'lucide-react';
import * as RadixSeparator from '@radix-ui/react-separator';
import * as LabelPrimitive from '@radix-ui/react-label';
import * as RadixSelect from '@radix-ui/react-select';
import * as Popover$1 from '@radix-ui/react-popover';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import * as RadixRadio from '@radix-ui/react-radio-group';
import * as RadixSwitch from '@radix-ui/react-switch';
import * as RadixSlider from '@radix-ui/react-slider';
import { FormProvider, useFormContext, Controller } from 'react-hook-form';
import { DayPicker } from 'react-day-picker';
import * as RadixAccordion from '@radix-ui/react-accordion';
import { Command as Command$1 } from 'cmdk';
import * as RadixMenubar from '@radix-ui/react-menubar';
import * as RadixTabs from '@radix-ui/react-tabs';
import * as RadixDialog from '@radix-ui/react-dialog';
import * as RadixAlertDialog from '@radix-ui/react-alert-dialog';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import * as RadixCtxMenu from '@radix-ui/react-context-menu';
import * as RadixAvatar from '@radix-ui/react-avatar';
import * as RadixProgress from '@radix-ui/react-progress';
import * as RadixToast from '@radix-ui/react-toast';
import { YAxis, XAxis, CartesianGrid, Tooltip as Tooltip$1 } from 'recharts';

const colors = {
    neutral: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a',
    },
    primary: {
        50: '#eef3fa',
        100: '#d6e2f2',
        200: '#adc4e4',
        300: '#7ba0d0',
        400: '#4a76b4',
        500: '#012d69',
        600: '#012456',
        700: '#011d45',
        800: '#011633',
        900: '#010f22',
    },
    accent: {
        50: '#eef3fa',
        100: '#d6e2f2',
        200: '#adc4e4',
        300: '#7ba0d0',
        400: '#4a76b4',
        500: '#012d69',
        600: '#012456',
        700: '#011d45',
        800: '#011633',
        900: '#010f22',
    },
    brand: {
        red: '#e40521',
        redStrong: '#b8041a',
        redSubtle: '#fef2f3',
        navy: '#012d69',
    },
    gradient: {
        primary: 'linear-gradient(135deg, #012d69 0%, #011d45 100%)',
        aiBanner: 'linear-gradient(135deg, #2B3674 0%, #4A5899 100%)',
        sidebar: 'linear-gradient(180deg, #012d69 0%, #011d45 60%, #011633 100%)',
    },
    semantic: {
        success: { default: '#10b981', subtle: '#ecfdf5', foreground: '#047857', border: '#a7f3d0' },
        warning: { default: '#f59e0b', subtle: '#fffbeb', foreground: '#b45309', border: '#fde68a' },
        destructive: { default: '#dc2626', subtle: '#fef2f2', foreground: '#b91c1c', border: '#fecaca' },
        info: { default: '#0284c7', subtle: '#eff6ff', foreground: '#1d4ed8', border: '#bfdbfe' },
        neutral: { default: '#64748b', subtle: '#f1f5f9', foreground: '#334155', border: '#e2e8f0' },
    },
    mode: {
        ocean: { default: '#0e7490', subtle: '#ecfeff', foreground: '#0e7490', border: '#a5f3fc' },
        air: { default: '#c2410c', subtle: '#fff7ed', foreground: '#c2410c', border: '#fed7aa' },
        truck: { default: '#047857', subtle: '#ecfdf5', foreground: '#047857', border: '#a7f3d0' },
        rail: { default: '#6b21a8', subtle: '#faf5ff', foreground: '#6b21a8', border: '#e9d5ff' },
        courier: { default: '#b91c1c', subtle: '#fef2f2', foreground: '#b91c1c', border: '#fecaca' },
    },
};

const spacing = {
    0: '0px',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    8: '32px',
    10: '40px',
    12: '48px',
    16: '64px',
    20: '80px',
    24: '96px',
};
const semanticSpacing = {
    pageContainer: spacing[6],
    stackVertical: spacing[6],
    cardPadding: spacing[5],
    tableCell: spacing[3],
    inlineGap: spacing[2],
    buttonGroup: spacing[3],
    headingH1: spacing[6],
    headingH2: spacing[4],
    headingH3: spacing[3],
};

const radius = {
    none: '0px',
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '10px',
    '2xl': '12px',
    full: '9999px',
};

const shadow = {
    none: 'none',
    xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.08), 0 4px 6px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.10), 0 8px 10px rgba(0, 0, 0, 0.04)',
};

const fonts = {
    body: 'Geist, system-ui, -apple-system, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
};
const fontSize = {
    xs: '10px',
    sm: '12px',
    base: '14px',
    md: '16px',
    lg: '16px',
    xl: '18px',
    '2xl': '24px',
    '3xl': '30px',
};
const fontWeight = {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
};
const lineHeight = {
    tight: '1.2',
    snug: '1.375',
    normal: '1.5',
    relaxed: '1.6',
};
const tracking = {
    tight: '-0.02em',
    snug: '-0.01em',
    normal: '0',
    wide: '0.04em',
    wider: '0.08em',
};
const typography = {
    display: { fontFamily: fonts.body, fontSize: '30px', lineHeight: '36px', fontWeight: 700, letterSpacing: '-0.02em', textTransform: 'none' },
    h1: { fontFamily: fonts.body, fontSize: '24px', lineHeight: '32px', fontWeight: 700, letterSpacing: '-0.01em', textTransform: 'none' },
    h2: { fontFamily: fonts.body, fontSize: '18px', lineHeight: '28px', fontWeight: 600, letterSpacing: '0', textTransform: 'none' },
    h3: { fontFamily: fonts.body, fontSize: '16px', lineHeight: '24px', fontWeight: 600, letterSpacing: '0', textTransform: 'none' },
    bodyLg: { fontFamily: fonts.body, fontSize: '16px', lineHeight: '24px', fontWeight: 400, letterSpacing: '0', textTransform: 'none' },
    body: { fontFamily: fonts.body, fontSize: '14px', lineHeight: '20px', fontWeight: 400, letterSpacing: '0', textTransform: 'none' },
    label: { fontFamily: fonts.body, fontSize: '12px', lineHeight: '16px', fontWeight: 400, letterSpacing: '0', textTransform: 'none' },
    caption: { fontFamily: fonts.body, fontSize: '12px', lineHeight: '16px', fontWeight: 400, letterSpacing: '0', textTransform: 'none' },
    eyebrow: { fontFamily: fonts.body, fontSize: '10px', lineHeight: '14px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' },
    code: { fontFamily: fonts.mono, fontSize: '12px', lineHeight: '18px', fontWeight: 500, letterSpacing: '0', textTransform: 'none' },
};

const duration = {
    instant: '0ms',
    fast: '150ms',
    default: '200ms',
    slow: '300ms',
    slower: '450ms',
};
const easing = {
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    spring: 'cubic-bezier(0.5, -0.5, 0.1, 1.5)',
};
const keyframes = {
    'fade-in-up': { '0%': { opacity: '0', transform: 'translateY(8px)' }, '100%': { opacity: '1', transform: 'translateY(0)' } },
    'slide-down': { '0%': { 'grid-template-rows': '0fr' }, '100%': { 'grid-template-rows': '1fr' } },
    'slide-up': { '0%': { 'grid-template-rows': '1fr' }, '100%': { 'grid-template-rows': '0fr' } },
    'accordion-down': { from: { height: '0' }, to: { height: 'var(--radix-accordion-content-height)' } },
    'accordion-up': { from: { height: 'var(--radix-accordion-content-height)' }, to: { height: '0' } },
    'shimmer': { '0%': { 'background-position': '-200% 0' }, '100%': { 'background-position': '200% 0' } },
};

const zIndex = {
    base: 0,
    dropdown: 1000,
    sticky: 1100,
    overlay: 1200,
    dialog: 1300,
    popover: 1400,
    toast: 1500,
};

const lightTheme = {
    background: '#f8fafc',
    foreground: colors.neutral[900],
    surface: '#ffffff',
    surfaceForeground: colors.neutral[900],
    surfaceRaised: '#ffffff',
    border: colors.neutral[200],
    borderStrong: colors.neutral[300],
    muted: colors.neutral[100],
    mutedForeground: colors.neutral[500],
    primary: colors.primary[500],
    primaryForeground: '#ffffff',
    primarySubtle: colors.primary[50],
    accent: colors.accent[500],
    accentForeground: '#ffffff',
    ring: colors.primary[500],
    successDefault: colors.semantic.success.default,
    successSubtle: colors.semantic.success.subtle,
    successForeground: colors.semantic.success.foreground,
    successBorder: colors.semantic.success.border,
    warningDefault: colors.semantic.warning.default,
    warningSubtle: colors.semantic.warning.subtle,
    warningForeground: colors.semantic.warning.foreground,
    warningBorder: colors.semantic.warning.border,
    destructiveDefault: colors.semantic.destructive.default,
    destructiveSubtle: colors.semantic.destructive.subtle,
    destructiveForeground: colors.semantic.destructive.foreground,
    destructiveBorder: colors.semantic.destructive.border,
    infoDefault: colors.semantic.info.default,
    infoSubtle: colors.semantic.info.subtle,
    infoForeground: colors.semantic.info.foreground,
    infoBorder: colors.semantic.info.border,
    neutralDefault: colors.semantic.neutral.default,
    neutralSubtle: colors.semantic.neutral.subtle,
    neutralForeground: colors.semantic.neutral.foreground,
    neutralBorder: colors.semantic.neutral.border,
    sidebarBackground: colors.primary[500],
    sidebarForeground: colors.neutral[300],
    sidebarPrimary: colors.brand.red,
    sidebarAccent: colors.brand.red,
    sidebarAccentForeground: '#ffffff',
    sidebarBorder: colors.primary[700],
    sidebarRing: colors.brand.red,
};

const darkTheme = {
    background: colors.neutral[900],
    foreground: colors.neutral[100],
    surface: colors.neutral[800],
    surfaceForeground: colors.neutral[100],
    surfaceRaised: colors.neutral[700],
    border: 'rgba(255, 255, 255, 0.06)',
    borderStrong: 'rgba(255, 255, 255, 0.12)',
    muted: colors.neutral[800],
    mutedForeground: colors.neutral[400],
    primary: colors.primary[300],
    primaryForeground: colors.neutral[900],
    primarySubtle: 'rgba(123, 160, 208, 0.14)',
    accent: colors.accent[300],
    accentForeground: colors.neutral[900],
    ring: colors.primary[300],
    successDefault: '#34d399',
    successSubtle: 'rgba(16, 185, 129, 0.12)',
    successForeground: '#a7f3d0',
    successBorder: 'rgba(16, 185, 129, 0.32)',
    warningDefault: '#fbbf24',
    warningSubtle: 'rgba(245, 158, 11, 0.12)',
    warningForeground: '#fde68a',
    warningBorder: 'rgba(245, 158, 11, 0.32)',
    destructiveDefault: '#f87171',
    destructiveSubtle: 'rgba(220, 38, 38, 0.12)',
    destructiveForeground: '#fecaca',
    destructiveBorder: 'rgba(220, 38, 38, 0.32)',
    infoDefault: '#38bdf8',
    infoSubtle: 'rgba(2, 132, 199, 0.12)',
    infoForeground: '#bae6fd',
    infoBorder: 'rgba(2, 132, 199, 0.32)',
    neutralDefault: colors.neutral[400],
    neutralSubtle: 'rgba(255, 255, 255, 0.04)',
    neutralForeground: colors.neutral[300],
    neutralBorder: 'rgba(255, 255, 255, 0.08)',
    sidebarBackground: colors.primary[900],
    sidebarForeground: colors.neutral[300],
    sidebarPrimary: colors.brand.red,
    sidebarAccent: colors.brand.red,
    sidebarAccentForeground: '#ffffff',
    sidebarBorder: 'rgba(255, 255, 255, 0.06)',
    sidebarRing: colors.brand.red,
};

const themes = {
    light: lightTheme,
    dark: darkTheme,
};

function cn(...inputs) {
    return twMerge(clsx(inputs));
}

const SPACING_PAGE_SECTION = 'mb-6';
const SPACING_PAGE_CONTAINER = 'p-6';
const SPACING_SECTION_ELEMENT = 'mb-4';
const SPACING_CARD_PADDING = 'p-6';
const SPACING_TABLE_CELL = 'p-4';
const SPACING_TABLE_TO_PAGINATION = 'mt-6';
const SPACING_KPI_GRID_GAP = 'gap-4';
const SPACING_KPI_GRID_GAP_PREMIUM = 'gap-6';
const SPACING_STACK_VERTICAL = 'space-y-6';
const SPACING_INLINE_GAP = 'gap-2';
const SPACING_BUTTON_GROUP_GAP = 'gap-3';
const SPACING_HEADING_H1 = 'mb-6';
const SPACING_HEADING_H2 = 'mb-4';
const SPACING_HEADING_H3 = 'mb-3';
const SPACING_WORKFLOW_STEPPER = 'mb-6';
const SPACING_FORM_FIELD = 'mb-4';
const SPACING_INFO_BOX_PADDING = 'p-3';
const SPACING_PRESETS = {
    page: {
        container: SPACING_PAGE_CONTAINER,
        section: SPACING_PAGE_SECTION,
    },
    card: {
        padding: SPACING_CARD_PADDING,
    },
    grid: {
        kpi: SPACING_KPI_GRID_GAP,
        kpiPremium: SPACING_KPI_GRID_GAP_PREMIUM,
    },
    table: {
        cell: SPACING_TABLE_CELL,
        toPagination: SPACING_TABLE_TO_PAGINATION,
    },
    heading: {
        h1: SPACING_HEADING_H1,
        h2: SPACING_HEADING_H2,
        h3: SPACING_HEADING_H3,
    },
    workflow: {
        stepper: SPACING_WORKFLOW_STEPPER,
        formField: SPACING_FORM_FIELD,
    },
};

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000000;
let count = 0;
function genId() {
    count = (count + 1) % Number.MAX_SAFE_INTEGER;
    return count.toString();
}
const toastTimeouts = new Map();
const addToRemoveQueue = (toastId) => {
    if (toastTimeouts.has(toastId))
        return;
    const timeout = setTimeout(() => {
        toastTimeouts.delete(toastId);
        dispatch({ type: 'REMOVE_TOAST', toastId });
    }, TOAST_REMOVE_DELAY);
    toastTimeouts.set(toastId, timeout);
};
const reducer = (state, action) => {
    switch (action.type) {
        case 'ADD_TOAST':
            return { ...state, toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT) };
        case 'UPDATE_TOAST':
            return {
                ...state,
                toasts: state.toasts.map((t) => t.id === action.toast.id ? { ...t, ...action.toast } : t),
            };
        case 'DISMISS_TOAST': {
            const { toastId } = action;
            if (toastId) {
                addToRemoveQueue(toastId);
            }
            else {
                state.toasts.forEach((t) => addToRemoveQueue(t.id));
            }
            return {
                ...state,
                toasts: state.toasts.map((t) => t.id === toastId || toastId === undefined ? { ...t, open: false } : t),
            };
        }
        case 'REMOVE_TOAST':
            if (action.toastId === undefined)
                return { ...state, toasts: [] };
            return { ...state, toasts: state.toasts.filter((t) => t.id !== action.toastId) };
    }
};
const listeners = [];
let memoryState = { toasts: [] };
function dispatch(action) {
    memoryState = reducer(memoryState, action);
    listeners.forEach((listener) => listener(memoryState));
}
function toast({ variant, ...props }) {
    const id = genId();
    const tone = props.tone ?? (variant && variant !== 'default' ? variant : undefined);
    const update = (p) => dispatch({ type: 'UPDATE_TOAST', toast: { ...p, id } });
    const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id });
    dispatch({
        type: 'ADD_TOAST',
        toast: {
            ...props,
            tone,
            id,
            open: true,
            onOpenChange: (open) => {
                if (!open)
                    dismiss();
            },
        },
    });
    return { id, dismiss, update };
}
function useToast() {
    const [state, setState] = React.useState(memoryState);
    React.useEffect(() => {
        listeners.push(setState);
        return () => {
            const index = listeners.indexOf(setState);
            if (index > -1)
                listeners.splice(index, 1);
        };
    }, [state]);
    return {
        ...state,
        toast,
        dismiss: (toastId) => toastId !== undefined
            ? dispatch({ type: 'DISMISS_TOAST', toastId })
            : dispatch({ type: 'DISMISS_TOAST' }),
    };
}

const Icon = React.forwardRef(({ icon: LucideComp, size = 16, decorative = true, className, ...rest }, ref) => {
    const ariaProps = decorative
        ? { 'aria-hidden': true }
        : { role: 'img' };
    return (jsx(LucideComp, { ref: ref, width: size, height: size, strokeWidth: 2, className: cn('shrink-0', className), ...ariaProps, ...rest }));
});
Icon.displayName = 'Icon';

const buttonStyles = cva([
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium',
    'transition-all duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
], {
    variants: {
        variant: {
            primary: 'bg-primary text-primary-foreground shadow-sm hover:opacity-90 active:scale-[0.98]',
            secondary: 'bg-surface text-surface-foreground border border-border shadow-xs hover:bg-muted',
            ghost: 'bg-transparent text-foreground hover:bg-muted',
            outline: 'bg-transparent text-foreground border border-border-strong hover:bg-muted',
            destructive: 'bg-destructive text-white shadow-sm hover:opacity-90 active:scale-[0.98]',
            subtle: 'bg-primary-subtle text-primary hover:bg-primary-subtle/80',
        },
        size: {
            xs: 'h-7  px-2   text-xs',
            sm: 'h-8  px-3   text-sm',
            md: 'h-9  px-3.5 text-sm',
            lg: 'h-10 px-4   text-base',
        },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
});
const Button = React.forwardRef(({ className, variant, size, asChild = false, isLoading = false, leftIcon, rightIcon, children, disabled, type = 'button', ...rest }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const content = (jsxs(Fragment, { children: [isLoading ? jsx(Icon, { icon: Loader2, size: 14, className: "animate-spin" }) : leftIcon, children, !isLoading && rightIcon] }));
    return (jsx(Comp, { ref: ref, type: type, "aria-busy": isLoading || undefined, disabled: disabled || isLoading, className: cn(buttonStyles({ variant, size }), className), ...rest, children: content }));
});
Button.displayName = 'Button';

const iconButtonStyles = cva([
    'inline-flex items-center justify-center rounded-lg transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
], {
    variants: {
        variant: {
            primary: 'bg-primary text-primary-foreground shadow-sm hover:opacity-90',
            secondary: 'bg-surface text-surface-foreground border border-border hover:bg-muted',
            ghost: 'bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
            destructive: 'bg-transparent text-destructive hover:bg-destructive-subtle',
        },
        size: {
            sm: 'h-7 w-7',
            md: 'h-8 w-8',
            lg: 'h-10 w-10',
        },
    },
    defaultVariants: { variant: 'ghost', size: 'md' },
});
const ICON_SIZE_FOR_BUTTON = { sm: 12, md: 16, lg: 20 };
const IconButton = React.forwardRef(({ icon, variant, size = 'md', className, type = 'button', ...rest }, ref) => (jsx("button", { ref: ref, type: type, className: cn(iconButtonStyles({ variant, size }), className), ...rest, children: jsx(Icon, { icon: icon, size: ICON_SIZE_FOR_BUTTON[size ?? 'md'] }) })));
IconButton.displayName = 'IconButton';

const Separator$4 = React.forwardRef(({ className, orientation = 'horizontal', decorative = true, ...rest }, ref) => (jsx(RadixSeparator.Root, { ref: ref, orientation: orientation, decorative: decorative, className: cn('shrink-0 bg-border', orientation === 'horizontal' ? 'h-px w-full' : 'h-full w-px', className), ...rest })));
Separator$4.displayName = 'Separator';

const inputStyles = cva([
    'flex w-full rounded-lg border border-border bg-surface text-surface-foreground transition-colors',
    'placeholder:text-muted-foreground',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive',
], {
    variants: {
        size: {
            sm: 'h-8 px-2.5 text-sm',
            md: 'h-9 px-3   text-sm',
            lg: 'h-10 px-3.5 text-base',
        },
    },
    defaultVariants: { size: 'md' },
});
const Input$1 = React.forwardRef(({ className, size, startContent, endContent, isInvalid, type = 'text', ...rest }, ref) => {
    const hasAffix = startContent || endContent;
    const input = (jsx("input", { ref: ref, type: type, "aria-invalid": isInvalid || undefined, className: cn(inputStyles({ size }), startContent && 'pl-9', endContent && 'pr-9', !hasAffix && className), ...rest }));
    if (!hasAffix)
        return input;
    return (jsxs("div", { className: cn('relative inline-flex w-full items-center', className), children: [startContent && jsx("span", { className: "pointer-events-none absolute left-3 inline-flex text-muted-foreground", children: startContent }), input, endContent && jsx("span", { className: "pointer-events-none absolute right-3 inline-flex text-muted-foreground", children: endContent })] }));
});
Input$1.displayName = 'Input';

const Textarea = React.forwardRef(({ className, rows = 3, isInvalid, ...rest }, ref) => (jsx("textarea", { ref: ref, rows: rows, "aria-invalid": isInvalid || undefined, className: cn('flex w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-surface-foreground transition-colors', 'placeholder:text-muted-foreground resize-y', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'disabled:cursor-not-allowed disabled:opacity-50', 'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive', className), ...rest })));
Textarea.displayName = 'Textarea';

const Label$2 = React.forwardRef(({ className, children, isRequired, ...rest }, ref) => (jsxs(LabelPrimitive.Root, { ref: ref, className: cn('text-sm font-medium text-foreground', className), ...rest, children: [children, isRequired && jsx("span", { className: "ml-0.5 text-destructive", "aria-hidden": true, children: "*" })] })));
Label$2.displayName = 'Label';

const MultiCtx = React.createContext(null);
const useMulti = () => {
    const c = React.useContext(MultiCtx);
    if (!c)
        throw new Error('Select.* (multi) must be inside <Select.Root multiple>');
    return c;
};
function MultiRoot({ value: controlled, defaultValue = [], onValueChange, children }) {
    const [internal, setInternal] = React.useState(defaultValue);
    const value = controlled ?? internal;
    const [options, setOptions] = React.useState(new Map());
    const registerOption = React.useCallback((v, label) => {
        setOptions(prev => {
            if (prev.get(v) === label)
                return prev;
            const next = new Map(prev);
            next.set(v, label);
            return next;
        });
    }, []);
    const commit = React.useCallback((next) => {
        if (controlled === undefined)
            setInternal(next);
        onValueChange?.(next);
    }, [controlled, onValueChange]);
    const toggle = React.useCallback((v) => {
        commit(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
    }, [value, commit]);
    const remove = React.useCallback((v) => commit(value.filter(x => x !== v)), [value, commit]);
    const ctx = React.useMemo(() => ({ value, toggle, remove, options, registerOption }), [value, toggle, remove, options, registerOption]);
    return (jsx(MultiCtx.Provider, { value: ctx, children: jsx(Popover$1.Root, { children: children }) }));
}
const MultiTrigger = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(Popover$1.Trigger, { ref: ref, className: cn('flex min-h-9 w-full items-center justify-between gap-2 rounded-lg border border-border bg-surface px-2 py-1 text-sm text-surface-foreground', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', className), ...rest, children: [children, jsx(Icon, { icon: ChevronDown, size: 14, className: "text-muted-foreground" })] })));
MultiTrigger.displayName = 'Select.Trigger';
function MultiValue() {
    const { value, options, remove } = useMulti();
    if (value.length === 0)
        return jsx("span", { className: "text-muted-foreground", children: "Select\u2026" });
    return (jsx("div", { className: "flex flex-wrap gap-1", children: value.map(v => (jsxs("span", { className: "inline-flex items-center gap-1 rounded-md bg-primary-subtle px-1.5 py-0.5 text-xs text-primary", children: [options.get(v) ?? v, jsx("button", { type: "button", onClick: (e) => { e.stopPropagation(); remove(v); }, "aria-label": `Remove ${v}`, children: jsx(Icon, { icon: X, size: 12 }) })] }, v))) }));
}
const MultiContent = React.forwardRef(({ className, children, ...rest }, _ref) => (jsxs(Fragment, { children: [jsx("div", { "aria-hidden": "true", className: "hidden", children: children }), jsx(Popover$1.Portal, { children: jsx(Popover$1.Content, { sideOffset: 4, className: cn('z-popover min-w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-lg border border-border bg-surface p-1 shadow-md', className), ...rest, children: children }) })] })));
MultiContent.displayName = 'Select.Content';
function MultiItem({ value, children, className }) {
    const { value: selected, toggle, registerOption } = useMulti();
    React.useEffect(() => registerOption(value, children), [value, children, registerOption]);
    const checked = selected.includes(value);
    return (jsxs("button", { type: "button", role: "menuitemcheckbox", "aria-checked": checked, onClick: () => toggle(value), className: cn('flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground hover:bg-muted', className), children: [jsx("span", { className: "inline-flex h-4 w-4 items-center justify-center rounded border border-border bg-surface", children: checked && jsx(Icon, { icon: Check, size: 12 }) }), children] }));
}

const Group$3 = RadixSelect.Group;
const Value = RadixSelect.Value;
const SingleTrigger = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixSelect.Trigger, { ref: ref, className: cn('flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-border bg-surface px-3 text-sm text-surface-foreground transition-colors', 'placeholder:text-muted-foreground', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'disabled:cursor-not-allowed disabled:opacity-50', 'data-[placeholder]:text-muted-foreground', className), ...rest, children: [children, jsx(RadixSelect.Icon, { asChild: true, children: jsx(Icon, { icon: ChevronDown, size: 14, className: "text-muted-foreground" }) })] })));
SingleTrigger.displayName = 'Select.Trigger';
const SingleContent = React.forwardRef(({ className, children, position = 'popper', ...rest }, ref) => (jsx(RadixSelect.Portal, { children: jsx(RadixSelect.Content, { ref: ref, position: position, sideOffset: 4, className: cn('z-popover overflow-hidden rounded-lg border border-border bg-surface text-surface-foreground shadow-md', 'data-[state=open]:animate-fade-in-up', 'min-w-[var(--radix-select-trigger-width)]', className), ...rest, children: jsx(RadixSelect.Viewport, { className: "p-1", children: children }) }) })));
SingleContent.displayName = 'Select.Content';
const SingleItem = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixSelect.Item, { ref: ref, className: cn('relative flex cursor-default select-none items-center rounded-md py-1.5 pl-8 pr-2 text-sm text-foreground outline-none', 'data-[highlighted]:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50', className), ...rest, children: [jsx("span", { className: "absolute left-2 inline-flex h-4 w-4 items-center justify-center", children: jsx(RadixSelect.ItemIndicator, { children: jsx(Icon, { icon: Check, size: 12 }) }) }), jsx(RadixSelect.ItemText, { children: children })] })));
SingleItem.displayName = 'Select.Item';
function SelectRoot(props) {
    if (props.multiple) {
        const multiValue = props.value;
        const multiDefault = props.defaultValue ?? [];
        const multiChange = props.onValueChange;
        return (jsx(MultiRoot, { ...(multiValue !== undefined ? { value: multiValue } : {}), defaultValue: multiDefault, ...(multiChange !== undefined ? { onValueChange: multiChange } : {}), children: props.children }));
    }
    const singleValue = props.value;
    const singleDefault = props.defaultValue;
    const singleChange = props.onValueChange;
    return (jsx(RadixSelect.Root, { ...(singleValue !== undefined ? { value: singleValue } : {}), ...(singleDefault !== undefined ? { defaultValue: singleDefault } : {}), ...(singleChange !== undefined ? { onValueChange: singleChange } : {}), children: props.children }));
}
const TriggerDispatch = (props) => props.multiple ? jsx(MultiTrigger, { ...props }) : jsx(SingleTrigger, { ...props });
const ContentDispatch = (props) => props.multiple ? jsx(MultiContent, { ...props }) : jsx(SingleContent, { ...props });
const ItemDispatch = (props) => props.multiple
    ? jsx(MultiItem, { value: props.value, className: props.className, children: props.children })
    : jsx(SingleItem, { ...props });
const SelectRootWrapper = Object.assign(SelectRoot, {
    Root: SelectRoot,
    Group: Group$3,
    Value,
    MultiValue,
    Trigger: TriggerDispatch,
    Content: ContentDispatch,
    Item: ItemDispatch,
});
const Select = SelectRootWrapper;
const SelectValue = Value;
const SelectTrigger = TriggerDispatch;
const SelectContent = ContentDispatch;
const SelectItem = ItemDispatch;
const SelectGroup = Group$3;
const SelectMultiValue = MultiValue;

const Checkbox = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixCheckbox.Root, { ref: ref, className: cn('peer inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border bg-surface shadow-xs transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground', 'data-[state=indeterminate]:bg-primary data-[state=indeterminate]:border-primary data-[state=indeterminate]:text-primary-foreground', 'disabled:cursor-not-allowed disabled:opacity-50', className), ...rest, children: jsx(RadixCheckbox.Indicator, { className: "flex items-center justify-center", children: jsx(Icon, { icon: rest.checked === 'indeterminate' ? Minus : Check, size: 12 }) }) })));
Checkbox.displayName = 'Checkbox';

const Root$8 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixRadio.Root, { ref: ref, className: cn('grid gap-2', className), ...rest })));
Root$8.displayName = 'RadioGroup.Root';
const Item$6 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixRadio.Item, { ref: ref, className: cn('inline-flex h-4 w-4 items-center justify-center rounded-full border border-border bg-surface shadow-xs', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'disabled:cursor-not-allowed disabled:opacity-50', 'data-[state=checked]:border-primary', className), ...rest, children: jsx(RadixRadio.Indicator, { className: "h-2 w-2 rounded-full bg-primary" }) })));
Item$6.displayName = 'RadioGroup.Item';
const RadioGroup$2 = Object.assign(Root$8, {
    Root: Root$8,
    Item: Item$6,
});
const RadioGroupItem = Item$6;

const Switch = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixSwitch.Root, { ref: ref, className: cn('peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'disabled:cursor-not-allowed disabled:opacity-50', 'data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted', className), ...rest, children: jsx(RadixSwitch.Thumb, { className: "pointer-events-none block h-4 w-4 rounded-full bg-surface shadow ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0" }) })));
Switch.displayName = 'Switch';

const Slider = React.forwardRef(({ className, ...rest }, ref) => (jsxs(RadixSlider.Root, { ref: ref, className: cn('relative flex w-full touch-none select-none items-center', className), ...rest, children: [jsx(RadixSlider.Track, { className: "relative h-1.5 w-full grow overflow-hidden rounded-full bg-muted", children: jsx(RadixSlider.Range, { className: "absolute h-full bg-primary" }) }), jsx(RadixSlider.Thumb, { className: "block h-4 w-4 rounded-full border-2 border-primary bg-surface shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50" })] })));
Slider.displayName = 'Slider';

const FieldContext = React.createContext(null);
const useField = () => {
    const c = React.useContext(FieldContext);
    if (!c)
        throw new Error('Form.* must be used inside Form.Field');
    return c;
};
function Field({ name, children, className }) {
    const methods = useFormContext();
    const error = methods.formState.errors[name]?.message;
    const id = React.useId();
    return (jsx(FieldContext.Provider, { value: { name, error, describedById: `${id}-desc`, controlId: `${id}-control` }, children: jsx("div", { className: cn('space-y-1.5', className), children: children }) }));
}
function LegacyField({ name, control, render }) {
    const id = React.useId();
    return (jsx(Controller, { name: name, control: control, render: (rp) => (jsx(FieldContext.Provider, { value: {
                name: String(name),
                error: rp.fieldState.error?.message,
                describedById: `${id}-desc`,
                controlId: `${id}-control`,
                legacy: true,
            }, children: render(rp) })) }));
}
function SmartFormField(props) {
    if ('render' in props) {
        return jsx(LegacyField, { ...props });
    }
    return jsx(Field, { ...props });
}
function FieldLabel(props) {
    const { controlId } = useField();
    return jsx(Label$2, { htmlFor: controlId, ...props });
}
function FieldControl({ children, asChild, ...rest }) {
    const { name, error, controlId, describedById, legacy } = useField();
    const methods = useFormContext();
    if (legacy) {
        return jsx(Fragment, { children: children });
    }
    const Comp = asChild ? Slot : 'div';
    return (jsx(Controller, { name: name, control: methods.control, render: ({ field }) => (jsx(Comp, { id: controlId, "aria-invalid": !!error || undefined, "aria-describedby": describedById, ...field, ...rest, children: children })) }));
}
function FieldDescription({ className, children, ...rest }) {
    const { describedById } = useField();
    return (jsx("p", { id: describedById, className: cn('text-xs text-muted-foreground', className), ...rest, children: children }));
}
function FieldMessage({ className, ...rest }) {
    const { error } = useField();
    if (!error)
        return null;
    return (jsx("p", { className: cn('text-xs text-destructive', className), ...rest, children: error }));
}
function FormRoot({ children, ...methods }) {
    return jsx(FormProvider, { ...methods, children: children });
}
FormRoot.displayName = 'Form';
const FormItem = React.forwardRef(({ className, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('space-y-2', className), ...rest })));
FormItem.displayName = 'FormItem';
const Form = Object.assign(FormRoot, {
    Root: FormRoot,
    Field,
    Item: FormItem,
    Label: FieldLabel,
    Control: FieldControl,
    Description: FieldDescription,
    Message: FieldMessage,
});
const FormField = SmartFormField;
const FormLabel = FieldLabel;
const FormControl = FieldControl;
const FormDescription = FieldDescription;
const FormMessage = FieldMessage;

const Calendar = React.forwardRef(({ className, classNames, showOutsideDays = true, ...rest }, ref) => (jsx("div", { ref: ref, children: jsx(DayPicker, { showOutsideDays: showOutsideDays, className: cn('p-3', className), classNames: {
            months: 'flex flex-col sm:flex-row gap-4',
            month: 'space-y-3',
            caption: 'flex justify-between items-center px-1',
            caption_label: 'text-sm font-medium',
            nav: 'flex items-center gap-1',
            nav_button: 'inline-flex h-7 w-7 items-center justify-center rounded-md border border-border hover:bg-muted',
            nav_button_previous: '',
            nav_button_next: '',
            table: 'w-full border-collapse space-y-1',
            head_row: 'flex',
            head_cell: 'w-8 text-center text-xs font-medium text-muted-foreground',
            row: 'flex w-full mt-1',
            cell: 'h-8 w-8 text-center text-sm',
            day: 'inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted',
            day_selected: 'bg-primary text-primary-foreground hover:bg-primary',
            day_today: 'ring-1 ring-primary',
            day_outside: 'text-muted-foreground opacity-50',
            day_disabled: 'opacity-40 pointer-events-none',
            ...classNames,
        }, components: {
            IconLeft: () => jsx(ChevronLeft, { className: "h-4 w-4" }),
            IconRight: () => jsx(ChevronRight, { className: "h-4 w-4" }),
        }, ...rest }) })));
Calendar.displayName = 'Calendar';

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param argument - The value to convert
 *
 * @returns The parsed date in the local time zone
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */
function toDate(argument) {
  const argStr = Object.prototype.toString.call(argument);

  // Clone the date
  if (
    argument instanceof Date ||
    (typeof argument === "object" && argStr === "[object Date]")
  ) {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new argument.constructor(+argument);
  } else if (
    typeof argument === "number" ||
    argStr === "[object Number]" ||
    typeof argument === "string" ||
    argStr === "[object String]"
  ) {
    // TODO: Can we get rid of as?
    return new Date(argument);
  } else {
    // TODO: Can we get rid of as?
    return new Date(NaN);
  }
}

/**
 * @name constructFrom
 * @category Generic Helpers
 * @summary Constructs a date using the reference date and the value
 *
 * @description
 * The function constructs a new date using the constructor from the reference
 * date and the given value. It helps to build generic functions that accept
 * date extensions.
 *
 * It defaults to `Date` if the passed reference date is a number or a string.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The reference date to take constructor from
 * @param value - The value to create the date
 *
 * @returns Date initialized using the given date and value
 *
 * @example
 * import { constructFrom } from 'date-fns'
 *
 * // A function that clones a date preserving the original type
 * function cloneDate<DateType extends Date(date: DateType): DateType {
 *   return constructFrom(
 *     date, // Use contrustor from the given date
 *     date.getTime() // Use the date value to create a new date
 *   )
 * }
 */
function constructFrom(date, value) {
  if (date instanceof Date) {
    return new date.constructor(value);
  } else {
    return new Date(value);
  }
}

/**
 * @module constants
 * @summary Useful constants
 * @description
 * Collection of useful date constants.
 *
 * The constants could be imported from `date-fns/constants`:
 *
 * ```ts
 * import { maxTime, minTime } from "./constants/date-fns/constants";
 *
 * function isAllowedTime(time) {
 *   return time <= maxTime && time >= minTime;
 * }
 * ```
 */


/**
 * @constant
 * @name millisecondsInWeek
 * @summary Milliseconds in 1 week.
 */
const millisecondsInWeek = 604800000;

/**
 * @constant
 * @name millisecondsInDay
 * @summary Milliseconds in 1 day.
 */
const millisecondsInDay = 86400000;

let defaultOptions = {};

function getDefaultOptions() {
  return defaultOptions;
}

/**
 * The {@link startOfWeek} function options.
 */

/**
 * @name startOfWeek
 * @category Week Helpers
 * @summary Return the start of a week for the given date.
 *
 * @description
 * Return the start of a week for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 * @param options - An object with options
 *
 * @returns The start of a week
 *
 * @example
 * // The start of a week for 2 September 2014 11:55:00:
 * const result = startOfWeek(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Sun Aug 31 2014 00:00:00
 *
 * @example
 * // If the week starts on Monday, the start of the week for 2 September 2014 11:55:00:
 * const result = startOfWeek(new Date(2014, 8, 2, 11, 55, 0), { weekStartsOn: 1 })
 * //=> Mon Sep 01 2014 00:00:00
 */
function startOfWeek(date, options) {
  const defaultOptions = getDefaultOptions();
  const weekStartsOn =
    options?.weekStartsOn ??
    options?.locale?.options?.weekStartsOn ??
    defaultOptions.weekStartsOn ??
    defaultOptions.locale?.options?.weekStartsOn ??
    0;

  const _date = toDate(date);
  const day = _date.getDay();
  const diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;

  _date.setDate(_date.getDate() - diff);
  _date.setHours(0, 0, 0, 0);
  return _date;
}

/**
 * @name startOfISOWeek
 * @category ISO Week Helpers
 * @summary Return the start of an ISO week for the given date.
 *
 * @description
 * Return the start of an ISO week for the given date.
 * The result will be in the local timezone.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The start of an ISO week
 *
 * @example
 * // The start of an ISO week for 2 September 2014 11:55:00:
 * const result = startOfISOWeek(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Mon Sep 01 2014 00:00:00
 */
function startOfISOWeek(date) {
  return startOfWeek(date, { weekStartsOn: 1 });
}

/**
 * @name getISOWeekYear
 * @category ISO Week-Numbering Year Helpers
 * @summary Get the ISO week-numbering year of the given date.
 *
 * @description
 * Get the ISO week-numbering year of the given date,
 * which always starts 3 days before the year's first Thursday.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The ISO week-numbering year
 *
 * @example
 * // Which ISO-week numbering year is 2 January 2005?
 * const result = getISOWeekYear(new Date(2005, 0, 2))
 * //=> 2004
 */
function getISOWeekYear(date) {
  const _date = toDate(date);
  const year = _date.getFullYear();

  const fourthOfJanuaryOfNextYear = constructFrom(date, 0);
  fourthOfJanuaryOfNextYear.setFullYear(year + 1, 0, 4);
  fourthOfJanuaryOfNextYear.setHours(0, 0, 0, 0);
  const startOfNextYear = startOfISOWeek(fourthOfJanuaryOfNextYear);

  const fourthOfJanuaryOfThisYear = constructFrom(date, 0);
  fourthOfJanuaryOfThisYear.setFullYear(year, 0, 4);
  fourthOfJanuaryOfThisYear.setHours(0, 0, 0, 0);
  const startOfThisYear = startOfISOWeek(fourthOfJanuaryOfThisYear);

  if (_date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (_date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

/**
 * @name startOfDay
 * @category Day Helpers
 * @summary Return the start of a day for the given date.
 *
 * @description
 * Return the start of a day for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The start of a day
 *
 * @example
 * // The start of a day for 2 September 2014 11:55:00:
 * const result = startOfDay(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 02 2014 00:00:00
 */
function startOfDay(date) {
  const _date = toDate(date);
  _date.setHours(0, 0, 0, 0);
  return _date;
}

/**
 * Google Chrome as of 67.0.3396.87 introduced timezones with offset that includes seconds.
 * They usually appear for dates that denote time before the timezones were introduced
 * (e.g. for 'Europe/Prague' timezone the offset is GMT+00:57:44 before 1 October 1891
 * and GMT+01:00:00 after that date)
 *
 * Date#getTimezoneOffset returns the offset in minutes and would return 57 for the example above,
 * which would lead to incorrect calculations.
 *
 * This function returns the timezone offset in milliseconds that takes seconds in account.
 */
function getTimezoneOffsetInMilliseconds(date) {
  const _date = toDate(date);
  const utcDate = new Date(
    Date.UTC(
      _date.getFullYear(),
      _date.getMonth(),
      _date.getDate(),
      _date.getHours(),
      _date.getMinutes(),
      _date.getSeconds(),
      _date.getMilliseconds(),
    ),
  );
  utcDate.setUTCFullYear(_date.getFullYear());
  return +date - +utcDate;
}

/**
 * @name differenceInCalendarDays
 * @category Day Helpers
 * @summary Get the number of calendar days between the given dates.
 *
 * @description
 * Get the number of calendar days between the given dates. This means that the times are removed
 * from the dates and then the difference in days is calculated.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dateLeft - The later date
 * @param dateRight - The earlier date
 *
 * @returns The number of calendar days
 *
 * @example
 * // How many calendar days are between
 * // 2 July 2011 23:00:00 and 2 July 2012 00:00:00?
 * const result = differenceInCalendarDays(
 *   new Date(2012, 6, 2, 0, 0),
 *   new Date(2011, 6, 2, 23, 0)
 * )
 * //=> 366
 * // How many calendar days are between
 * // 2 July 2011 23:59:00 and 3 July 2011 00:01:00?
 * const result = differenceInCalendarDays(
 *   new Date(2011, 6, 3, 0, 1),
 *   new Date(2011, 6, 2, 23, 59)
 * )
 * //=> 1
 */
function differenceInCalendarDays(dateLeft, dateRight) {
  const startOfDayLeft = startOfDay(dateLeft);
  const startOfDayRight = startOfDay(dateRight);

  const timestampLeft =
    +startOfDayLeft - getTimezoneOffsetInMilliseconds(startOfDayLeft);
  const timestampRight =
    +startOfDayRight - getTimezoneOffsetInMilliseconds(startOfDayRight);

  // Round the number of days to the nearest integer because the number of
  // milliseconds in a day is not constant (e.g. it's different in the week of
  // the daylight saving time clock shift).
  return Math.round((timestampLeft - timestampRight) / millisecondsInDay);
}

/**
 * @name startOfISOWeekYear
 * @category ISO Week-Numbering Year Helpers
 * @summary Return the start of an ISO week-numbering year for the given date.
 *
 * @description
 * Return the start of an ISO week-numbering year,
 * which always starts 3 days before the year's first Thursday.
 * The result will be in the local timezone.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The start of an ISO week-numbering year
 *
 * @example
 * // The start of an ISO week-numbering year for 2 July 2005:
 * const result = startOfISOWeekYear(new Date(2005, 6, 2))
 * //=> Mon Jan 03 2005 00:00:00
 */
function startOfISOWeekYear(date) {
  const year = getISOWeekYear(date);
  const fourthOfJanuary = constructFrom(date, 0);
  fourthOfJanuary.setFullYear(year, 0, 4);
  fourthOfJanuary.setHours(0, 0, 0, 0);
  return startOfISOWeek(fourthOfJanuary);
}

/**
 * @name isDate
 * @category Common Helpers
 * @summary Is the given value a date?
 *
 * @description
 * Returns true if the given value is an instance of Date. The function works for dates transferred across iframes.
 *
 * @param value - The value to check
 *
 * @returns True if the given value is a date
 *
 * @example
 * // For a valid date:
 * const result = isDate(new Date())
 * //=> true
 *
 * @example
 * // For an invalid date:
 * const result = isDate(new Date(NaN))
 * //=> true
 *
 * @example
 * // For some value:
 * const result = isDate('2014-02-31')
 * //=> false
 *
 * @example
 * // For an object:
 * const result = isDate({})
 * //=> false
 */
function isDate(value) {
  return (
    value instanceof Date ||
    (typeof value === "object" &&
      Object.prototype.toString.call(value) === "[object Date]")
  );
}

/**
 * @name isValid
 * @category Common Helpers
 * @summary Is the given date valid?
 *
 * @description
 * Returns false if argument is Invalid Date and true otherwise.
 * Argument is converted to Date using `toDate`. See [toDate](https://date-fns.org/docs/toDate)
 * Invalid Date is a Date, whose time value is NaN.
 *
 * Time value of Date: http://es5.github.io/#x15.9.1.1
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to check
 *
 * @returns The date is valid
 *
 * @example
 * // For the valid date:
 * const result = isValid(new Date(2014, 1, 31))
 * //=> true
 *
 * @example
 * // For the value, convertable into a date:
 * const result = isValid(1393804800000)
 * //=> true
 *
 * @example
 * // For the invalid date:
 * const result = isValid(new Date(''))
 * //=> false
 */
function isValid(date) {
  if (!isDate(date) && typeof date !== "number") {
    return false;
  }
  const _date = toDate(date);
  return !isNaN(Number(_date));
}

/**
 * @name startOfYear
 * @category Year Helpers
 * @summary Return the start of a year for the given date.
 *
 * @description
 * Return the start of a year for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The start of a year
 *
 * @example
 * // The start of a year for 2 September 2014 11:55:00:
 * const result = startOfYear(new Date(2014, 8, 2, 11, 55, 00))
 * //=> Wed Jan 01 2014 00:00:00
 */
function startOfYear(date) {
  const cleanDate = toDate(date);
  const _date = constructFrom(date, 0);
  _date.setFullYear(cleanDate.getFullYear(), 0, 1);
  _date.setHours(0, 0, 0, 0);
  return _date;
}

const formatDistanceLocale = {
  lessThanXSeconds: {
    one: "less than a second",
    other: "less than {{count}} seconds",
  },

  xSeconds: {
    one: "1 second",
    other: "{{count}} seconds",
  },

  halfAMinute: "half a minute",

  lessThanXMinutes: {
    one: "less than a minute",
    other: "less than {{count}} minutes",
  },

  xMinutes: {
    one: "1 minute",
    other: "{{count}} minutes",
  },

  aboutXHours: {
    one: "about 1 hour",
    other: "about {{count}} hours",
  },

  xHours: {
    one: "1 hour",
    other: "{{count}} hours",
  },

  xDays: {
    one: "1 day",
    other: "{{count}} days",
  },

  aboutXWeeks: {
    one: "about 1 week",
    other: "about {{count}} weeks",
  },

  xWeeks: {
    one: "1 week",
    other: "{{count}} weeks",
  },

  aboutXMonths: {
    one: "about 1 month",
    other: "about {{count}} months",
  },

  xMonths: {
    one: "1 month",
    other: "{{count}} months",
  },

  aboutXYears: {
    one: "about 1 year",
    other: "about {{count}} years",
  },

  xYears: {
    one: "1 year",
    other: "{{count}} years",
  },

  overXYears: {
    one: "over 1 year",
    other: "over {{count}} years",
  },

  almostXYears: {
    one: "almost 1 year",
    other: "almost {{count}} years",
  },
};

const formatDistance = (token, count, options) => {
  let result;

  const tokenValue = formatDistanceLocale[token];
  if (typeof tokenValue === "string") {
    result = tokenValue;
  } else if (count === 1) {
    result = tokenValue.one;
  } else {
    result = tokenValue.other.replace("{{count}}", count.toString());
  }

  if (options?.addSuffix) {
    if (options.comparison && options.comparison > 0) {
      return "in " + result;
    } else {
      return result + " ago";
    }
  }

  return result;
};

function buildFormatLongFn(args) {
  return (options = {}) => {
    // TODO: Remove String()
    const width = options.width ? String(options.width) : args.defaultWidth;
    const format = args.formats[width] || args.formats[args.defaultWidth];
    return format;
  };
}

const dateFormats = {
  full: "EEEE, MMMM do, y",
  long: "MMMM do, y",
  medium: "MMM d, y",
  short: "MM/dd/yyyy",
};

const timeFormats = {
  full: "h:mm:ss a zzzz",
  long: "h:mm:ss a z",
  medium: "h:mm:ss a",
  short: "h:mm a",
};

const dateTimeFormats = {
  full: "{{date}} 'at' {{time}}",
  long: "{{date}} 'at' {{time}}",
  medium: "{{date}}, {{time}}",
  short: "{{date}}, {{time}}",
};

const formatLong = {
  date: buildFormatLongFn({
    formats: dateFormats,
    defaultWidth: "full",
  }),

  time: buildFormatLongFn({
    formats: timeFormats,
    defaultWidth: "full",
  }),

  dateTime: buildFormatLongFn({
    formats: dateTimeFormats,
    defaultWidth: "full",
  }),
};

const formatRelativeLocale = {
  lastWeek: "'last' eeee 'at' p",
  yesterday: "'yesterday at' p",
  today: "'today at' p",
  tomorrow: "'tomorrow at' p",
  nextWeek: "eeee 'at' p",
  other: "P",
};

const formatRelative = (token, _date, _baseDate, _options) =>
  formatRelativeLocale[token];

/* eslint-disable no-unused-vars */

/**
 * The localize function argument callback which allows to convert raw value to
 * the actual type.
 *
 * @param value - The value to convert
 *
 * @returns The converted value
 */

/**
 * The map of localized values for each width.
 */

/**
 * The index type of the locale unit value. It types conversion of units of
 * values that don't start at 0 (i.e. quarters).
 */

/**
 * Converts the unit value to the tuple of values.
 */

/**
 * The tuple of localized era values. The first element represents BC,
 * the second element represents AD.
 */

/**
 * The tuple of localized quarter values. The first element represents Q1.
 */

/**
 * The tuple of localized day values. The first element represents Sunday.
 */

/**
 * The tuple of localized month values. The first element represents January.
 */

function buildLocalizeFn(args) {
  return (value, options) => {
    const context = options?.context ? String(options.context) : "standalone";

    let valuesArray;
    if (context === "formatting" && args.formattingValues) {
      const defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      const width = options?.width ? String(options.width) : defaultWidth;

      valuesArray =
        args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      const defaultWidth = args.defaultWidth;
      const width = options?.width ? String(options.width) : args.defaultWidth;

      valuesArray = args.values[width] || args.values[defaultWidth];
    }
    const index = args.argumentCallback ? args.argumentCallback(value) : value;

    // @ts-expect-error - For some reason TypeScript just don't want to match it, no matter how hard we try. I challenge you to try to remove it!
    return valuesArray[index];
  };
}

const eraValues = {
  narrow: ["B", "A"],
  abbreviated: ["BC", "AD"],
  wide: ["Before Christ", "Anno Domini"],
};

const quarterValues = {
  narrow: ["1", "2", "3", "4"],
  abbreviated: ["Q1", "Q2", "Q3", "Q4"],
  wide: ["1st quarter", "2nd quarter", "3rd quarter", "4th quarter"],
};

// Note: in English, the names of days of the week and months are capitalized.
// If you are making a new locale based on this one, check if the same is true for the language you're working on.
// Generally, formatted dates should look like they are in the middle of a sentence,
// e.g. in Spanish language the weekdays and months should be in the lowercase.
const monthValues = {
  narrow: ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"],
  abbreviated: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],

  wide: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
};

const dayValues = {
  narrow: ["S", "M", "T", "W", "T", "F", "S"],
  short: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
  abbreviated: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  wide: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
};

const dayPeriodValues = {
  narrow: {
    am: "a",
    pm: "p",
    midnight: "mi",
    noon: "n",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night",
  },
  abbreviated: {
    am: "AM",
    pm: "PM",
    midnight: "midnight",
    noon: "noon",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night",
  },
  wide: {
    am: "a.m.",
    pm: "p.m.",
    midnight: "midnight",
    noon: "noon",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night",
  },
};

const formattingDayPeriodValues = {
  narrow: {
    am: "a",
    pm: "p",
    midnight: "mi",
    noon: "n",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night",
  },
  abbreviated: {
    am: "AM",
    pm: "PM",
    midnight: "midnight",
    noon: "noon",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night",
  },
  wide: {
    am: "a.m.",
    pm: "p.m.",
    midnight: "midnight",
    noon: "noon",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night",
  },
};

const ordinalNumber = (dirtyNumber, _options) => {
  const number = Number(dirtyNumber);

  // If ordinal numbers depend on context, for example,
  // if they are different for different grammatical genders,
  // use `options.unit`.
  //
  // `unit` can be 'year', 'quarter', 'month', 'week', 'date', 'dayOfYear',
  // 'day', 'hour', 'minute', 'second'.

  const rem100 = number % 100;
  if (rem100 > 20 || rem100 < 10) {
    switch (rem100 % 10) {
      case 1:
        return number + "st";
      case 2:
        return number + "nd";
      case 3:
        return number + "rd";
    }
  }
  return number + "th";
};

const localize = {
  ordinalNumber,

  era: buildLocalizeFn({
    values: eraValues,
    defaultWidth: "wide",
  }),

  quarter: buildLocalizeFn({
    values: quarterValues,
    defaultWidth: "wide",
    argumentCallback: (quarter) => quarter - 1,
  }),

  month: buildLocalizeFn({
    values: monthValues,
    defaultWidth: "wide",
  }),

  day: buildLocalizeFn({
    values: dayValues,
    defaultWidth: "wide",
  }),

  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: "wide",
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: "wide",
  }),
};

function buildMatchFn(args) {
  return (string, options = {}) => {
    const width = options.width;

    const matchPattern =
      (width && args.matchPatterns[width]) ||
      args.matchPatterns[args.defaultMatchWidth];
    const matchResult = string.match(matchPattern);

    if (!matchResult) {
      return null;
    }
    const matchedString = matchResult[0];

    const parsePatterns =
      (width && args.parsePatterns[width]) ||
      args.parsePatterns[args.defaultParseWidth];

    const key = Array.isArray(parsePatterns)
      ? findIndex(parsePatterns, (pattern) => pattern.test(matchedString))
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any -- I challange you to fix the type
        findKey(parsePatterns, (pattern) => pattern.test(matchedString));

    let value;

    value = args.valueCallback ? args.valueCallback(key) : key;
    value = options.valueCallback
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any -- I challange you to fix the type
        options.valueCallback(value)
      : value;

    const rest = string.slice(matchedString.length);

    return { value, rest };
  };
}

function findKey(object, predicate) {
  for (const key in object) {
    if (
      Object.prototype.hasOwnProperty.call(object, key) &&
      predicate(object[key])
    ) {
      return key;
    }
  }
  return undefined;
}

function findIndex(array, predicate) {
  for (let key = 0; key < array.length; key++) {
    if (predicate(array[key])) {
      return key;
    }
  }
  return undefined;
}

function buildMatchPatternFn(args) {
  return (string, options = {}) => {
    const matchResult = string.match(args.matchPattern);
    if (!matchResult) return null;
    const matchedString = matchResult[0];

    const parseResult = string.match(args.parsePattern);
    if (!parseResult) return null;
    let value = args.valueCallback
      ? args.valueCallback(parseResult[0])
      : parseResult[0];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- I challange you to fix the type
    value = options.valueCallback ? options.valueCallback(value) : value;

    const rest = string.slice(matchedString.length);

    return { value, rest };
  };
}

const matchOrdinalNumberPattern = /^(\d+)(th|st|nd|rd)?/i;
const parseOrdinalNumberPattern = /\d+/i;

const matchEraPatterns = {
  narrow: /^(b|a)/i,
  abbreviated: /^(b\.?\s?c\.?|b\.?\s?c\.?\s?e\.?|a\.?\s?d\.?|c\.?\s?e\.?)/i,
  wide: /^(before christ|before common era|anno domini|common era)/i,
};
const parseEraPatterns = {
  any: [/^b/i, /^(a|c)/i],
};

const matchQuarterPatterns = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](th|st|nd|rd)? quarter/i,
};
const parseQuarterPatterns = {
  any: [/1/i, /2/i, /3/i, /4/i],
};

const matchMonthPatterns = {
  narrow: /^[jfmasond]/i,
  abbreviated: /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
  wide: /^(january|february|march|april|may|june|july|august|september|october|november|december)/i,
};
const parseMonthPatterns = {
  narrow: [
    /^j/i,
    /^f/i,
    /^m/i,
    /^a/i,
    /^m/i,
    /^j/i,
    /^j/i,
    /^a/i,
    /^s/i,
    /^o/i,
    /^n/i,
    /^d/i,
  ],

  any: [
    /^ja/i,
    /^f/i,
    /^mar/i,
    /^ap/i,
    /^may/i,
    /^jun/i,
    /^jul/i,
    /^au/i,
    /^s/i,
    /^o/i,
    /^n/i,
    /^d/i,
  ],
};

const matchDayPatterns = {
  narrow: /^[smtwf]/i,
  short: /^(su|mo|tu|we|th|fr|sa)/i,
  abbreviated: /^(sun|mon|tue|wed|thu|fri|sat)/i,
  wide: /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i,
};
const parseDayPatterns = {
  narrow: [/^s/i, /^m/i, /^t/i, /^w/i, /^t/i, /^f/i, /^s/i],
  any: [/^su/i, /^m/i, /^tu/i, /^w/i, /^th/i, /^f/i, /^sa/i],
};

const matchDayPeriodPatterns = {
  narrow: /^(a|p|mi|n|(in the|at) (morning|afternoon|evening|night))/i,
  any: /^([ap]\.?\s?m\.?|midnight|noon|(in the|at) (morning|afternoon|evening|night))/i,
};
const parseDayPeriodPatterns = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mi/i,
    noon: /^no/i,
    morning: /morning/i,
    afternoon: /afternoon/i,
    evening: /evening/i,
    night: /night/i,
  },
};

const match = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern,
    parsePattern: parseOrdinalNumberPattern,
    valueCallback: (value) => parseInt(value, 10),
  }),

  era: buildMatchFn({
    matchPatterns: matchEraPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseEraPatterns,
    defaultParseWidth: "any",
  }),

  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseQuarterPatterns,
    defaultParseWidth: "any",
    valueCallback: (index) => index + 1,
  }),

  month: buildMatchFn({
    matchPatterns: matchMonthPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseMonthPatterns,
    defaultParseWidth: "any",
  }),

  day: buildMatchFn({
    matchPatterns: matchDayPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseDayPatterns,
    defaultParseWidth: "any",
  }),

  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns,
    defaultMatchWidth: "any",
    parsePatterns: parseDayPeriodPatterns,
    defaultParseWidth: "any",
  }),
};

/**
 * @category Locales
 * @summary English locale (United States).
 * @language English
 * @iso-639-2 eng
 * @author Sasha Koss [@kossnocorp](https://github.com/kossnocorp)
 * @author Lesha Koss [@leshakoss](https://github.com/leshakoss)
 */
const enUS = {
  code: "en-US",
  formatDistance: formatDistance,
  formatLong: formatLong,
  formatRelative: formatRelative,
  localize: localize,
  match: match,
  options: {
    weekStartsOn: 0 /* Sunday */,
    firstWeekContainsDate: 1,
  },
};

/**
 * @name getDayOfYear
 * @category Day Helpers
 * @summary Get the day of the year of the given date.
 *
 * @description
 * Get the day of the year of the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The day of year
 *
 * @example
 * // Which day of the year is 2 July 2014?
 * const result = getDayOfYear(new Date(2014, 6, 2))
 * //=> 183
 */
function getDayOfYear(date) {
  const _date = toDate(date);
  const diff = differenceInCalendarDays(_date, startOfYear(_date));
  const dayOfYear = diff + 1;
  return dayOfYear;
}

/**
 * @name getISOWeek
 * @category ISO Week Helpers
 * @summary Get the ISO week of the given date.
 *
 * @description
 * Get the ISO week of the given date.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The ISO week
 *
 * @example
 * // Which week of the ISO-week numbering year is 2 January 2005?
 * const result = getISOWeek(new Date(2005, 0, 2))
 * //=> 53
 */
function getISOWeek(date) {
  const _date = toDate(date);
  const diff = +startOfISOWeek(_date) - +startOfISOWeekYear(_date);

  // Round the number of weeks to the nearest integer because the number of
  // milliseconds in a week is not constant (e.g. it's different in the week of
  // the daylight saving time clock shift).
  return Math.round(diff / millisecondsInWeek) + 1;
}

/**
 * The {@link getWeekYear} function options.
 */

/**
 * @name getWeekYear
 * @category Week-Numbering Year Helpers
 * @summary Get the local week-numbering year of the given date.
 *
 * @description
 * Get the local week-numbering year of the given date.
 * The exact calculation depends on the values of
 * `options.weekStartsOn` (which is the index of the first day of the week)
 * and `options.firstWeekContainsDate` (which is the day of January, which is always in
 * the first week of the week-numbering year)
 *
 * Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 * @param options - An object with options.
 *
 * @returns The local week-numbering year
 *
 * @example
 * // Which week numbering year is 26 December 2004 with the default settings?
 * const result = getWeekYear(new Date(2004, 11, 26))
 * //=> 2005
 *
 * @example
 * // Which week numbering year is 26 December 2004 if week starts on Saturday?
 * const result = getWeekYear(new Date(2004, 11, 26), { weekStartsOn: 6 })
 * //=> 2004
 *
 * @example
 * // Which week numbering year is 26 December 2004 if the first week contains 4 January?
 * const result = getWeekYear(new Date(2004, 11, 26), { firstWeekContainsDate: 4 })
 * //=> 2004
 */
function getWeekYear(date, options) {
  const _date = toDate(date);
  const year = _date.getFullYear();

  const defaultOptions = getDefaultOptions();
  const firstWeekContainsDate =
    options?.firstWeekContainsDate ??
    options?.locale?.options?.firstWeekContainsDate ??
    defaultOptions.firstWeekContainsDate ??
    defaultOptions.locale?.options?.firstWeekContainsDate ??
    1;

  const firstWeekOfNextYear = constructFrom(date, 0);
  firstWeekOfNextYear.setFullYear(year + 1, 0, firstWeekContainsDate);
  firstWeekOfNextYear.setHours(0, 0, 0, 0);
  const startOfNextYear = startOfWeek(firstWeekOfNextYear, options);

  const firstWeekOfThisYear = constructFrom(date, 0);
  firstWeekOfThisYear.setFullYear(year, 0, firstWeekContainsDate);
  firstWeekOfThisYear.setHours(0, 0, 0, 0);
  const startOfThisYear = startOfWeek(firstWeekOfThisYear, options);

  if (_date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (_date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

/**
 * The {@link startOfWeekYear} function options.
 */

/**
 * @name startOfWeekYear
 * @category Week-Numbering Year Helpers
 * @summary Return the start of a local week-numbering year for the given date.
 *
 * @description
 * Return the start of a local week-numbering year.
 * The exact calculation depends on the values of
 * `options.weekStartsOn` (which is the index of the first day of the week)
 * and `options.firstWeekContainsDate` (which is the day of January, which is always in
 * the first week of the week-numbering year)
 *
 * Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 * @param options - An object with options
 *
 * @returns The start of a week-numbering year
 *
 * @example
 * // The start of an a week-numbering year for 2 July 2005 with default settings:
 * const result = startOfWeekYear(new Date(2005, 6, 2))
 * //=> Sun Dec 26 2004 00:00:00
 *
 * @example
 * // The start of a week-numbering year for 2 July 2005
 * // if Monday is the first day of week
 * // and 4 January is always in the first week of the year:
 * const result = startOfWeekYear(new Date(2005, 6, 2), {
 *   weekStartsOn: 1,
 *   firstWeekContainsDate: 4
 * })
 * //=> Mon Jan 03 2005 00:00:00
 */
function startOfWeekYear(date, options) {
  const defaultOptions = getDefaultOptions();
  const firstWeekContainsDate =
    options?.firstWeekContainsDate ??
    options?.locale?.options?.firstWeekContainsDate ??
    defaultOptions.firstWeekContainsDate ??
    defaultOptions.locale?.options?.firstWeekContainsDate ??
    1;

  const year = getWeekYear(date, options);
  const firstWeek = constructFrom(date, 0);
  firstWeek.setFullYear(year, 0, firstWeekContainsDate);
  firstWeek.setHours(0, 0, 0, 0);
  const _date = startOfWeek(firstWeek, options);
  return _date;
}

/**
 * The {@link getWeek} function options.
 */

/**
 * @name getWeek
 * @category Week Helpers
 * @summary Get the local week index of the given date.
 *
 * @description
 * Get the local week index of the given date.
 * The exact calculation depends on the values of
 * `options.weekStartsOn` (which is the index of the first day of the week)
 * and `options.firstWeekContainsDate` (which is the day of January, which is always in
 * the first week of the week-numbering year)
 *
 * Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 * @param options - An object with options
 *
 * @returns The week
 *
 * @example
 * // Which week of the local week numbering year is 2 January 2005 with default options?
 * const result = getWeek(new Date(2005, 0, 2))
 * //=> 2
 *
 * @example
 * // Which week of the local week numbering year is 2 January 2005,
 * // if Monday is the first day of the week,
 * // and the first week of the year always contains 4 January?
 * const result = getWeek(new Date(2005, 0, 2), {
 *   weekStartsOn: 1,
 *   firstWeekContainsDate: 4
 * })
 * //=> 53
 */

function getWeek(date, options) {
  const _date = toDate(date);
  const diff = +startOfWeek(_date, options) - +startOfWeekYear(_date, options);

  // Round the number of weeks to the nearest integer because the number of
  // milliseconds in a week is not constant (e.g. it's different in the week of
  // the daylight saving time clock shift).
  return Math.round(diff / millisecondsInWeek) + 1;
}

function addLeadingZeros(number, targetLength) {
  const sign = number < 0 ? "-" : "";
  const output = Math.abs(number).toString().padStart(targetLength, "0");
  return sign + output;
}

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* |                                |
 * |  d  | Day of month                   |  D  |                                |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  m  | Minute                         |  M  | Month                          |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  y  | Year (abs)                     |  Y  |                                |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 */

const lightFormatters = {
  // Year
  y(date, token) {
    // From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_tokens
    // | Year     |     y | yy |   yyy |  yyyy | yyyyy |
    // |----------|-------|----|-------|-------|-------|
    // | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
    // | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
    // | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
    // | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
    // | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |

    const signedYear = date.getFullYear();
    // Returns 1 for 1 BC (which is year 0 in JavaScript)
    const year = signedYear > 0 ? signedYear : 1 - signedYear;
    return addLeadingZeros(token === "yy" ? year % 100 : year, token.length);
  },

  // Month
  M(date, token) {
    const month = date.getMonth();
    return token === "M" ? String(month + 1) : addLeadingZeros(month + 1, 2);
  },

  // Day of the month
  d(date, token) {
    return addLeadingZeros(date.getDate(), token.length);
  },

  // AM or PM
  a(date, token) {
    const dayPeriodEnumValue = date.getHours() / 12 >= 1 ? "pm" : "am";

    switch (token) {
      case "a":
      case "aa":
        return dayPeriodEnumValue.toUpperCase();
      case "aaa":
        return dayPeriodEnumValue;
      case "aaaaa":
        return dayPeriodEnumValue[0];
      case "aaaa":
      default:
        return dayPeriodEnumValue === "am" ? "a.m." : "p.m.";
    }
  },

  // Hour [1-12]
  h(date, token) {
    return addLeadingZeros(date.getHours() % 12 || 12, token.length);
  },

  // Hour [0-23]
  H(date, token) {
    return addLeadingZeros(date.getHours(), token.length);
  },

  // Minute
  m(date, token) {
    return addLeadingZeros(date.getMinutes(), token.length);
  },

  // Second
  s(date, token) {
    return addLeadingZeros(date.getSeconds(), token.length);
  },

  // Fraction of second
  S(date, token) {
    const numberOfDigits = token.length;
    const milliseconds = date.getMilliseconds();
    const fractionalSeconds = Math.trunc(
      milliseconds * Math.pow(10, numberOfDigits - 3),
    );
    return addLeadingZeros(fractionalSeconds, token.length);
  },
};

const dayPeriodEnum = {
  midnight: "midnight",
  noon: "noon",
  morning: "morning",
  afternoon: "afternoon",
  evening: "evening",
  night: "night",
};

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* | Milliseconds in day            |
 * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
 * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
 * |  d  | Day of month                   |  D  | Day of year                    |
 * |  e  | Local day of week              |  E  | Day of week                    |
 * |  f  |                                |  F* | Day of week in month           |
 * |  g* | Modified Julian day            |  G  | Era                            |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  i! | ISO day of week                |  I! | ISO week of year               |
 * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
 * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
 * |  l* | (deprecated)                   |  L  | Stand-alone month              |
 * |  m  | Minute                         |  M  | Month                          |
 * |  n  |                                |  N  |                                |
 * |  o! | Ordinal number modifier        |  O  | Timezone (GMT)                 |
 * |  p! | Long localized time            |  P! | Long localized date            |
 * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
 * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
 * |  u  | Extended year                  |  U* | Cyclic year                    |
 * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
 * |  w  | Local week of year             |  W* | Week of month                  |
 * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
 * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
 * |  z  | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 *
 * Letters marked by ! are non-standard, but implemented by date-fns:
 * - `o` modifies the previous token to turn it into an ordinal (see `format` docs)
 * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
 *   i.e. 7 for Sunday, 1 for Monday, etc.
 * - `I` is ISO week of year, as opposed to `w` which is local week of year.
 * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
 *   `R` is supposed to be used in conjunction with `I` and `i`
 *   for universal ISO week-numbering date, whereas
 *   `Y` is supposed to be used in conjunction with `w` and `e`
 *   for week-numbering date specific to the locale.
 * - `P` is long localized date format
 * - `p` is long localized time format
 */

const formatters = {
  // Era
  G: function (date, token, localize) {
    const era = date.getFullYear() > 0 ? 1 : 0;
    switch (token) {
      // AD, BC
      case "G":
      case "GG":
      case "GGG":
        return localize.era(era, { width: "abbreviated" });
      // A, B
      case "GGGGG":
        return localize.era(era, { width: "narrow" });
      // Anno Domini, Before Christ
      case "GGGG":
      default:
        return localize.era(era, { width: "wide" });
    }
  },

  // Year
  y: function (date, token, localize) {
    // Ordinal number
    if (token === "yo") {
      const signedYear = date.getFullYear();
      // Returns 1 for 1 BC (which is year 0 in JavaScript)
      const year = signedYear > 0 ? signedYear : 1 - signedYear;
      return localize.ordinalNumber(year, { unit: "year" });
    }

    return lightFormatters.y(date, token);
  },

  // Local week-numbering year
  Y: function (date, token, localize, options) {
    const signedWeekYear = getWeekYear(date, options);
    // Returns 1 for 1 BC (which is year 0 in JavaScript)
    const weekYear = signedWeekYear > 0 ? signedWeekYear : 1 - signedWeekYear;

    // Two digit year
    if (token === "YY") {
      const twoDigitYear = weekYear % 100;
      return addLeadingZeros(twoDigitYear, 2);
    }

    // Ordinal number
    if (token === "Yo") {
      return localize.ordinalNumber(weekYear, { unit: "year" });
    }

    // Padding
    return addLeadingZeros(weekYear, token.length);
  },

  // ISO week-numbering year
  R: function (date, token) {
    const isoWeekYear = getISOWeekYear(date);

    // Padding
    return addLeadingZeros(isoWeekYear, token.length);
  },

  // Extended year. This is a single number designating the year of this calendar system.
  // The main difference between `y` and `u` localizers are B.C. years:
  // | Year | `y` | `u` |
  // |------|-----|-----|
  // | AC 1 |   1 |   1 |
  // | BC 1 |   1 |   0 |
  // | BC 2 |   2 |  -1 |
  // Also `yy` always returns the last two digits of a year,
  // while `uu` pads single digit years to 2 characters and returns other years unchanged.
  u: function (date, token) {
    const year = date.getFullYear();
    return addLeadingZeros(year, token.length);
  },

  // Quarter
  Q: function (date, token, localize) {
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    switch (token) {
      // 1, 2, 3, 4
      case "Q":
        return String(quarter);
      // 01, 02, 03, 04
      case "QQ":
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th
      case "Qo":
        return localize.ordinalNumber(quarter, { unit: "quarter" });
      // Q1, Q2, Q3, Q4
      case "QQQ":
        return localize.quarter(quarter, {
          width: "abbreviated",
          context: "formatting",
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case "QQQQQ":
        return localize.quarter(quarter, {
          width: "narrow",
          context: "formatting",
        });
      // 1st quarter, 2nd quarter, ...
      case "QQQQ":
      default:
        return localize.quarter(quarter, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Stand-alone quarter
  q: function (date, token, localize) {
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    switch (token) {
      // 1, 2, 3, 4
      case "q":
        return String(quarter);
      // 01, 02, 03, 04
      case "qq":
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th
      case "qo":
        return localize.ordinalNumber(quarter, { unit: "quarter" });
      // Q1, Q2, Q3, Q4
      case "qqq":
        return localize.quarter(quarter, {
          width: "abbreviated",
          context: "standalone",
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case "qqqqq":
        return localize.quarter(quarter, {
          width: "narrow",
          context: "standalone",
        });
      // 1st quarter, 2nd quarter, ...
      case "qqqq":
      default:
        return localize.quarter(quarter, {
          width: "wide",
          context: "standalone",
        });
    }
  },

  // Month
  M: function (date, token, localize) {
    const month = date.getMonth();
    switch (token) {
      case "M":
      case "MM":
        return lightFormatters.M(date, token);
      // 1st, 2nd, ..., 12th
      case "Mo":
        return localize.ordinalNumber(month + 1, { unit: "month" });
      // Jan, Feb, ..., Dec
      case "MMM":
        return localize.month(month, {
          width: "abbreviated",
          context: "formatting",
        });
      // J, F, ..., D
      case "MMMMM":
        return localize.month(month, {
          width: "narrow",
          context: "formatting",
        });
      // January, February, ..., December
      case "MMMM":
      default:
        return localize.month(month, { width: "wide", context: "formatting" });
    }
  },

  // Stand-alone month
  L: function (date, token, localize) {
    const month = date.getMonth();
    switch (token) {
      // 1, 2, ..., 12
      case "L":
        return String(month + 1);
      // 01, 02, ..., 12
      case "LL":
        return addLeadingZeros(month + 1, 2);
      // 1st, 2nd, ..., 12th
      case "Lo":
        return localize.ordinalNumber(month + 1, { unit: "month" });
      // Jan, Feb, ..., Dec
      case "LLL":
        return localize.month(month, {
          width: "abbreviated",
          context: "standalone",
        });
      // J, F, ..., D
      case "LLLLL":
        return localize.month(month, {
          width: "narrow",
          context: "standalone",
        });
      // January, February, ..., December
      case "LLLL":
      default:
        return localize.month(month, { width: "wide", context: "standalone" });
    }
  },

  // Local week of year
  w: function (date, token, localize, options) {
    const week = getWeek(date, options);

    if (token === "wo") {
      return localize.ordinalNumber(week, { unit: "week" });
    }

    return addLeadingZeros(week, token.length);
  },

  // ISO week of year
  I: function (date, token, localize) {
    const isoWeek = getISOWeek(date);

    if (token === "Io") {
      return localize.ordinalNumber(isoWeek, { unit: "week" });
    }

    return addLeadingZeros(isoWeek, token.length);
  },

  // Day of the month
  d: function (date, token, localize) {
    if (token === "do") {
      return localize.ordinalNumber(date.getDate(), { unit: "date" });
    }

    return lightFormatters.d(date, token);
  },

  // Day of year
  D: function (date, token, localize) {
    const dayOfYear = getDayOfYear(date);

    if (token === "Do") {
      return localize.ordinalNumber(dayOfYear, { unit: "dayOfYear" });
    }

    return addLeadingZeros(dayOfYear, token.length);
  },

  // Day of week
  E: function (date, token, localize) {
    const dayOfWeek = date.getDay();
    switch (token) {
      // Tue
      case "E":
      case "EE":
      case "EEE":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting",
        });
      // T
      case "EEEEE":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "formatting",
        });
      // Tu
      case "EEEEEE":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "formatting",
        });
      // Tuesday
      case "EEEE":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Local day of week
  e: function (date, token, localize, options) {
    const dayOfWeek = date.getDay();
    const localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
    switch (token) {
      // Numerical value (Nth day of week with current locale or weekStartsOn)
      case "e":
        return String(localDayOfWeek);
      // Padded numerical value
      case "ee":
        return addLeadingZeros(localDayOfWeek, 2);
      // 1st, 2nd, ..., 7th
      case "eo":
        return localize.ordinalNumber(localDayOfWeek, { unit: "day" });
      case "eee":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting",
        });
      // T
      case "eeeee":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "formatting",
        });
      // Tu
      case "eeeeee":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "formatting",
        });
      // Tuesday
      case "eeee":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Stand-alone local day of week
  c: function (date, token, localize, options) {
    const dayOfWeek = date.getDay();
    const localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
    switch (token) {
      // Numerical value (same as in `e`)
      case "c":
        return String(localDayOfWeek);
      // Padded numerical value
      case "cc":
        return addLeadingZeros(localDayOfWeek, token.length);
      // 1st, 2nd, ..., 7th
      case "co":
        return localize.ordinalNumber(localDayOfWeek, { unit: "day" });
      case "ccc":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "standalone",
        });
      // T
      case "ccccc":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "standalone",
        });
      // Tu
      case "cccccc":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "standalone",
        });
      // Tuesday
      case "cccc":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "standalone",
        });
    }
  },

  // ISO day of week
  i: function (date, token, localize) {
    const dayOfWeek = date.getDay();
    const isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
    switch (token) {
      // 2
      case "i":
        return String(isoDayOfWeek);
      // 02
      case "ii":
        return addLeadingZeros(isoDayOfWeek, token.length);
      // 2nd
      case "io":
        return localize.ordinalNumber(isoDayOfWeek, { unit: "day" });
      // Tue
      case "iii":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting",
        });
      // T
      case "iiiii":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "formatting",
        });
      // Tu
      case "iiiiii":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "formatting",
        });
      // Tuesday
      case "iiii":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // AM or PM
  a: function (date, token, localize) {
    const hours = date.getHours();
    const dayPeriodEnumValue = hours / 12 >= 1 ? "pm" : "am";

    switch (token) {
      case "a":
      case "aa":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting",
        });
      case "aaa":
        return localize
          .dayPeriod(dayPeriodEnumValue, {
            width: "abbreviated",
            context: "formatting",
          })
          .toLowerCase();
      case "aaaaa":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting",
        });
      case "aaaa":
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // AM, PM, midnight, noon
  b: function (date, token, localize) {
    const hours = date.getHours();
    let dayPeriodEnumValue;
    if (hours === 12) {
      dayPeriodEnumValue = dayPeriodEnum.noon;
    } else if (hours === 0) {
      dayPeriodEnumValue = dayPeriodEnum.midnight;
    } else {
      dayPeriodEnumValue = hours / 12 >= 1 ? "pm" : "am";
    }

    switch (token) {
      case "b":
      case "bb":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting",
        });
      case "bbb":
        return localize
          .dayPeriod(dayPeriodEnumValue, {
            width: "abbreviated",
            context: "formatting",
          })
          .toLowerCase();
      case "bbbbb":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting",
        });
      case "bbbb":
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // in the morning, in the afternoon, in the evening, at night
  B: function (date, token, localize) {
    const hours = date.getHours();
    let dayPeriodEnumValue;
    if (hours >= 17) {
      dayPeriodEnumValue = dayPeriodEnum.evening;
    } else if (hours >= 12) {
      dayPeriodEnumValue = dayPeriodEnum.afternoon;
    } else if (hours >= 4) {
      dayPeriodEnumValue = dayPeriodEnum.morning;
    } else {
      dayPeriodEnumValue = dayPeriodEnum.night;
    }

    switch (token) {
      case "B":
      case "BB":
      case "BBB":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting",
        });
      case "BBBBB":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting",
        });
      case "BBBB":
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Hour [1-12]
  h: function (date, token, localize) {
    if (token === "ho") {
      let hours = date.getHours() % 12;
      if (hours === 0) hours = 12;
      return localize.ordinalNumber(hours, { unit: "hour" });
    }

    return lightFormatters.h(date, token);
  },

  // Hour [0-23]
  H: function (date, token, localize) {
    if (token === "Ho") {
      return localize.ordinalNumber(date.getHours(), { unit: "hour" });
    }

    return lightFormatters.H(date, token);
  },

  // Hour [0-11]
  K: function (date, token, localize) {
    const hours = date.getHours() % 12;

    if (token === "Ko") {
      return localize.ordinalNumber(hours, { unit: "hour" });
    }

    return addLeadingZeros(hours, token.length);
  },

  // Hour [1-24]
  k: function (date, token, localize) {
    let hours = date.getHours();
    if (hours === 0) hours = 24;

    if (token === "ko") {
      return localize.ordinalNumber(hours, { unit: "hour" });
    }

    return addLeadingZeros(hours, token.length);
  },

  // Minute
  m: function (date, token, localize) {
    if (token === "mo") {
      return localize.ordinalNumber(date.getMinutes(), { unit: "minute" });
    }

    return lightFormatters.m(date, token);
  },

  // Second
  s: function (date, token, localize) {
    if (token === "so") {
      return localize.ordinalNumber(date.getSeconds(), { unit: "second" });
    }

    return lightFormatters.s(date, token);
  },

  // Fraction of second
  S: function (date, token) {
    return lightFormatters.S(date, token);
  },

  // Timezone (ISO-8601. If offset is 0, output is always `'Z'`)
  X: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    if (timezoneOffset === 0) {
      return "Z";
    }

    switch (token) {
      // Hours and optional minutes
      case "X":
        return formatTimezoneWithOptionalMinutes(timezoneOffset);

      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XX`
      case "XXXX":
      case "XX": // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);

      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XXX`
      case "XXXXX":
      case "XXX": // Hours and minutes with `:` delimiter
      default:
        return formatTimezone(timezoneOffset, ":");
    }
  },

  // Timezone (ISO-8601. If offset is 0, output is `'+00:00'` or equivalent)
  x: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    switch (token) {
      // Hours and optional minutes
      case "x":
        return formatTimezoneWithOptionalMinutes(timezoneOffset);

      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xx`
      case "xxxx":
      case "xx": // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);

      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xxx`
      case "xxxxx":
      case "xxx": // Hours and minutes with `:` delimiter
      default:
        return formatTimezone(timezoneOffset, ":");
    }
  },

  // Timezone (GMT)
  O: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    switch (token) {
      // Short
      case "O":
      case "OO":
      case "OOO":
        return "GMT" + formatTimezoneShort(timezoneOffset, ":");
      // Long
      case "OOOO":
      default:
        return "GMT" + formatTimezone(timezoneOffset, ":");
    }
  },

  // Timezone (specific non-location)
  z: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    switch (token) {
      // Short
      case "z":
      case "zz":
      case "zzz":
        return "GMT" + formatTimezoneShort(timezoneOffset, ":");
      // Long
      case "zzzz":
      default:
        return "GMT" + formatTimezone(timezoneOffset, ":");
    }
  },

  // Seconds timestamp
  t: function (date, token, _localize) {
    const timestamp = Math.trunc(date.getTime() / 1000);
    return addLeadingZeros(timestamp, token.length);
  },

  // Milliseconds timestamp
  T: function (date, token, _localize) {
    const timestamp = date.getTime();
    return addLeadingZeros(timestamp, token.length);
  },
};

function formatTimezoneShort(offset, delimiter = "") {
  const sign = offset > 0 ? "-" : "+";
  const absOffset = Math.abs(offset);
  const hours = Math.trunc(absOffset / 60);
  const minutes = absOffset % 60;
  if (minutes === 0) {
    return sign + String(hours);
  }
  return sign + String(hours) + delimiter + addLeadingZeros(minutes, 2);
}

function formatTimezoneWithOptionalMinutes(offset, delimiter) {
  if (offset % 60 === 0) {
    const sign = offset > 0 ? "-" : "+";
    return sign + addLeadingZeros(Math.abs(offset) / 60, 2);
  }
  return formatTimezone(offset, delimiter);
}

function formatTimezone(offset, delimiter = "") {
  const sign = offset > 0 ? "-" : "+";
  const absOffset = Math.abs(offset);
  const hours = addLeadingZeros(Math.trunc(absOffset / 60), 2);
  const minutes = addLeadingZeros(absOffset % 60, 2);
  return sign + hours + delimiter + minutes;
}

const dateLongFormatter = (pattern, formatLong) => {
  switch (pattern) {
    case "P":
      return formatLong.date({ width: "short" });
    case "PP":
      return formatLong.date({ width: "medium" });
    case "PPP":
      return formatLong.date({ width: "long" });
    case "PPPP":
    default:
      return formatLong.date({ width: "full" });
  }
};

const timeLongFormatter = (pattern, formatLong) => {
  switch (pattern) {
    case "p":
      return formatLong.time({ width: "short" });
    case "pp":
      return formatLong.time({ width: "medium" });
    case "ppp":
      return formatLong.time({ width: "long" });
    case "pppp":
    default:
      return formatLong.time({ width: "full" });
  }
};

const dateTimeLongFormatter = (pattern, formatLong) => {
  const matchResult = pattern.match(/(P+)(p+)?/) || [];
  const datePattern = matchResult[1];
  const timePattern = matchResult[2];

  if (!timePattern) {
    return dateLongFormatter(pattern, formatLong);
  }

  let dateTimeFormat;

  switch (datePattern) {
    case "P":
      dateTimeFormat = formatLong.dateTime({ width: "short" });
      break;
    case "PP":
      dateTimeFormat = formatLong.dateTime({ width: "medium" });
      break;
    case "PPP":
      dateTimeFormat = formatLong.dateTime({ width: "long" });
      break;
    case "PPPP":
    default:
      dateTimeFormat = formatLong.dateTime({ width: "full" });
      break;
  }

  return dateTimeFormat
    .replace("{{date}}", dateLongFormatter(datePattern, formatLong))
    .replace("{{time}}", timeLongFormatter(timePattern, formatLong));
};

const longFormatters = {
  p: timeLongFormatter,
  P: dateTimeLongFormatter,
};

const dayOfYearTokenRE = /^D+$/;
const weekYearTokenRE = /^Y+$/;

const throwTokens = ["D", "DD", "YY", "YYYY"];

function isProtectedDayOfYearToken(token) {
  return dayOfYearTokenRE.test(token);
}

function isProtectedWeekYearToken(token) {
  return weekYearTokenRE.test(token);
}

function warnOrThrowProtectedError(token, format, input) {
  const _message = message(token, format, input);
  console.warn(_message);
  if (throwTokens.includes(token)) throw new RangeError(_message);
}

function message(token, format, input) {
  const subject = token[0] === "Y" ? "years" : "days of the month";
  return `Use \`${token.toLowerCase()}\` instead of \`${token}\` (in \`${format}\`) for formatting ${subject} to the input \`${input}\`; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md`;
}

// This RegExp consists of three parts separated by `|`:
// - [yYQqMLwIdDecihHKkms]o matches any available ordinal number token
//   (one of the certain letters followed by `o`)
// - (\w)\1* matches any sequences of the same letter
// - '' matches two quote characters in a row
// - '(''|[^'])+('|$) matches anything surrounded by two quote characters ('),
//   except a single quote symbol, which ends the sequence.
//   Two quote characters do not end the sequence.
//   If there is no matching single quote
//   then the sequence will continue until the end of the string.
// - . matches any single character unmatched by previous parts of the RegExps
const formattingTokensRegExp =
  /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g;

// This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`
const longFormattingTokensRegExp = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;

const escapedStringRegExp = /^'([^]*?)'?$/;
const doubleQuoteRegExp = /''/g;
const unescapedLatinCharacterRegExp = /[a-zA-Z]/;

/**
 * The {@link format} function options.
 */

/**
 * @name format
 * @alias formatDate
 * @category Common Helpers
 * @summary Format the date.
 *
 * @description
 * Return the formatted date string in the given format. The result may vary by locale.
 *
 * > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
 * > See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * The characters wrapped between two single quotes characters (') are escaped.
 * Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
 * (see the last example)
 *
 * Format of the string is based on Unicode Technical Standard #35:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * with a few additions (see note 7 below the table).
 *
 * Accepted patterns:
 * | Unit                            | Pattern | Result examples                   | Notes |
 * |---------------------------------|---------|-----------------------------------|-------|
 * | Era                             | G..GGG  | AD, BC                            |       |
 * |                                 | GGGG    | Anno Domini, Before Christ        | 2     |
 * |                                 | GGGGG   | A, B                              |       |
 * | Calendar year                   | y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | yo      | 44th, 1st, 0th, 17th              | 5,7   |
 * |                                 | yy      | 44, 01, 00, 17                    | 5     |
 * |                                 | yyy     | 044, 001, 1900, 2017              | 5     |
 * |                                 | yyyy    | 0044, 0001, 1900, 2017            | 5     |
 * |                                 | yyyyy   | ...                               | 3,5   |
 * | Local week-numbering year       | Y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | Yo      | 44th, 1st, 1900th, 2017th         | 5,7   |
 * |                                 | YY      | 44, 01, 00, 17                    | 5,8   |
 * |                                 | YYY     | 044, 001, 1900, 2017              | 5     |
 * |                                 | YYYY    | 0044, 0001, 1900, 2017            | 5,8   |
 * |                                 | YYYYY   | ...                               | 3,5   |
 * | ISO week-numbering year         | R       | -43, 0, 1, 1900, 2017             | 5,7   |
 * |                                 | RR      | -43, 00, 01, 1900, 2017           | 5,7   |
 * |                                 | RRR     | -043, 000, 001, 1900, 2017        | 5,7   |
 * |                                 | RRRR    | -0043, 0000, 0001, 1900, 2017     | 5,7   |
 * |                                 | RRRRR   | ...                               | 3,5,7 |
 * | Extended year                   | u       | -43, 0, 1, 1900, 2017             | 5     |
 * |                                 | uu      | -43, 01, 1900, 2017               | 5     |
 * |                                 | uuu     | -043, 001, 1900, 2017             | 5     |
 * |                                 | uuuu    | -0043, 0001, 1900, 2017           | 5     |
 * |                                 | uuuuu   | ...                               | 3,5   |
 * | Quarter (formatting)            | Q       | 1, 2, 3, 4                        |       |
 * |                                 | Qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | QQ      | 01, 02, 03, 04                    |       |
 * |                                 | QQQ     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | QQQQQ   | 1, 2, 3, 4                        | 4     |
 * | Quarter (stand-alone)           | q       | 1, 2, 3, 4                        |       |
 * |                                 | qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | qq      | 01, 02, 03, 04                    |       |
 * |                                 | qqq     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | qqqqq   | 1, 2, 3, 4                        | 4     |
 * | Month (formatting)              | M       | 1, 2, ..., 12                     |       |
 * |                                 | Mo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | MM      | 01, 02, ..., 12                   |       |
 * |                                 | MMM     | Jan, Feb, ..., Dec                |       |
 * |                                 | MMMM    | January, February, ..., December  | 2     |
 * |                                 | MMMMM   | J, F, ..., D                      |       |
 * | Month (stand-alone)             | L       | 1, 2, ..., 12                     |       |
 * |                                 | Lo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | LL      | 01, 02, ..., 12                   |       |
 * |                                 | LLL     | Jan, Feb, ..., Dec                |       |
 * |                                 | LLLL    | January, February, ..., December  | 2     |
 * |                                 | LLLLL   | J, F, ..., D                      |       |
 * | Local week of year              | w       | 1, 2, ..., 53                     |       |
 * |                                 | wo      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | ww      | 01, 02, ..., 53                   |       |
 * | ISO week of year                | I       | 1, 2, ..., 53                     | 7     |
 * |                                 | Io      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | II      | 01, 02, ..., 53                   | 7     |
 * | Day of month                    | d       | 1, 2, ..., 31                     |       |
 * |                                 | do      | 1st, 2nd, ..., 31st               | 7     |
 * |                                 | dd      | 01, 02, ..., 31                   |       |
 * | Day of year                     | D       | 1, 2, ..., 365, 366               | 9     |
 * |                                 | Do      | 1st, 2nd, ..., 365th, 366th       | 7     |
 * |                                 | DD      | 01, 02, ..., 365, 366             | 9     |
 * |                                 | DDD     | 001, 002, ..., 365, 366           |       |
 * |                                 | DDDD    | ...                               | 3     |
 * | Day of week (formatting)        | E..EEE  | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | EEEEE   | M, T, W, T, F, S, S               |       |
 * |                                 | EEEEEE  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | ISO day of week (formatting)    | i       | 1, 2, 3, ..., 7                   | 7     |
 * |                                 | io      | 1st, 2nd, ..., 7th                | 7     |
 * |                                 | ii      | 01, 02, ..., 07                   | 7     |
 * |                                 | iii     | Mon, Tue, Wed, ..., Sun           | 7     |
 * |                                 | iiii    | Monday, Tuesday, ..., Sunday      | 2,7   |
 * |                                 | iiiii   | M, T, W, T, F, S, S               | 7     |
 * |                                 | iiiiii  | Mo, Tu, We, Th, Fr, Sa, Su        | 7     |
 * | Local day of week (formatting)  | e       | 2, 3, 4, ..., 1                   |       |
 * |                                 | eo      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | ee      | 02, 03, ..., 01                   |       |
 * |                                 | eee     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | eeeee   | M, T, W, T, F, S, S               |       |
 * |                                 | eeeeee  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | Local day of week (stand-alone) | c       | 2, 3, 4, ..., 1                   |       |
 * |                                 | co      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | cc      | 02, 03, ..., 01                   |       |
 * |                                 | ccc     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | ccccc   | M, T, W, T, F, S, S               |       |
 * |                                 | cccccc  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | AM, PM                          | a..aa   | AM, PM                            |       |
 * |                                 | aaa     | am, pm                            |       |
 * |                                 | aaaa    | a.m., p.m.                        | 2     |
 * |                                 | aaaaa   | a, p                              |       |
 * | AM, PM, noon, midnight          | b..bb   | AM, PM, noon, midnight            |       |
 * |                                 | bbb     | am, pm, noon, midnight            |       |
 * |                                 | bbbb    | a.m., p.m., noon, midnight        | 2     |
 * |                                 | bbbbb   | a, p, n, mi                       |       |
 * | Flexible day period             | B..BBB  | at night, in the morning, ...     |       |
 * |                                 | BBBB    | at night, in the morning, ...     | 2     |
 * |                                 | BBBBB   | at night, in the morning, ...     |       |
 * | Hour [1-12]                     | h       | 1, 2, ..., 11, 12                 |       |
 * |                                 | ho      | 1st, 2nd, ..., 11th, 12th         | 7     |
 * |                                 | hh      | 01, 02, ..., 11, 12               |       |
 * | Hour [0-23]                     | H       | 0, 1, 2, ..., 23                  |       |
 * |                                 | Ho      | 0th, 1st, 2nd, ..., 23rd          | 7     |
 * |                                 | HH      | 00, 01, 02, ..., 23               |       |
 * | Hour [0-11]                     | K       | 1, 2, ..., 11, 0                  |       |
 * |                                 | Ko      | 1st, 2nd, ..., 11th, 0th          | 7     |
 * |                                 | KK      | 01, 02, ..., 11, 00               |       |
 * | Hour [1-24]                     | k       | 24, 1, 2, ..., 23                 |       |
 * |                                 | ko      | 24th, 1st, 2nd, ..., 23rd         | 7     |
 * |                                 | kk      | 24, 01, 02, ..., 23               |       |
 * | Minute                          | m       | 0, 1, ..., 59                     |       |
 * |                                 | mo      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | mm      | 00, 01, ..., 59                   |       |
 * | Second                          | s       | 0, 1, ..., 59                     |       |
 * |                                 | so      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | ss      | 00, 01, ..., 59                   |       |
 * | Fraction of second              | S       | 0, 1, ..., 9                      |       |
 * |                                 | SS      | 00, 01, ..., 99                   |       |
 * |                                 | SSS     | 000, 001, ..., 999                |       |
 * |                                 | SSSS    | ...                               | 3     |
 * | Timezone (ISO-8601 w/ Z)        | X       | -08, +0530, Z                     |       |
 * |                                 | XX      | -0800, +0530, Z                   |       |
 * |                                 | XXX     | -08:00, +05:30, Z                 |       |
 * |                                 | XXXX    | -0800, +0530, Z, +123456          | 2     |
 * |                                 | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
 * | Timezone (ISO-8601 w/o Z)       | x       | -08, +0530, +00                   |       |
 * |                                 | xx      | -0800, +0530, +0000               |       |
 * |                                 | xxx     | -08:00, +05:30, +00:00            | 2     |
 * |                                 | xxxx    | -0800, +0530, +0000, +123456      |       |
 * |                                 | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
 * | Timezone (GMT)                  | O...OOO | GMT-8, GMT+5:30, GMT+0            |       |
 * |                                 | OOOO    | GMT-08:00, GMT+05:30, GMT+00:00   | 2     |
 * | Timezone (specific non-locat.)  | z...zzz | GMT-8, GMT+5:30, GMT+0            | 6     |
 * |                                 | zzzz    | GMT-08:00, GMT+05:30, GMT+00:00   | 2,6   |
 * | Seconds timestamp               | t       | 512969520                         | 7     |
 * |                                 | tt      | ...                               | 3,7   |
 * | Milliseconds timestamp          | T       | 512969520900                      | 7     |
 * |                                 | TT      | ...                               | 3,7   |
 * | Long localized date             | P       | 04/29/1453                        | 7     |
 * |                                 | PP      | Apr 29, 1453                      | 7     |
 * |                                 | PPP     | April 29th, 1453                  | 7     |
 * |                                 | PPPP    | Friday, April 29th, 1453          | 2,7   |
 * | Long localized time             | p       | 12:00 AM                          | 7     |
 * |                                 | pp      | 12:00:00 AM                       | 7     |
 * |                                 | ppp     | 12:00:00 AM GMT+2                 | 7     |
 * |                                 | pppp    | 12:00:00 AM GMT+02:00             | 2,7   |
 * | Combination of date and time    | Pp      | 04/29/1453, 12:00 AM              | 7     |
 * |                                 | PPpp    | Apr 29, 1453, 12:00:00 AM         | 7     |
 * |                                 | PPPppp  | April 29th, 1453 at ...           | 7     |
 * |                                 | PPPPpppp| Friday, April 29th, 1453 at ...   | 2,7   |
 * Notes:
 * 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
 *    are the same as "stand-alone" units, but are different in some languages.
 *    "Formatting" units are declined according to the rules of the language
 *    in the context of a date. "Stand-alone" units are always nominative singular:
 *
 *    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
 *
 *    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
 *
 * 2. Any sequence of the identical letters is a pattern, unless it is escaped by
 *    the single quote characters (see below).
 *    If the sequence is longer than listed in table (e.g. `EEEEEEEEEEE`)
 *    the output will be the same as default pattern for this unit, usually
 *    the longest one (in case of ISO weekdays, `EEEE`). Default patterns for units
 *    are marked with "2" in the last column of the table.
 *
 *    `format(new Date(2017, 10, 6), 'MMM') //=> 'Nov'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMM') //=> 'N'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMMM') //=> 'November'`
 *
 * 3. Some patterns could be unlimited length (such as `yyyyyyyy`).
 *    The output will be padded with zeros to match the length of the pattern.
 *
 *    `format(new Date(2017, 10, 6), 'yyyyyyyy') //=> '00002017'`
 *
 * 4. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
 *    These tokens represent the shortest form of the quarter.
 *
 * 5. The main difference between `y` and `u` patterns are B.C. years:
 *
 *    | Year | `y` | `u` |
 *    |------|-----|-----|
 *    | AC 1 |   1 |   1 |
 *    | BC 1 |   1 |   0 |
 *    | BC 2 |   2 |  -1 |
 *
 *    Also `yy` always returns the last two digits of a year,
 *    while `uu` pads single digit years to 2 characters and returns other years unchanged:
 *
 *    | Year | `yy` | `uu` |
 *    |------|------|------|
 *    | 1    |   01 |   01 |
 *    | 14   |   14 |   14 |
 *    | 376  |   76 |  376 |
 *    | 1453 |   53 | 1453 |
 *
 *    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
 *    except local week-numbering years are dependent on `options.weekStartsOn`
 *    and `options.firstWeekContainsDate` (compare [getISOWeekYear](https://date-fns.org/docs/getISOWeekYear)
 *    and [getWeekYear](https://date-fns.org/docs/getWeekYear)).
 *
 * 6. Specific non-location timezones are currently unavailable in `date-fns`,
 *    so right now these tokens fall back to GMT timezones.
 *
 * 7. These patterns are not in the Unicode Technical Standard #35:
 *    - `i`: ISO day of week
 *    - `I`: ISO week of year
 *    - `R`: ISO week-numbering year
 *    - `t`: seconds timestamp
 *    - `T`: milliseconds timestamp
 *    - `o`: ordinal number modifier
 *    - `P`: long localized date
 *    - `p`: long localized time
 *
 * 8. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
 *    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * 9. `D` and `DD` tokens represent days of the year but they are often confused with days of the month.
 *    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 * @param format - The string of tokens
 * @param options - An object with options
 *
 * @returns The formatted date string
 *
 * @throws `date` must not be Invalid Date
 * @throws `options.locale` must contain `localize` property
 * @throws `options.locale` must contain `formatLong` property
 * @throws use `yyyy` instead of `YYYY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `yy` instead of `YY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `d` instead of `D` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `dd` instead of `DD` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws format string contains an unescaped latin alphabet character
 *
 * @example
 * // Represent 11 February 2014 in middle-endian format:
 * const result = format(new Date(2014, 1, 11), 'MM/dd/yyyy')
 * //=> '02/11/2014'
 *
 * @example
 * // Represent 2 July 2014 in Esperanto:
 * import { eoLocale } from 'date-fns/locale/eo'
 * const result = format(new Date(2014, 6, 2), "do 'de' MMMM yyyy", {
 *   locale: eoLocale
 * })
 * //=> '2-a de julio 2014'
 *
 * @example
 * // Escape string by single quote characters:
 * const result = format(new Date(2014, 6, 2, 15), "h 'o''clock'")
 * //=> "3 o'clock"
 */
function format(date, formatStr, options) {
  const defaultOptions = getDefaultOptions();
  const locale = defaultOptions.locale ?? enUS;

  const firstWeekContainsDate =
    defaultOptions.firstWeekContainsDate ??
    defaultOptions.locale?.options?.firstWeekContainsDate ??
    1;

  const weekStartsOn =
    defaultOptions.weekStartsOn ??
    defaultOptions.locale?.options?.weekStartsOn ??
    0;

  const originalDate = toDate(date);

  if (!isValid(originalDate)) {
    throw new RangeError("Invalid time value");
  }

  let parts = formatStr
    .match(longFormattingTokensRegExp)
    .map((substring) => {
      const firstCharacter = substring[0];
      if (firstCharacter === "p" || firstCharacter === "P") {
        const longFormatter = longFormatters[firstCharacter];
        return longFormatter(substring, locale.formatLong);
      }
      return substring;
    })
    .join("")
    .match(formattingTokensRegExp)
    .map((substring) => {
      // Replace two single quote characters with one single quote character
      if (substring === "''") {
        return { isToken: false, value: "'" };
      }

      const firstCharacter = substring[0];
      if (firstCharacter === "'") {
        return { isToken: false, value: cleanEscapedString(substring) };
      }

      if (formatters[firstCharacter]) {
        return { isToken: true, value: substring };
      }

      if (firstCharacter.match(unescapedLatinCharacterRegExp)) {
        throw new RangeError(
          "Format string contains an unescaped latin alphabet character `" +
            firstCharacter +
            "`",
        );
      }

      return { isToken: false, value: substring };
    });

  // invoke localize preprocessor (only for french locales at the moment)
  if (locale.localize.preprocessor) {
    parts = locale.localize.preprocessor(originalDate, parts);
  }

  const formatterOptions = {
    firstWeekContainsDate,
    weekStartsOn,
    locale,
  };

  return parts
    .map((part) => {
      if (!part.isToken) return part.value;

      const token = part.value;

      if (
        (isProtectedWeekYearToken(token)) ||
        (isProtectedDayOfYearToken(token))
      ) {
        warnOrThrowProtectedError(token, formatStr, String(date));
      }

      const formatter = formatters[token[0]];
      return formatter(originalDate, token, locale.localize, formatterOptions);
    })
    .join("");
}

function cleanEscapedString(input) {
  const matched = input.match(escapedStringRegExp);

  if (!matched) {
    return input;
  }

  return matched[1].replace(doubleQuoteRegExp, "'");
}

const DatePicker = React.forwardRef(({ value: controlled, defaultValue, onValueChange, placeholder = 'Pick date', className, disabled, ...rest }, ref) => {
    const [internal, setInternal] = React.useState(defaultValue);
    const value = controlled ?? internal;
    const commit = (d) => {
        if (controlled === undefined)
            setInternal(d);
        onValueChange?.(d);
    };
    return (jsxs(Popover$1.Root, { children: [jsxs(Popover$1.Trigger, { ref: ref, disabled: disabled, className: cn('flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-border bg-surface px-3 text-sm text-surface-foreground', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'disabled:cursor-not-allowed disabled:opacity-50', className), ...rest, children: [jsx("span", { className: cn(!value && 'text-muted-foreground'), children: value ? format(value, 'MMM d, yyyy') : placeholder }), jsx(Icon, { icon: Calendar$1, size: 14, className: "text-muted-foreground" })] }), jsx(Popover$1.Portal, { children: jsx(Popover$1.Content, { sideOffset: 4, className: "z-popover rounded-lg border border-border bg-surface shadow-md", children: jsx(Calendar, { mode: "single", selected: value, onSelect: commit, initialFocus: true }) }) })] }));
});
DatePicker.displayName = 'DatePicker';

const Item$5 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAccordion.Item, { ref: ref, className: cn('border-b border-border', className), ...rest })));
Item$5.displayName = 'Accordion.Item';
const Trigger$9 = React.forwardRef(({ className, children, ...rest }, ref) => (jsx(RadixAccordion.Header, { className: "flex", children: jsxs(RadixAccordion.Trigger, { ref: ref, className: cn('flex flex-1 items-center justify-between gap-2 py-3 text-sm font-medium text-foreground transition-all', 'hover:underline data-[state=open]:[&>svg]:rotate-180', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', className), ...rest, children: [children, jsx(Icon, { icon: ChevronDown, size: 16, className: "shrink-0 text-muted-foreground transition-transform" })] }) })));
Trigger$9.displayName = 'Accordion.Trigger';
const Content$9 = React.forwardRef(({ className, children, ...rest }, ref) => (jsx(RadixAccordion.Content, { ref: ref, className: cn('overflow-hidden text-sm text-muted-foreground data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up', className), ...rest, children: jsx("div", { className: "pb-3", children: children }) })));
Content$9.displayName = 'Accordion.Content';
const AccordionRootWrapper = ((props) => jsx(RadixAccordion.Root, { ...props }));
AccordionRootWrapper.displayName = 'Accordion';
const Accordion = Object.assign(AccordionRootWrapper, {
    Root: AccordionRootWrapper,
    Item: Item$5,
    Trigger: Trigger$9,
    Content: Content$9,
});
const AccordionItem = Item$5;
const AccordionTrigger = Trigger$9;
const AccordionContent = Content$9;

const Root$7 = React.forwardRef(({ className, ...rest }, ref) => (jsx(Command$1, { ref: ref, className: cn('flex h-full w-full flex-col overflow-hidden rounded-lg bg-surface text-surface-foreground', className), ...rest })));
Root$7.displayName = 'Command.Root';
const Input = React.forwardRef(({ className, ...rest }, ref) => (jsxs("div", { className: "flex items-center gap-2 border-b border-border px-3", children: [jsx(Icon, { icon: Search, size: 14, className: "text-muted-foreground" }), jsx(Command$1.Input, { ref: ref, className: cn('flex h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground', className), ...rest })] })));
Input.displayName = 'Command.Input';
const List$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx(Command$1.List, { ref: ref, className: cn('max-h-[300px] overflow-y-auto p-1', className), ...rest })));
List$1.displayName = 'Command.List';
const Empty = React.forwardRef(({ className, ...rest }, ref) => (jsx(Command$1.Empty, { ref: ref, className: cn('py-6 text-center text-sm text-muted-foreground', className), ...rest })));
Empty.displayName = 'Command.Empty';
const Group$2 = React.forwardRef(({ className, ...rest }, ref) => (jsx(Command$1.Group, { ref: ref, className: cn('text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.08em] [&_[cmdk-group-heading]]:text-muted-foreground', className), ...rest })));
Group$2.displayName = 'Command.Group';
const Item$4 = React.forwardRef(({ className, ...rest }, ref) => (jsx(Command$1.Item, { ref: ref, className: cn('relative flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none', 'data-[selected=true]:bg-muted data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50', className), ...rest })));
Item$4.displayName = 'Command.Item';
const Separator$3 = React.forwardRef(({ className, ...rest }, ref) => (jsx(Command$1.Separator, { ref: ref, className: cn('-mx-1 my-1 h-px bg-border', className), ...rest })));
Separator$3.displayName = 'Command.Separator';
const Command = Object.assign(Root$7, {
    Root: Root$7,
    Input,
    List: List$1,
    Empty,
    Group: Group$2,
    Item: Item$4,
    Separator: Separator$3,
});
const CommandInput = Input;
const CommandList = List$1;
const CommandEmpty = Empty;
const CommandGroup = Group$2;
const CommandItem = Item$4;
const CommandSeparator = Separator$3;

const Root$6 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixMenubar.Root, { ref: ref, className: cn('flex h-10 items-center gap-1 rounded-lg border border-border bg-surface p-1', className), ...rest })));
Root$6.displayName = 'Menubar.Root';
const Menu = RadixMenubar.Menu;
const Trigger$8 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixMenubar.Trigger, { ref: ref, className: cn('flex cursor-default select-none items-center rounded-md px-3 py-1 text-sm font-medium outline-none', 'focus:bg-muted data-[state=open]:bg-muted', className), ...rest })));
Trigger$8.displayName = 'Menubar.Trigger';
const itemBase$2 = 'flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50';
const Content$8 = React.forwardRef(({ className, align = 'start', sideOffset = 8, ...rest }, ref) => (jsx(RadixMenubar.Portal, { children: jsx(RadixMenubar.Content, { ref: ref, align: align, sideOffset: sideOffset, className: cn('z-popover min-w-[12rem] overflow-hidden rounded-lg border border-border bg-surface p-1 shadow-md', className), ...rest }) })));
Content$8.displayName = 'Menubar.Content';
const Item$3 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixMenubar.Item, { ref: ref, className: cn(itemBase$2, className), ...rest })));
Item$3.displayName = 'Menubar.Item';
const Separator$2 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixMenubar.Separator, { ref: ref, className: cn('-mx-1 my-1 h-px bg-border', className), ...rest })));
Separator$2.displayName = 'Menubar.Separator';
const Shortcut$2 = ({ className, ...rest }) => (jsx("span", { className: cn('ml-auto text-xs text-muted-foreground', className), ...rest }));
const Menubar = Object.assign(Root$6, {
    Root: Root$6,
    Menu,
    Trigger: Trigger$8,
    Content: Content$8,
    Item: Item$3,
    Separator: Separator$2,
    Shortcut: Shortcut$2,
});
const MenubarMenu = Menu;
const MenubarTrigger = Trigger$8;
const MenubarContent = Content$8;
const MenubarItem = Item$3;
const MenubarSeparator = Separator$2;
const MenubarShortcut = Shortcut$2;

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];
function Pagination(props) {
    const normalized = React.useMemo(() => {
        if ('pagination' in props || 'onLimitChange' in props) {
            const v1 = props;
            const pageCount = v1.pagination?.totalPages ?? 1;
            const page = v1.pagination?.page ?? 1;
            const pageSize = v1.pagination?.limit ?? 20;
            const totalRecords = v1.pagination?.total ?? 0;
            return {
                page,
                pageCount,
                pageSize,
                pageSizeOptions: v1.pageSizeOptions ?? [...DEFAULT_PAGE_SIZE_OPTIONS],
                totalRecords,
                onPageChange: v1.onPageChange,
                onPageSizeChange: v1.onLimitChange,
                hasControls: true,
                isLoading: v1.isLoading === true,
                className: v1.className,
            };
        }
        if (props.variant === 'controls') {
            const v2 = props;
            return {
                page: v2.page,
                pageCount: v2.pageCount,
                pageSize: v2.pageSize,
                pageSizeOptions: v2.pageSizeOptions ?? [...DEFAULT_PAGE_SIZE_OPTIONS],
                totalRecords: v2.totalRecords,
                onPageChange: v2.onPageChange,
                onPageSizeChange: v2.onPageSizeChange,
                hasControls: true,
                isLoading: false,
                className: v2.className,
            };
        }
        const simple = props;
        return {
            page: simple.page,
            pageCount: simple.pageCount,
            pageSize: 0,
            pageSizeOptions: [],
            totalRecords: 0,
            onPageChange: simple.onPageChange,
            onPageSizeChange: (_size) => { },
            hasControls: false,
            isLoading: false,
            className: simple.className,
        };
    }, [props]);
    const { page, pageCount, pageSize, pageSizeOptions, totalRecords, onPageChange, onPageSizeChange, hasControls, isLoading, className } = normalized;
    const canPrev = page > 1 && !isLoading;
    const canNext = page < pageCount && !isLoading;
    const prev = () => canPrev && onPageChange(page - 1);
    const next = () => canNext && onPageChange(page + 1);
    const first = () => canPrev && onPageChange(1);
    const last = () => canNext && onPageChange(pageCount);
    const nav = (jsxs("div", { className: "flex items-center gap-1", children: [jsx(IconButton, { icon: ChevronFirst, "aria-label": "First page", variant: "ghost", size: "sm", onClick: first, disabled: !canPrev }), jsx(IconButton, { icon: ChevronLeft, "aria-label": "Previous page", variant: "ghost", size: "sm", onClick: prev, disabled: !canPrev }), jsxs("span", { className: "px-2 text-sm text-muted-foreground", children: ["Page ", page, " of ", pageCount] }), jsx(IconButton, { icon: ChevronRight, "aria-label": "Next page", variant: "ghost", size: "sm", onClick: next, disabled: !canNext }), jsx(IconButton, { icon: ChevronLast, "aria-label": "Last page", variant: "ghost", size: "sm", onClick: last, disabled: !canNext })] }));
    if (!hasControls) {
        return jsx("nav", { className: cn('flex justify-end', className), children: nav });
    }
    const from = totalRecords === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, totalRecords);
    return (jsxs("nav", { className: cn('flex flex-wrap items-center justify-between gap-3', className), children: [jsxs("div", { className: "flex items-center gap-2 text-sm text-muted-foreground", children: [jsxs(Select.Root, { value: String(pageSize), onValueChange: (v) => onPageSizeChange(Number(v)), children: [jsx(Select.Trigger, { "aria-label": "Rows per page", className: "w-28 whitespace-nowrap", children: jsx(Select.Value, {}) }), jsx(Select.Content, { children: pageSizeOptions.map(opt => (jsxs(Select.Item, { value: String(opt), className: "whitespace-nowrap", children: [opt, " / page"] }, opt))) })] }), jsx("span", { children: totalRecords === 0
                            ? 'No items'
                            : jsxs(Fragment, { children: ["Showing ", jsxs("strong", { className: "text-foreground", children: [from, "\u2013", to] }), " of ", jsx("strong", { className: "text-foreground", children: totalRecords })] }) })] }), nav] }));
}

const List = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixTabs.List, { ref: ref, className: cn('flex items-center gap-1 border-b border-border', className), ...rest })));
List.displayName = 'Tabs.List';
const Trigger$7 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixTabs.Trigger, { ref: ref, className: cn('inline-flex items-center gap-2 border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground transition-colors', 'hover:text-foreground', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'data-[state=active]:border-primary data-[state=active]:text-foreground', 'disabled:pointer-events-none disabled:opacity-50', className), ...rest })));
Trigger$7.displayName = 'Tabs.Trigger';
const Content$7 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixTabs.Content, { ref: ref, className: cn('mt-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', className), ...rest })));
Content$7.displayName = 'Tabs.Content';
const TabsRootWrapper = ((props) => jsx(RadixTabs.Root, { ...props }));
TabsRootWrapper.displayName = 'Tabs';
const Tabs = Object.assign(TabsRootWrapper, {
    Root: TabsRootWrapper,
    List,
    Trigger: Trigger$7,
    Content: Content$7,
});
const TabsList = List;
const TabsTrigger = Trigger$7;
const TabsContent = Content$7;

const cardStyles$1 = cva('rounded-xl border bg-surface text-surface-foreground', {
    variants: {
        variant: {
            default: 'border-border shadow-sm',
            elevated: 'border-border shadow-md',
            summary: 'border-border shadow-sm',
            flat: 'border-border shadow-none',
        },
        tone: {
            neutral: '',
            success: '',
            warning: '',
            destructive: '',
            info: '',
        },
    },
    defaultVariants: { variant: 'default', tone: 'neutral' },
});
const iconToneText = {
    primary: 'text-primary',
    accent: 'text-primary',
    success: 'text-success-foreground',
    warning: 'text-warning-foreground',
    destructive: 'text-destructive-foreground',
    info: 'text-info-foreground',
    neutral: 'text-muted-foreground',
};
function renderLegacySummary(props) {
    const { title, value, icon, iconTone = 'primary', description, valueClassName, trend } = props;
    return (jsxs("div", { className: "p-5", children: [jsxs("div", { className: "flex items-center justify-between gap-3", children: [jsx("div", { className: "truncate text-sm font-medium text-muted-foreground", children: title }), icon && (jsx("div", { className: cn('shrink-0 [&_svg]:h-4 [&_svg]:w-4', iconToneText[iconTone]), children: icon }))] }), jsx("div", { className: cn('mt-3 text-3xl font-semibold tracking-tight tabular-nums text-foreground', valueClassName), children: value }), description && jsx("p", { className: "mt-1 text-xs text-muted-foreground", children: description }), trend && (jsxs("p", { className: cn('mt-1 text-xs font-medium', trend.isPositive ? 'text-success-foreground' : 'text-destructive-foreground'), children: [trend.isPositive ? '↑' : '↓', " ", Math.abs(trend.value), "%"] }))] }));
}
const Root$5 = React.forwardRef(({ className, variant, tone, title, value, icon, iconTone, description, valueClassName, trend, children, ...rest }, ref) => {
    const isLegacySummary = title !== undefined && value !== undefined;
    return (jsx("div", { ref: ref, className: cn(cardStyles$1({ variant, tone }), className), ...rest, children: isLegacySummary
            ? renderLegacySummary({ title, value, icon, iconTone, description, valueClassName, trend })
            : children }));
});
Root$5.displayName = 'Card.Root';
const Header$3 = React.forwardRef(({ className, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('flex items-center justify-between gap-3 border-b border-border px-5 py-4', className), ...rest })));
Header$3.displayName = 'Card.Header';
const Title$5 = React.forwardRef(({ className, ...rest }, ref) => (jsx("h3", { ref: ref, className: cn('text-base font-semibold leading-tight', className), ...rest })));
Title$5.displayName = 'Card.Title';
const Description$5 = React.forwardRef(({ className, ...rest }, ref) => (jsx("p", { ref: ref, className: cn('text-sm text-muted-foreground', className), ...rest })));
Description$5.displayName = 'Card.Description';
const Body$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('p-5', className), ...rest })));
Body$1.displayName = 'Card.Body';
const Footer$4 = React.forwardRef(({ className, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('flex items-center justify-end gap-2 border-t border-border px-5 py-3', className), ...rest })));
Footer$4.displayName = 'Card.Footer';
const CardRootWrapper = React.forwardRef((props, ref) => (jsx(Root$5, { ref: ref, ...props })));
CardRootWrapper.displayName = 'Card';
const Card = Object.assign(CardRootWrapper, {
    Root: CardRootWrapper,
    Header: Header$3,
    Title: Title$5,
    Description: Description$5,
    Body: Body$1,
    Footer: Footer$4,
});
const CardHeader = Header$3;
const CardTitle = Title$5;
const CardDescription = Description$5;
const CardContent = Body$1;
const CardFooter = Footer$4;
const GRID_COLS = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
    7: 'grid-cols-7',
    8: 'grid-cols-8',
};
const colClass = (bp, cols) => {
    if (!cols)
        return '';
    const base = GRID_COLS[cols];
    return base ? `${bp}:${base}` : '';
};
const SummaryCardGrid = React.forwardRef(({ className, columns = { md: 2, lg: 4 }, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('grid gap-4', colClass('sm', columns.sm), colClass('md', columns.md), colClass('lg', columns.lg), colClass('xl', columns.xl), className), ...rest })));
SummaryCardGrid.displayName = 'SummaryCardGrid';

const CollapsibleRoot = React.forwardRef(({ children, open, onOpenChange, onToggle, ...rest }, ref) => {
    const innerRef = React.useRef(null);
    React.useImperativeHandle(ref, () => innerRef.current);
    React.useEffect(() => {
        const el = innerRef.current;
        if (!el)
            return;
        if (open !== undefined && el.open !== open) {
            el.open = open;
        }
    }, [open]);
    const handleToggle = (e) => {
        if (onOpenChange) {
            onOpenChange(e.currentTarget.open);
        }
        onToggle?.(e);
    };
    return (jsx("details", { ref: innerRef, open: open, onToggle: handleToggle, ...rest, children: children }));
});
CollapsibleRoot.displayName = 'Collapsible';
const CollapsibleTrigger = React.forwardRef(({ children, asChild, ...rest }, ref) => {
    if (asChild && React.isValidElement(children)) {
        return React.cloneElement(children, { ...rest, ref });
    }
    return (jsx("summary", { ref: ref, ...rest, children: children }));
});
CollapsibleTrigger.displayName = 'CollapsibleTrigger';
const CollapsibleContent = React.forwardRef(({ children, ...rest }, ref) => (jsx("div", { ref: ref, ...rest, children: children })));
CollapsibleContent.displayName = 'CollapsibleContent';
const Collapsible = Object.assign(CollapsibleRoot, {
    Root: CollapsibleRoot,
    Trigger: CollapsibleTrigger,
    Content: CollapsibleContent,
});

const Trigger$6 = RadixDialog.Trigger;
const Close$3 = RadixDialog.Close;
const Portal$3 = RadixDialog.Portal;
const Overlay$2 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixDialog.Overlay, { ref: ref, className: cn('fixed inset-0 z-overlay bg-neutral-900/60 backdrop-blur-sm', 'data-[state=open]:animate-in data-[state=open]:fade-in-0', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0', className), ...rest })));
Overlay$2.displayName = 'Dialog.Overlay';
const Content$6 = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(Portal$3, { children: [jsx(Overlay$2, {}), jsxs(RadixDialog.Content, { ref: ref, className: cn('fixed left-[50%] top-[50%] z-dialog grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4', 'rounded-lg border border-border bg-surface p-6 text-surface-foreground shadow-lg', className), ...rest, children: [children, jsx(RadixDialog.Close, { className: "absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring", "aria-label": "Close", children: jsx(Icon, { icon: X, size: 16 }) })] })] })));
Content$6.displayName = 'Dialog.Content';
const Header$2 = ({ className, ...rest }) => (jsx("div", { className: cn('flex flex-col gap-1.5 text-left', className), ...rest }));
const Footer$3 = ({ className, ...rest }) => (jsx("div", { className: cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className), ...rest }));
const Title$4 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixDialog.Title, { ref: ref, className: cn('text-lg font-semibold leading-tight', className), ...rest })));
Title$4.displayName = 'Dialog.Title';
const Description$4 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixDialog.Description, { ref: ref, className: cn('text-sm text-muted-foreground', className), ...rest })));
Description$4.displayName = 'Dialog.Description';
const DialogRootWrapper = ((props) => jsx(RadixDialog.Root, { ...props }));
DialogRootWrapper.displayName = 'Dialog';
const Dialog = Object.assign(DialogRootWrapper, {
    Root: DialogRootWrapper,
    Trigger: Trigger$6,
    Close: Close$3,
    Portal: Portal$3,
    Overlay: Overlay$2,
    Content: Content$6,
    Header: Header$2,
    Footer: Footer$3,
    Title: Title$4,
    Description: Description$4,
});
const DialogTrigger = Trigger$6;
const DialogContent = Content$6;
const DialogTitle = Title$4;
const DialogDescription = Description$4;
const DialogHeader = Header$2;
const DialogFooter = Footer$3;
const DialogClose = Close$3;
const DialogOverlay = Overlay$2;
const DialogPortal = Portal$3;

const Trigger$5 = RadixAlertDialog.Trigger;
const Portal$2 = RadixAlertDialog.Portal;
const Overlay$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAlertDialog.Overlay, { ref: ref, className: cn('fixed inset-0 z-overlay bg-neutral-900/60 backdrop-blur-sm', className), ...rest })));
Overlay$1.displayName = 'AlertDialog.Overlay';
const Content$5 = React.forwardRef(({ className, ...rest }, ref) => (jsxs(Portal$2, { children: [jsx(Overlay$1, {}), jsx(RadixAlertDialog.Content, { ref: ref, className: cn('fixed left-1/2 top-1/2 z-dialog grid w-full max-w-md -translate-x-1/2 -translate-y-1/2 gap-4', 'rounded-lg border border-border bg-surface p-6 text-surface-foreground shadow-lg', className), ...rest })] })));
Content$5.displayName = 'AlertDialog.Content';
const Title$3 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAlertDialog.Title, { ref: ref, className: cn('text-lg font-semibold', className), ...rest })));
Title$3.displayName = 'AlertDialog.Title';
const Description$3 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAlertDialog.Description, { ref: ref, className: cn('text-sm text-muted-foreground', className), ...rest })));
Description$3.displayName = 'AlertDialog.Description';
const Footer$2 = ({ className, ...rest }) => (jsx("div", { className: cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className), ...rest }));
const Action$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAlertDialog.Action, { ref: ref, className: cn(buttonStyles({ variant: 'destructive', size: 'md' }), className), ...rest })));
Action$1.displayName = 'AlertDialog.Action';
const Cancel = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAlertDialog.Cancel, { ref: ref, className: cn(buttonStyles({ variant: 'secondary', size: 'md' }), className), ...rest })));
Cancel.displayName = 'AlertDialog.Cancel';
const AlertDialogHeader = ({ className, ...rest }) => (jsx("div", { className: cn('flex flex-col gap-1.5 text-left', className), ...rest }));
const AlertDialogRootWrapper = ((props) => jsx(RadixAlertDialog.Root, { ...props }));
AlertDialogRootWrapper.displayName = 'AlertDialog';
const AlertDialog = Object.assign(AlertDialogRootWrapper, {
    Root: AlertDialogRootWrapper,
    Trigger: Trigger$5,
    Portal: Portal$2,
    Overlay: Overlay$1,
    Content: Content$5,
    Header: AlertDialogHeader,
    Title: Title$3,
    Description: Description$3,
    Footer: Footer$2,
    Action: Action$1,
    Cancel,
});
const AlertDialogTrigger = Trigger$5;
const AlertDialogContent = Content$5;
const AlertDialogTitle = Title$3;
const AlertDialogDescription = Description$3;
const AlertDialogFooter = Footer$2;
const AlertDialogAction = Action$1;
const AlertDialogCancel = Cancel;
const AlertDialogOverlay = Overlay$1;
const AlertDialogPortal = Portal$2;

const sheetVariants = cva([
    'fixed z-dialog flex flex-col gap-4 bg-surface text-surface-foreground p-6 shadow-lg border border-border',
    'data-[state=open]:animate-in data-[state=open]:duration-200',
    'data-[state=closed]:animate-out data-[state=closed]:duration-200',
], {
    variants: {
        side: {
            right: 'right-0 top-0 h-full w-full sm:max-w-md data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
            left: 'left-0  top-0 h-full w-full sm:max-w-md data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
            top: 'top-0    left-0 w-full h-auto data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top',
            bottom: 'bottom-0 left-0 w-full h-auto data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
        },
    },
    defaultVariants: { side: 'right' },
});
const Trigger$4 = RadixDialog.Trigger;
const Close$2 = RadixDialog.Close;
const Overlay = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixDialog.Overlay, { ref: ref, className: cn('fixed inset-0 z-overlay bg-neutral-900/50 backdrop-blur-sm', className), ...rest })));
Overlay.displayName = 'Sheet.Overlay';
const Content$4 = React.forwardRef(({ side, className, children, viewportOffset = 'none', overlayClassName, style, ...rest }, ref) => (jsxs(RadixDialog.Portal, { children: [jsx(Overlay, { className: overlayClassName, style: viewportOffset === 'app-header' ? { top: '3.5rem' } : undefined }), jsxs(RadixDialog.Content, { ref: ref, className: cn(sheetVariants({ side }), className), style: viewportOffset === 'app-header' ? { ...style, top: '3.5rem', bottom: 0, height: 'auto' } : style, ...rest, children: [children, jsx(RadixDialog.Close, { className: "absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring", "aria-label": "Close", children: jsx(Icon, { icon: X, size: 16 }) })] })] })));
Content$4.displayName = 'Sheet.Content';
const Title$2 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixDialog.Title, { ref: ref, className: cn('text-lg font-semibold', className), ...rest })));
Title$2.displayName = 'Sheet.Title';
const Description$2 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixDialog.Description, { ref: ref, className: cn('text-sm text-muted-foreground', className), ...rest })));
Description$2.displayName = 'Sheet.Description';
const Header$1 = ({ className, ...rest }) => (jsx("div", { className: cn('flex flex-col gap-1', className), ...rest }));
const Footer$1 = ({ className, ...rest }) => (jsx("div", { className: cn('mt-auto flex items-center justify-end gap-2', className), ...rest }));
const SheetRootWrapper = ((props) => jsx(RadixDialog.Root, { ...props }));
SheetRootWrapper.displayName = 'Sheet';
const Sheet = Object.assign(SheetRootWrapper, {
    Root: SheetRootWrapper,
    Trigger: Trigger$4,
    Close: Close$2,
    Overlay,
    Content: Content$4,
    Title: Title$2,
    Description: Description$2,
    Header: Header$1,
    Footer: Footer$1,
});
const SheetTrigger = Trigger$4;
const SheetContent = Content$4;
const SheetTitle = Title$2;
const SheetDescription = Description$2;
const SheetHeader = Header$1;
const SheetFooter = Footer$1;
const SheetClose = Close$2;
const SheetOverlay = Overlay;

const FormDrawer = ({ open, onOpenChange, title, description, children, footer, isDirty, widthClassName = 'max-w-xl', }) => {
    const [interacted, setInteracted] = React.useState(false);
    const [confirmDiscard, setConfirmDiscard] = React.useState(false);
    React.useEffect(() => {
        if (open)
            setInteracted(false);
    }, [open]);
    const dirty = isDirty ?? interacted;
    const requestClose = (next) => {
        if (next) {
            onOpenChange(true);
            return;
        }
        if (dirty)
            setConfirmDiscard(true);
        else
            onOpenChange(false);
    };
    return (jsxs(Fragment, { children: [jsx(Sheet, { open: open, onOpenChange: requestClose, children: jsxs(SheetContent, { side: "right", className: cn('flex w-full flex-col gap-0 p-0 sm:w-[90vw]', widthClassName), children: [jsxs(SheetHeader, { className: "border-b border-border px-6 py-4", children: [jsx(SheetTitle, { children: title }), description && jsx(SheetDescription, { children: description })] }), jsx("div", { className: "min-h-0 flex-1 overflow-y-auto px-6 py-5", onChangeCapture: () => setInteracted(true), onInput: () => setInteracted(true), children: children }), footer && (jsx("div", { className: "border-t border-border px-6 py-4", children: jsx("div", { className: "flex items-center justify-end gap-3", children: footer }) }))] }) }), jsx(AlertDialog, { open: confirmDiscard, onOpenChange: setConfirmDiscard, children: jsxs(AlertDialogContent, { children: [jsxs(AlertDialogHeader, { children: [jsx(AlertDialogTitle, { children: "Discard unsaved changes?" }), jsx(AlertDialogDescription, { children: "You have unsaved changes. If you close now, they will be lost." })] }), jsxs(AlertDialogFooter, { children: [jsx(AlertDialogCancel, { children: "Keep editing" }), jsx(AlertDialogAction, { className: "bg-destructive text-destructive-foreground hover:bg-destructive/90", onClick: () => {
                                        setConfirmDiscard(false);
                                        onOpenChange(false);
                                    }, children: "Discard" })] })] }) })] }));
};

const Trigger$3 = Popover$1.Trigger;
const Anchor = Popover$1.Anchor;
const Close$1 = Popover$1.Close;
const Content$3 = React.forwardRef(({ className, align = 'center', sideOffset = 6, ...rest }, ref) => (jsx(Popover$1.Portal, { children: jsx(Popover$1.Content, { ref: ref, align: align, sideOffset: sideOffset, className: cn('z-popover w-72 rounded-lg border border-border bg-surface p-4 text-surface-foreground shadow-md outline-none', 'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95', className), ...rest }) })));
Content$3.displayName = 'Popover.Content';
const PopoverRootWrapper = ((props) => jsx(Popover$1.Root, { ...props }));
PopoverRootWrapper.displayName = 'Popover';
const Popover = Object.assign(PopoverRootWrapper, {
    Root: PopoverRootWrapper,
    Trigger: Trigger$3,
    Anchor,
    Close: Close$1,
    Content: Content$3,
});
const PopoverTrigger = Trigger$3;
const PopoverContent = Content$3;
const PopoverAnchor = Anchor;
const PopoverClose = Close$1;

const Provider$1 = (props) => (jsx(RadixTooltip.Provider, { delayDuration: 200, ...props }));
const Trigger$2 = RadixTooltip.Trigger;
const Content$2 = React.forwardRef(({ className, sideOffset = 6, ...rest }, ref) => (jsx(RadixTooltip.Portal, { children: jsx(RadixTooltip.Content, { ref: ref, sideOffset: sideOffset, className: cn('z-popover rounded-md bg-neutral-900 px-2 py-1 text-xs text-white shadow-md', 'data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95', className), ...rest }) })));
Content$2.displayName = 'Tooltip.Content';
const TooltipRootWrapper = ((props) => jsx(RadixTooltip.Root, { ...props }));
TooltipRootWrapper.displayName = 'Tooltip';
const Tooltip = Object.assign(TooltipRootWrapper, {
    Root: TooltipRootWrapper,
    Provider: Provider$1,
    Trigger: Trigger$2,
    Content: Content$2,
});
const TooltipProvider = Provider$1;
const TooltipTrigger = Trigger$2;
const TooltipContent = Content$2;

const Trigger$1 = RadixMenu.Trigger;
const Group$1 = RadixMenu.Group;
const Sub$1 = RadixMenu.Sub;
const RadioGroup$1 = RadixMenu.RadioGroup;
const Portal$1 = RadixMenu.Portal;
const Content$1 = React.forwardRef(({ className, sideOffset = 4, ...rest }, ref) => (jsx(Portal$1, { children: jsx(RadixMenu.Content, { ref: ref, sideOffset: sideOffset, className: cn('z-popover min-w-[10rem] overflow-hidden rounded-lg border border-border bg-surface p-1 text-surface-foreground shadow-md', className), ...rest }) })));
Content$1.displayName = 'DropdownMenu.Content';
const itemBase$1 = 'flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50';
const Item$2 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixMenu.Item, { ref: ref, className: cn(itemBase$1, className), ...rest })));
Item$2.displayName = 'DropdownMenu.Item';
const CheckboxItem$1 = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixMenu.CheckboxItem, { ref: ref, className: cn(itemBase$1, 'pl-7 relative', className), ...rest, children: [jsx("span", { className: "absolute left-2 inline-flex h-3.5 w-3.5 items-center justify-center", children: jsx(RadixMenu.ItemIndicator, { children: jsx(Icon, { icon: Check, size: 12 }) }) }), children] })));
CheckboxItem$1.displayName = 'DropdownMenu.CheckboxItem';
const RadioItem$1 = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixMenu.RadioItem, { ref: ref, className: cn(itemBase$1, 'pl-7 relative', className), ...rest, children: [jsx("span", { className: "absolute left-2 inline-flex h-3.5 w-3.5 items-center justify-center", children: jsx(RadixMenu.ItemIndicator, { children: jsx(Icon, { icon: Circle, size: 12, className: "fill-current" }) }) }), children] })));
RadioItem$1.displayName = 'DropdownMenu.RadioItem';
const Label$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixMenu.Label, { ref: ref, className: cn('px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground', className), ...rest })));
Label$1.displayName = 'DropdownMenu.Label';
const Separator$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixMenu.Separator, { ref: ref, className: cn('-mx-1 my-1 h-px bg-border', className), ...rest })));
Separator$1.displayName = 'DropdownMenu.Separator';
const Shortcut$1 = ({ className, ...rest }) => (jsx("span", { className: cn('ml-auto text-xs text-muted-foreground', className), ...rest }));
const SubTrigger$1 = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixMenu.SubTrigger, { ref: ref, className: cn(itemBase$1, className), ...rest, children: [children, jsx(Icon, { icon: ChevronRight, size: 12, className: "ml-auto" })] })));
SubTrigger$1.displayName = 'DropdownMenu.SubTrigger';
const SubContent$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixMenu.SubContent, { ref: ref, className: cn('z-popover min-w-[10rem] overflow-hidden rounded-lg border border-border bg-surface p-1 shadow-md', className), ...rest })));
SubContent$1.displayName = 'DropdownMenu.SubContent';
const DropdownMenuRootWrapper = ((props) => jsx(RadixMenu.Root, { ...props }));
DropdownMenuRootWrapper.displayName = 'DropdownMenu';
const DropdownMenu = Object.assign(DropdownMenuRootWrapper, {
    Root: DropdownMenuRootWrapper,
    Trigger: Trigger$1,
    Portal: Portal$1,
    Content: Content$1,
    Group: Group$1,
    Item: Item$2,
    CheckboxItem: CheckboxItem$1,
    RadioGroup: RadioGroup$1,
    RadioItem: RadioItem$1,
    Label: Label$1,
    Separator: Separator$1,
    Shortcut: Shortcut$1,
    Sub: Sub$1,
    SubTrigger: SubTrigger$1,
    SubContent: SubContent$1,
});
const DropdownMenuTrigger = Trigger$1;
const DropdownMenuPortal = Portal$1;
const DropdownMenuContent = Content$1;
const DropdownMenuGroup = Group$1;
const DropdownMenuItem = Item$2;
const DropdownMenuCheckboxItem = CheckboxItem$1;
const DropdownMenuRadioGroup = RadioGroup$1;
const DropdownMenuRadioItem = RadioItem$1;
const DropdownMenuLabel = Label$1;
const DropdownMenuSeparator = Separator$1;
const DropdownMenuShortcut = Shortcut$1;
const DropdownMenuSub = Sub$1;
const DropdownMenuSubTrigger = SubTrigger$1;
const DropdownMenuSubContent = SubContent$1;

const Trigger = RadixCtxMenu.Trigger;
const Group = RadixCtxMenu.Group;
const Sub = RadixCtxMenu.Sub;
const RadioGroup = RadixCtxMenu.RadioGroup;
const Portal = RadixCtxMenu.Portal;
const Content = React.forwardRef(({ className, ...rest }, ref) => (jsx(Portal, { children: jsx(RadixCtxMenu.Content, { ref: ref, className: cn('z-popover min-w-[10rem] overflow-hidden rounded-lg border border-border bg-surface p-1 text-surface-foreground shadow-md', className), ...rest }) })));
Content.displayName = 'ContextMenu.Content';
const itemBase = 'flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50';
const Item$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixCtxMenu.Item, { ref: ref, className: cn(itemBase, className), ...rest })));
Item$1.displayName = 'ContextMenu.Item';
const CheckboxItem = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixCtxMenu.CheckboxItem, { ref: ref, className: cn(itemBase, 'pl-7 relative', className), ...rest, children: [jsx("span", { className: "absolute left-2 inline-flex h-3.5 w-3.5 items-center justify-center", children: jsx(RadixCtxMenu.ItemIndicator, { children: jsx(Icon, { icon: Check, size: 12 }) }) }), children] })));
CheckboxItem.displayName = 'ContextMenu.CheckboxItem';
const RadioItem = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixCtxMenu.RadioItem, { ref: ref, className: cn(itemBase, 'pl-7 relative', className), ...rest, children: [jsx("span", { className: "absolute left-2 inline-flex h-3.5 w-3.5 items-center justify-center", children: jsx(RadixCtxMenu.ItemIndicator, { children: jsx(Icon, { icon: Circle, size: 12, className: "fill-current" }) }) }), children] })));
RadioItem.displayName = 'ContextMenu.RadioItem';
const Label = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixCtxMenu.Label, { ref: ref, className: cn('px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground', className), ...rest })));
Label.displayName = 'ContextMenu.Label';
const Separator = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixCtxMenu.Separator, { ref: ref, className: cn('-mx-1 my-1 h-px bg-border', className), ...rest })));
Separator.displayName = 'ContextMenu.Separator';
const Shortcut = ({ className, ...rest }) => (jsx("span", { className: cn('ml-auto text-xs text-muted-foreground', className), ...rest }));
const SubTrigger = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixCtxMenu.SubTrigger, { ref: ref, className: cn(itemBase, className), ...rest, children: [children, jsx(Icon, { icon: ChevronRight, size: 12, className: "ml-auto" })] })));
SubTrigger.displayName = 'ContextMenu.SubTrigger';
const SubContent = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixCtxMenu.SubContent, { ref: ref, className: cn('z-popover min-w-[10rem] overflow-hidden rounded-lg border border-border bg-surface p-1 shadow-md', className), ...rest })));
SubContent.displayName = 'ContextMenu.SubContent';
const ContextMenuRootWrapper = ((props) => jsx(RadixCtxMenu.Root, { ...props }));
ContextMenuRootWrapper.displayName = 'ContextMenu';
const ContextMenu = Object.assign(ContextMenuRootWrapper, {
    Root: ContextMenuRootWrapper,
    Trigger,
    Portal,
    Content,
    Group,
    Item: Item$1,
    CheckboxItem,
    RadioGroup,
    RadioItem,
    Label,
    Separator,
    Shortcut,
    Sub,
    SubTrigger,
    SubContent,
});
const ContextMenuTrigger = Trigger;
const ContextMenuPortal = Portal;
const ContextMenuContent = Content;
const ContextMenuGroup = Group;
const ContextMenuItem = Item$1;
const ContextMenuCheckboxItem = CheckboxItem;
const ContextMenuRadioGroup = RadioGroup;
const ContextMenuRadioItem = RadioItem;
const ContextMenuLabel = Label;
const ContextMenuSeparator = Separator;
const ContextMenuShortcut = Shortcut;
const ContextMenuSub = Sub;
const ContextMenuSubTrigger = SubTrigger;
const ContextMenuSubContent = SubContent;

const badgeStyles = cva('inline-flex items-center gap-1 rounded-sm font-semibold uppercase tracking-[0.04em] transition-colors leading-none align-middle', {
    variants: {
        variant: {
            subtle: '',
            outline: 'bg-transparent border',
        },
        tone: {
            neutral: '',
            success: '',
            warning: '',
            destructive: '',
            info: '',
        },
        size: {
            xs: 'px-1.5 py-0.5 text-[11px]',
            sm: 'px-2   py-0.5 text-[11px]',
            md: 'px-2.5 py-1   text-[11px]',
        },
    },
    compoundVariants: [
        { variant: 'subtle', tone: 'neutral', class: 'bg-neutral-tone-subtle text-neutral-tone-foreground border border-neutral-tone-border' },
        { variant: 'subtle', tone: 'success', class: 'bg-success-subtle text-success-foreground border border-success-border' },
        { variant: 'subtle', tone: 'warning', class: 'bg-warning-subtle text-warning-foreground border border-warning-border' },
        { variant: 'subtle', tone: 'destructive', class: 'bg-destructive-subtle text-destructive-foreground border border-destructive-border' },
        { variant: 'subtle', tone: 'info', class: 'bg-info-subtle text-info-foreground border border-info-border' },
        { variant: 'outline', tone: 'neutral', class: 'border-border text-muted-foreground' },
        { variant: 'outline', tone: 'success', class: 'border-success text-success-foreground' },
        { variant: 'outline', tone: 'warning', class: 'border-warning text-warning-foreground' },
        { variant: 'outline', tone: 'destructive', class: 'border-destructive text-destructive-foreground' },
        { variant: 'outline', tone: 'info', class: 'border-info text-info-foreground' },
    ],
    defaultVariants: { variant: 'subtle', tone: 'neutral', size: 'sm' },
});
const V1_VARIANT_TO_V2_TONE = {
    default: 'neutral',
    secondary: 'neutral',
    destructive: 'destructive',
    success: 'success',
    warning: 'warning',
    outline: 'neutral',
};
const Badge = React.forwardRef(({ className, variant, tone, size, ...rest }, ref) => {
    let v2Variant = undefined;
    let resolvedTone = tone;
    if (variant === 'subtle' || variant === 'outline' || variant === undefined || variant === null) {
        v2Variant = variant ?? undefined;
    }
    else {
        const v1 = variant;
        v2Variant = v1 === 'outline' ? 'outline' : 'subtle';
        if (!resolvedTone)
            resolvedTone = V1_VARIANT_TO_V2_TONE[v1];
    }
    return (jsx("span", { ref: ref, className: cn(badgeStyles({ variant: v2Variant, tone: resolvedTone, size }), className), ...rest }));
});
Badge.displayName = 'Badge';

const dotStyles = cva('inline-block h-1.5 w-1.5 shrink-0 rounded-full', {
    variants: {
        tone: {
            neutral: 'bg-neutral-tone-foreground',
            success: 'bg-success',
            warning: 'bg-warning',
            destructive: 'bg-destructive',
            info: 'bg-info',
        },
    },
    defaultVariants: { tone: 'neutral' },
});
const pillStyles = cva('inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium', {
    variants: {
        tone: {
            neutral: 'bg-neutral-tone-subtle text-neutral-tone-foreground border-neutral-tone-border',
            success: 'bg-success-subtle text-success-foreground border-success-border',
            warning: 'bg-warning-subtle text-warning-foreground border-warning-border',
            destructive: 'bg-destructive-subtle text-destructive-foreground border-destructive-border',
            info: 'bg-info-subtle text-info-foreground border-info-border',
        },
    },
    defaultVariants: { tone: 'neutral' },
});
const StatusPill = React.forwardRef(({ className, tone, isLive, children, ...rest }, ref) => (jsxs("span", { ref: ref, className: cn(pillStyles({ tone }), className), ...rest, children: [jsx("span", { "data-testid": "dot", className: cn(dotStyles({ tone }), isLive && 'animate-pulse') }), children] })));
StatusPill.displayName = 'StatusPill';

const avatarStyles = cva('inline-flex shrink-0 overflow-hidden rounded-full bg-primary text-primary-foreground', {
    variants: {
        size: {
            sm: 'h-6 w-6 text-[10px]',
            md: 'h-8 w-8 text-xs',
            lg: 'h-10 w-10 text-sm',
        },
    },
    defaultVariants: { size: 'md' },
});
function initialsFrom(name) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(w => w[0].toUpperCase())
        .join('');
}
const Avatar = React.forwardRef(({ className, size, src, alt, children, ...rest }, ref) => {
    if (children !== undefined) {
        return (jsx(RadixAvatar.Root, { ref: ref, className: cn(avatarStyles({ size }), className), ...rest, children: children }));
    }
    return (jsxs(RadixAvatar.Root, { ref: ref, className: cn(avatarStyles({ size }), className), ...rest, children: [src && (jsx(RadixAvatar.Image, { src: src, alt: alt ?? '', className: "h-full w-full object-cover" })), jsx(RadixAvatar.Fallback, { className: "flex h-full w-full items-center justify-center font-semibold", children: alt ? initialsFrom(alt) : '' })] }));
});
Avatar.displayName = 'Avatar';
const AvatarImage = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAvatar.Image, { ref: ref, className: cn('aspect-square h-full w-full object-cover', className), ...rest })));
AvatarImage.displayName = 'AvatarImage';
const AvatarFallback = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAvatar.Fallback, { ref: ref, className: cn('flex h-full w-full items-center justify-center rounded-full bg-muted font-semibold', className), ...rest })));
AvatarFallback.displayName = 'AvatarFallback';

const Skeleton = React.forwardRef(({ className, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('animate-pulse rounded-md bg-muted', className), ...rest })));
Skeleton.displayName = 'Skeleton';

const Root$4 = React.forwardRef(({ className, density = 'default', ...rest }, ref) => (jsx("div", { className: "w-full overflow-x-auto", children: jsx("table", { ref: ref, "data-density": density, className: cn('w-full caption-bottom text-sm', '[&_td]:align-middle [&_th]:align-middle', '[&[data-density="comfortable"]_td]:py-4   [&[data-density="comfortable"]_td]:px-5', '[&[data-density="comfortable"]_th]:py-3.5 [&[data-density="comfortable"]_th]:px-5', '[&[data-density="default"]_td]:py-3      [&[data-density="default"]_td]:px-4', '[&[data-density="default"]_th]:py-3      [&[data-density="default"]_th]:px-4', '[&[data-density="compact"]_td]:py-2      [&[data-density="compact"]_td]:px-3', '[&[data-density="compact"]_th]:py-2      [&[data-density="compact"]_th]:px-3', className), ...rest }) })));
Root$4.displayName = 'Table.Root';
const Header = React.forwardRef(({ className, ...rest }, ref) => (jsx("thead", { ref: ref, className: cn('[&_tr]:border-b [&_tr]:border-border/60', className), ...rest })));
Header.displayName = 'Table.Header';
const Body = React.forwardRef(({ className, ...rest }, ref) => (jsx("tbody", { ref: ref, className: cn('[&_tr:last-child]:border-0', className), ...rest })));
Body.displayName = 'Table.Body';
const Footer = React.forwardRef(({ className, ...rest }, ref) => (jsx("tfoot", { ref: ref, className: cn('border-t border-border/60 font-medium [&_tr]:last:border-b-0', className), ...rest })));
Footer.displayName = 'Table.Footer';
const Row = React.forwardRef(({ className, ...rest }, ref) => (jsx("tr", { ref: ref, className: cn('border-b border-border/40 transition-colors hover:bg-muted/30', className), ...rest })));
Row.displayName = 'Table.Row';
const Head = React.forwardRef(({ className, ...rest }, ref) => (jsx("th", { ref: ref, className: cn('text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground', className), ...rest })));
Head.displayName = 'Table.Head';
const Cell = React.forwardRef(({ className, ...rest }, ref) => (jsx("td", { ref: ref, className: cn('text-foreground', className), ...rest })));
Cell.displayName = 'Table.Cell';
const Caption = React.forwardRef(({ className, ...rest }, ref) => (jsx("caption", { ref: ref, className: cn('mt-4 text-sm text-muted-foreground', className), ...rest })));
Caption.displayName = 'Table.Caption';
const Table = Object.assign(Root$4, {
    Root: Root$4,
    Header,
    Body,
    Footer,
    Row,
    Head,
    Cell,
    Caption,
});
const TableHeader = Header;
const TableBody = Body;
const TableRow = Row;
const TableHead = Head;
const TableCell = Cell;
const TableFooter = Footer;
const TableCaption = Caption;

const Root$3 = React.forwardRef(({ className, density = 'default', ...rest }, ref) => (jsx("div", { ref: ref, "data-density": density, className: cn('divide-y divide-border overflow-hidden rounded-lg border border-border bg-surface', '[&[data-density="comfortable"]_[data-slot=row]]:py-4 [&[data-density="comfortable"]_[data-slot=row]]:px-5', '[&[data-density="default"]_[data-slot=row]]:py-3    [&[data-density="default"]_[data-slot=row]]:px-4', '[&[data-density="compact"]_[data-slot=row]]:py-2    [&[data-density="compact"]_[data-slot=row]]:px-3', className), ...rest })));
Root$3.displayName = 'DataList.Root';
const Item = React.forwardRef(({ className, ...rest }, ref) => (jsx("div", { ref: ref, "data-slot": "row", className: cn('flex items-center gap-3 transition-colors hover:bg-muted/40', className), ...rest })));
Item.displayName = 'DataList.Item';
const DataList = Object.assign(Root$3, {
    Root: Root$3,
    Item,
});
const DataListItem = Item;

const EmptyState = ({ icon, title, description, action, className, compact, }) => (jsxs("div", { className: cn('flex flex-col items-center justify-center text-center', compact ? 'py-10' : 'py-16', className), children: [icon && (jsx("div", { className: "mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground", children: icon })), jsx("p", { className: "text-base font-medium text-foreground", children: title }), description && jsx("p", { className: "mt-1 max-w-sm text-sm text-muted-foreground", children: description }), action && jsx("div", { className: "mt-4", children: action })] }));

const ColumnSettingsMenu = ({ columns, hidden, onChange, align = 'end', size = 'sm', }) => (jsxs(DropdownMenu, { children: [jsx(DropdownMenuTrigger, { asChild: true, children: jsxs(Button, { variant: "outline", size: size, children: [jsx(SlidersHorizontal, { className: "mr-2 h-4 w-4" }), "Columns"] }) }), jsxs(DropdownMenuContent, { align: align, className: "w-48", children: [jsx(DropdownMenuLabel, { children: "Toggle columns" }), jsx(DropdownMenuSeparator, {}), columns
                    .filter((c) => c.hideable !== false)
                    .map((c) => (jsx(DropdownMenuCheckboxItem, { checked: !hidden.has(c.id), onCheckedChange: (checked) => {
                        const next = new Set(hidden);
                        checked ? next.delete(c.id) : next.add(c.id);
                        onChange(next);
                    }, children: c.menuLabel ?? (typeof c.header === 'string' ? c.header : c.id) }, c.id)))] })] }));
const alignClass = (align) => align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
const nextSort = (current, columnId) => {
    if (!current || current.columnId !== columnId)
        return { columnId, direction: 'asc' };
    if (current.direction === 'asc')
        return { columnId, direction: 'desc' };
    return null;
};
function DataTable({ data, columns, rowKey, onRowClick, isLoading, emptyMessage = 'No records found', density = 'default', stickyHeader = false, selectable = false, selectedIds, onSelectionChange, bulkActions, sort, onSortChange, enableColumnVisibility = true, hiddenColumns, onHiddenColumnsChange, toolbar, footer, tableClassName, }) {
    const [internalHidden, setInternalHidden] = React.useState(() => new Set(columns.filter((c) => c.defaultHidden).map((c) => c.id)));
    const isControlled = hiddenColumns !== undefined;
    const hidden = hiddenColumns ?? internalHidden;
    const setHidden = (next) => {
        if (isControlled)
            onHiddenColumnsChange?.(next);
        else
            setInternalHidden(next);
    };
    const visibleColumns = columns.filter((c) => !hidden.has(c.id));
    const selected = React.useMemo(() => new Set(selectedIds ?? []), [selectedIds]);
    const allKeys = React.useMemo(() => data.map(rowKey), [data, rowKey]);
    const allSelected = allKeys.length > 0 && allKeys.every((k) => selected.has(k));
    const someSelected = allKeys.some((k) => selected.has(k));
    const clearSelection = () => onSelectionChange?.([]);
    const toggleAll = () => onSelectionChange?.(allSelected ? [] : allKeys);
    const toggleRow = (key) => {
        const next = new Set(selected);
        next.has(key) ? next.delete(key) : next.add(key);
        onSelectionChange?.(Array.from(next));
    };
    const colSpan = visibleColumns.length + (selectable ? 1 : 0);
    const showToolbar = Boolean(toolbar) || enableColumnVisibility;
    const selectionCount = selected.size;
    return (jsxs("div", { className: "w-full space-y-6", children: [selectable && selectionCount > 0 ? (jsxs("div", { className: "flex items-center justify-between rounded-lg border border-border bg-surface px-3 py-2 shadow-sm", children: [jsxs("div", { className: "flex items-center gap-2 text-sm", children: [jsx(Button, { variant: "ghost", size: "sm", onClick: clearSelection, "aria-label": "Clear selection", children: jsx(X, { className: "h-4 w-4" }) }), jsxs("span", { className: "font-medium", children: [selectionCount, " selected"] })] }), jsx("div", { className: "flex items-center gap-2", children: bulkActions?.({ selectedIds: Array.from(selected), clear: clearSelection }) })] })) : showToolbar ? (jsxs("div", { className: "flex items-center justify-between gap-3", children: [jsx("div", { className: "min-w-0 flex-1", children: toolbar }), enableColumnVisibility && (jsx(ColumnSettingsMenu, { columns: columns, hidden: hidden, onChange: setHidden }))] })) : null, jsx("div", { className: cn('w-full overflow-x-auto rounded-xl border border-border bg-surface shadow-sm', stickyHeader && 'max-h-[70vh] overflow-auto'), children: jsxs(Table, { density: density, className: tableClassName, children: [jsx(Table.Header, { className: cn(stickyHeader && '[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-surface'), children: jsxs(Table.Row, { children: [selectable && (jsx(Table.Head, { className: "w-10", children: jsx(Checkbox, { "aria-label": "Select all rows", checked: allSelected ? true : someSelected ? 'indeterminate' : false, onCheckedChange: toggleAll }) })), visibleColumns.map((col) => {
                                        const active = sort?.columnId === col.id;
                                        const sortable = col.sortable && onSortChange;
                                        return (jsx(Table.Head, { className: cn(alignClass(col.align), sortable && 'cursor-pointer select-none', col.headerClassName), onClick: sortable ? () => onSortChange(nextSort(sort ?? null, col.id)) : undefined, "aria-sort": active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined, children: jsxs("span", { className: cn('inline-flex items-center gap-1', col.align === 'right' && 'flex-row-reverse'), children: [col.header, sortable && (active ? (sort.direction === 'asc' ? jsx(ArrowUp, { className: "h-3 w-3" }) : jsx(ArrowDown, { className: "h-3 w-3" })) : (jsx(ChevronsUpDown, { className: "h-3 w-3 opacity-40" })))] }) }, col.id));
                                    })] }) }), jsx(Table.Body, { children: isLoading ? (Array.from({ length: 5 }).map((_, i) => (jsx(Table.Row, { children: jsx(Table.Cell, { colSpan: colSpan, children: jsx("div", { className: "h-6 w-full animate-pulse rounded bg-muted" }) }) }, `sk-${i}`)))) : data.length === 0 ? (jsx(Table.Row, { children: jsx(Table.Cell, { colSpan: colSpan, className: "p-0", children: typeof emptyMessage === 'string' ? (jsx(EmptyState, { title: emptyMessage, compact: true })) : (emptyMessage) }) })) : (data.map((row) => {
                                const key = rowKey(row);
                                const isSelected = selected.has(key);
                                return (jsxs(Table.Row, { "data-selected": isSelected || undefined, className: cn(onRowClick && 'cursor-pointer', isSelected && 'bg-primary/5'), onClick: onRowClick ? () => onRowClick(row) : undefined, children: [selectable && (jsx(Table.Cell, { className: "w-10", onClick: (e) => e.stopPropagation(), children: jsx(Checkbox, { "aria-label": "Select row", checked: isSelected, onCheckedChange: () => toggleRow(key) }) })), visibleColumns.map((col) => (jsx(Table.Cell, { className: cn(alignClass(col.align), col.className), children: col.cell(row) }, col.id)))] }, key));
                            })) })] }) }), footer] }));
}

const toneTile = {
    primary: 'bg-primary-subtle text-primary',
    success: 'bg-success-subtle text-success-foreground',
    warning: 'bg-warning-subtle text-warning-foreground',
    destructive: 'bg-destructive-subtle text-destructive-foreground',
    info: 'bg-info-subtle text-info-foreground',
    neutral: 'bg-muted text-muted-foreground',
};
const MetricRibbon = ({ items, isLoading, className, ...rest }) => {
    if (isLoading) {
        return (jsx("div", { className: cn('flex flex-wrap items-stretch divide-x divide-border rounded-xl border border-border bg-surface shadow-sm', className), ...rest, children: Array.from({ length: Math.max(items.length || 4, 4) }).map((_, i) => (jsxs("div", { className: "flex min-h-[92px] flex-1 items-center gap-3 px-4 py-3", style: { minWidth: 150 }, children: [jsx("div", { className: "h-9 w-9 shrink-0 animate-pulse rounded-lg bg-muted" }), jsxs("div", { className: "min-w-0 flex-1", children: [jsx("div", { className: "h-2.5 w-1/2 animate-pulse rounded bg-muted" }), jsx("div", { className: "mt-2 h-6 w-3/4 animate-pulse rounded bg-muted" })] })] }, i))) }));
    }
    return (jsx("div", { className: cn('flex flex-wrap items-stretch divide-x divide-border rounded-xl border border-border bg-surface shadow-sm', className), ...rest, children: items.map((item) => {
            const tone = item.tone ?? 'neutral';
            const interactive = Boolean(item.onClick);
            const Comp = interactive ? 'button' : 'div';
            return (jsxs(Comp, { type: interactive ? 'button' : undefined, onClick: item.onClick, "aria-pressed": interactive ? item.active : undefined, className: cn('group flex min-h-[92px] flex-1 items-center gap-3 px-4 py-3 text-left transition-colors', interactive && 'cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', item.active && 'bg-primary/5'), style: { minWidth: 150 }, children: [item.icon && (jsx("span", { className: cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg [&>svg]:h-4 [&>svg]:w-4', toneTile[tone], interactive && 'group-hover:text-primary'), children: item.icon })), jsxs("span", { className: "min-w-0 flex-1", children: [jsx("span", { className: "block truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground", children: item.label }), jsxs("span", { className: "mt-1 flex items-baseline gap-1.5", children: [jsx("span", { className: "truncate text-xl font-semibold leading-tight tracking-tight tabular-nums text-foreground", children: item.value }), item.delta && (jsxs("span", { className: cn('inline-flex items-center gap-0.5 text-xs font-medium', item.delta.direction === 'up' && 'text-success', item.delta.direction === 'down' && 'text-destructive', item.delta.direction === 'flat' && 'text-muted-foreground'), children: [item.delta.direction === 'up' && jsx(ArrowUp, { className: "h-3 w-3" }), item.delta.direction === 'down' && jsx(ArrowDown, { className: "h-3 w-3" }), item.delta.direction === 'flat' && jsx(Minus, { className: "h-3 w-3" }), item.delta.label] }))] }), item.hint && (jsx("span", { className: "mt-1 block truncate text-xs text-muted-foreground", children: item.hint }))] }), interactive && (jsx(ChevronRight, { "aria-hidden": "true", className: "h-4 w-4 shrink-0 self-center text-muted-foreground/50 transition-colors group-hover:text-primary" }))] }, item.id));
        }) }));
};

const ScrollArea = React.forwardRef(({ className, children, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('relative overflow-auto', className), ...rest, children: children })));
ScrollArea.displayName = 'ScrollArea';
const ScrollBar = React.forwardRef(() => null);
ScrollBar.displayName = 'ScrollBar';

const ALL = '__all__';
const FilterBar = ({ filters, hasActiveFilters, onClear, clearLabel = 'Clear', trailing, className, }) => {
    return (jsx(Card, { className: cn('shadow-sm', className), children: jsx(CardContent, { className: "p-4", children: jsxs("div", { className: "flex flex-nowrap items-center gap-3 overflow-x-auto", children: [filters.map((filter) => {
                        if (filter.type === 'search') {
                            return (jsxs("div", { className: cn('relative min-w-[140px]', filter.grow !== false && 'flex-1'), children: [jsx(Search, { className: "pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" }), jsx(Input$1, { type: "text", placeholder: filter.placeholder ?? 'Search...', value: filter.value, onChange: (e) => filter.onChange(e.target.value), className: "pl-9" })] }, filter.key));
                        }
                        return (jsxs(Select, { value: filter.value ?? ALL, onValueChange: (value) => filter.onChange(value === ALL ? undefined : value), children: [jsx(SelectTrigger, { className: "shrink-0", style: { width: filter.width ?? 180 }, children: jsx(SelectValue, { placeholder: filter.placeholder }) }), jsxs(SelectContent, { children: [jsx(SelectItem, { value: ALL, children: filter.placeholder }), filter.options.map((option) => (jsx(SelectItem, { value: option.value, children: option.label }, option.value)))] })] }, filter.key));
                    }), hasActiveFilters && (jsxs(Button, { variant: "outline", size: "sm", className: "shrink-0", onClick: onClear, children: [jsx(X, { className: "mr-2 h-4 w-4" }), clearLabel] })), trailing && (jsx("div", { className: "ml-auto flex shrink-0 items-center gap-2", children: trailing }))] }) }) }));
};

const fillStyles = cva('h-full transition-[width] duration-300', {
    variants: {
        tone: {
            primary: 'bg-primary',
            success: 'bg-success',
            warning: 'bg-warning',
            destructive: 'bg-destructive',
        },
    },
    defaultVariants: { tone: 'primary' },
});
const Progress = React.forwardRef(({ className, value, max = 100, tone, isIndeterminate, ...rest }, ref) => {
    const indeterminate = isIndeterminate ?? value == null;
    return (jsx(RadixProgress.Root, { ref: ref, value: indeterminate ? null : value, max: max, className: cn('relative h-2 w-full overflow-hidden rounded-full bg-muted', className), ...rest, children: jsx(RadixProgress.Indicator, { className: cn(fillStyles({ tone }), indeterminate && 'animate-[shimmer_1500ms_linear_infinite] bg-[length:200%_100%] bg-gradient-to-r from-primary/40 via-primary to-primary/40'), style: { width: indeterminate ? '100%' : `${Math.min(100, ((value ?? 0) / max) * 100)}%` } }) }));
});
Progress.displayName = 'Progress';

const alertStyles = cva('relative w-full rounded-lg border p-4 text-sm', {
    variants: {
        tone: {
            neutral: 'bg-surface border-border text-foreground',
            success: 'bg-success-subtle border-success-border text-success-foreground',
            warning: 'bg-warning-subtle border-warning-border text-warning-foreground',
            destructive: 'bg-destructive-subtle border-destructive-border text-destructive-foreground',
            info: 'bg-info-subtle border-info-border text-info-foreground',
        },
    },
    defaultVariants: { tone: 'neutral' },
});
const Root$2 = React.forwardRef(({ className, tone, role, ...rest }, ref) => (jsx("div", { ref: ref, role: role ?? (tone === 'destructive' ? 'alert' : 'status'), className: cn(alertStyles({ tone }), className), ...rest })));
Root$2.displayName = 'Alert.Root';
const Title$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx("h4", { ref: ref, className: cn('mb-1 text-sm font-semibold leading-none', className), ...rest })));
Title$1.displayName = 'Alert.Title';
const Description$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx("p", { ref: ref, className: cn('text-sm leading-relaxed', className), ...rest })));
Description$1.displayName = 'Alert.Description';
const Alert = Object.assign(Root$2, {
    Root: Root$2,
    Title: Title$1,
    Description: Description$1,
});
const AlertTitle = Title$1;
const AlertDescription = Description$1;

const cardStyles = cva([
    'relative flex items-start gap-3 overflow-hidden rounded-lg border border-border bg-surface p-4 text-surface-foreground shadow-xs',
    'before:absolute before:inset-y-0 before:left-0 before:w-1',
], {
    variants: {
        tone: {
            info: 'before:bg-info',
            warning: 'before:bg-warning',
            critical: 'before:bg-destructive',
            success: 'before:bg-success',
        },
    },
    defaultVariants: { tone: 'info' },
});
const iconTile = cva('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', {
    variants: {
        tone: {
            info: 'bg-info-subtle text-info-foreground',
            warning: 'bg-warning-subtle text-warning-foreground',
            critical: 'bg-destructive-subtle text-destructive-foreground',
            success: 'bg-success-subtle text-success-foreground',
        },
    },
    defaultVariants: { tone: 'info' },
});
const AlertCard = React.forwardRef(({ className, tone, icon, title, description, timestamp, action, onDismiss, ...rest }, ref) => (jsxs("div", { ref: ref, className: cn(cardStyles({ tone }), className), role: "status", ...rest, children: [jsx("div", { className: iconTile({ tone }), children: jsx(Icon, { icon: icon, size: 14 }) }), jsxs("div", { className: "min-w-0 flex-1", children: [jsxs("div", { className: "flex items-center gap-3", children: [jsx("div", { className: "truncate text-sm font-semibold", children: title }), timestamp && jsx("div", { className: "ml-auto text-xs font-mono text-muted-foreground shrink-0", children: timestamp })] }), description && jsx("div", { className: "mt-1 text-sm text-muted-foreground", children: description }), action && jsx("div", { className: "mt-2 flex gap-2", children: action })] }), onDismiss && (jsx("button", { type: "button", "aria-label": "Dismiss", onClick: onDismiss, className: "text-muted-foreground hover:text-foreground", children: jsx(Icon, { icon: X, size: 14 }) }))] })));
AlertCard.displayName = 'AlertCard';

const ErrorState = ({ title = 'Something went wrong', message, action, className, compact, }) => (jsx("div", { role: "alert", className: cn('rounded-xl border border-destructive/30 bg-destructive/5', compact ? 'p-4' : 'p-5', className), children: jsxs("div", { className: "flex items-start gap-3", children: [jsx(AlertCircle, { className: "mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" }), jsxs("div", { className: "min-w-0 flex-1", children: [jsx("h3", { className: "text-sm font-semibold text-destructive", children: title }), message && jsx("p", { className: "mt-1 text-sm text-destructive/90", children: message }), action && jsx("div", { className: "mt-3", children: action })] })] }) }));

const Provider = RadixToast.Provider;
const toastStyles = cva([
    'group pointer-events-auto relative flex w-full items-center justify-between gap-3 overflow-hidden rounded-lg border p-4 pr-8 shadow-md',
    'data-[state=open]:animate-in data-[state=open]:slide-in-from-top-full data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0',
    'data-[state=closed]:animate-out data-[state=closed]:fade-out-80',
], {
    variants: {
        tone: {
            neutral: 'bg-surface border-border text-surface-foreground',
            success: 'bg-success-subtle border-success-border text-success-foreground',
            warning: 'bg-warning-subtle border-warning-border text-warning-foreground',
            destructive: 'bg-destructive-subtle border-destructive-border text-destructive-foreground',
            info: 'bg-info-subtle border-info-border text-info-foreground',
        },
    },
    defaultVariants: { tone: 'neutral' },
});
const Root$1 = React.forwardRef(({ className, tone, ...rest }, ref) => (jsx(RadixToast.Root, { ref: ref, className: cn(toastStyles({ tone }), className), ...rest })));
Root$1.displayName = 'Toast.Root';
const Title = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixToast.Title, { ref: ref, className: cn('text-sm font-semibold', className), ...rest })));
Title.displayName = 'Toast.Title';
const Description = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixToast.Description, { ref: ref, className: cn('text-sm opacity-90', className), ...rest })));
Description.displayName = 'Toast.Description';
const Action = RadixToast.Action;
const Close = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixToast.Close, { ref: ref, "aria-label": "Close", className: cn('absolute right-2 top-2 rounded-md p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring', className), ...rest, children: jsx(Icon, { icon: X, size: 14 }) })));
Close.displayName = 'Toast.Close';
const Toast = Object.assign(Root$1, {
    Root: Root$1,
    Provider,
    Title,
    Description,
    Action,
    Close,
});
const ToastProvider = Provider;
const ToastTitle = Title;
const ToastDescription = Description;
const ToastAction = Action;
const ToastClose = Close;
function ToastViewport({ className, ...rest }) {
    return (jsx(RadixToast.Viewport, { className: cn('fixed bottom-0 right-0 z-toast flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:max-w-sm', className), ...rest }));
}
function Toaster() {
    const { toasts } = useToast();
    return (jsxs(RadixToast.Provider, { children: [toasts.map(({ id, title, description, action, ...props }) => (jsxs(Root$1, { ...props, children: [jsxs("div", { className: "grid gap-1", children: [title && jsx(Title, { children: title }), description && jsx(Description, { children: description })] }), action, jsx(Close, {})] }, id))), jsx(ToastViewport, {})] }));
}

const AIInsightBanner = React.forwardRef(({ className, label, title, description, action, onDismiss, ...rest }, ref) => (jsxs("div", { ref: ref, className: cn('relative overflow-hidden rounded-lg bg-gradient-ai-banner p-5 text-white shadow-xl', 'border border-white/10', className), ...rest, children: [jsx("span", { "aria-hidden": true, className: "absolute -top-10 -right-10 h-40 w-40 rounded-full bg-primary/40 blur-3xl" }), jsx("span", { "aria-hidden": true, className: "absolute -bottom-10 -left-10 h-36 w-36 rounded-full bg-accent/40 blur-3xl" }), jsxs("div", { className: "relative", children: [jsxs("div", { className: "flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-white/90", children: [jsx(Icon, { icon: Sparkles, size: 12 }), " ", label] }), jsx("h3", { className: "mt-2 text-lg font-semibold", children: title }), jsx("p", { className: "mt-2 text-sm leading-relaxed text-white/85", children: description }), action && jsx("div", { className: "mt-3", children: action })] }), onDismiss && (jsx("button", { type: "button", "aria-label": "Dismiss", onClick: onDismiss, className: "absolute right-3 top-3 text-white/70 hover:text-white", children: jsx(Icon, { icon: X, size: 16 }) }))] })));
AIInsightBanner.displayName = 'AIInsightBanner';

const MAX_WIDTH_CLS = {
    sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg',
    xl: 'max-w-xl', '2xl': 'max-w-2xl', '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl', '5xl': 'max-w-5xl', '6xl': 'max-w-6xl', '7xl': 'max-w-7xl',
    full: 'max-w-full',
};
const PageLayout = React.forwardRef(({ className, maxWidth, isCentered, applyVerticalSpacing = true, ...rest }, ref) => (jsx("div", { ref: ref, className: cn(applyVerticalSpacing && 'space-y-6', maxWidth && MAX_WIDTH_CLS[maxWidth], isCentered && 'mx-auto', className), ...rest })));
PageLayout.displayName = 'PageLayout';

const Breadcrumbs = React.forwardRef(({ items, className, ...rest }, ref) => {
    if (!items || items.length === 0)
        return null;
    return (jsx("nav", { ref: ref, "aria-label": "Breadcrumb", className: cn('flex items-center gap-1.5 text-xs text-muted-foreground', className), ...rest, children: items.map((b, i) => {
            const isLast = i === items.length - 1;
            const content = b.label;
            return (jsxs(React.Fragment, { children: [b.href || b.onClick ? (jsx("a", { href: b.href, onClick: b.onClick, className: cn('cursor-pointer hover:text-foreground', isLast && 'pointer-events-none font-medium text-foreground'), children: content })) : (jsx("span", { className: cn(isLast && 'font-medium text-foreground'), children: content })), !isLast && jsx(Icon, { icon: ChevronRight, size: 12 })] }, i));
        }) }));
});
Breadcrumbs.displayName = 'Breadcrumbs';

const PageHeader = React.forwardRef(({ className, title, subtitle, breadcrumbs, actions, hasBorder = true, ...rest }, ref) => (jsxs("div", { ref: ref, className: cn('flex flex-col gap-4 pb-4 md:flex-row md:items-end md:justify-between', hasBorder && 'border-b border-border', className), ...rest, children: [jsxs("div", { className: "min-w-0", children: [breadcrumbs && breadcrumbs.length > 0 && (jsx(Breadcrumbs, { items: breadcrumbs, className: "mb-1.5" })), jsx("h1", { className: "truncate text-2xl font-bold tracking-tight text-foreground", children: title }), subtitle && jsx("p", { className: "mt-1 text-sm text-muted-foreground", children: subtitle })] }), actions && jsx("div", { className: "flex min-w-0 max-w-full items-center gap-2", children: actions })] })));
PageHeader.displayName = 'PageHeader';

const EntityActions = ({ primary, actions, className }) => {
    const [confirming, setConfirming] = React.useState(null);
    const menuActions = (actions ?? []).filter((a) => !a.hidden);
    const run = (action) => {
        if (action.confirm)
            setConfirming(action);
        else
            action.onClick();
    };
    const showPrimary = primary && !primary.hidden;
    if (!showPrimary && menuActions.length === 0)
        return null;
    return (jsxs("div", { className: cn('flex items-center gap-2', className), children: [showPrimary && (jsx(Button, { onClick: () => run(primary), disabled: primary.disabled, leftIcon: primary.icon, children: primary.label })), menuActions.length > 0 && (jsxs(DropdownMenu, { children: [jsx(DropdownMenuTrigger, { asChild: true, children: jsx(Button, { variant: "outline", "aria-label": "More actions", children: jsx(MoreHorizontal, { className: "h-4 w-4" }) }) }), jsx(DropdownMenuContent, { align: "end", className: "w-48", children: menuActions.map((action) => (jsxs(DropdownMenuItem, { disabled: action.disabled ?? false, onSelect: () => run(action), className: cn(action.destructive && 'text-destructive focus:text-destructive'), children: [action.icon && jsx("span", { className: "mr-2 inline-flex", children: action.icon }), action.label] }, action.id))) })] })), jsx(AlertDialog, { open: !!confirming, onOpenChange: (open) => !open && setConfirming(null), children: jsxs(AlertDialogContent, { children: [jsxs(AlertDialogHeader, { children: [jsx(AlertDialogTitle, { children: confirming?.confirm?.title }), confirming?.confirm?.description && (jsx(AlertDialogDescription, { children: confirming.confirm.description }))] }), jsxs(AlertDialogFooter, { children: [jsx(AlertDialogCancel, { children: "Cancel" }), jsx(AlertDialogAction, { className: cn(confirming?.destructive && 'bg-destructive text-destructive-foreground hover:bg-destructive/90'), onClick: () => {
                                        confirming?.onClick();
                                        setConfirming(null);
                                    }, children: confirming?.confirm?.confirmLabel ?? 'Confirm' })] })] }) })] }));
};

const Root = React.forwardRef(({ className, brand, items, activeId, onNavigate, footer, ...rest }, ref) => (jsxs("aside", { ref: ref, className: cn('flex h-full w-64 shrink-0 flex-col gap-4 border-r border-sidebar-border bg-gradient-sidebar p-4 text-sidebar-foreground', className), ...rest, children: [brand && jsx("div", { children: brand }), jsx("nav", { className: "flex flex-col gap-4", children: items.map((group) => (jsxs("div", { children: [jsx("div", { className: "mb-1 px-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-neutral-500", children: group.section }), jsx("ul", { className: "flex flex-col gap-0.5", children: group.entries.map((entry) => {
                            const active = entry.id === activeId;
                            return (jsx("li", { children: jsxs("button", { type: "button", disabled: entry.disabled, onClick: () => onNavigate?.(entry.id, entry), className: cn('relative flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors', active
                                        ? 'bg-sidebar-accent/15 text-white font-medium'
                                        : 'text-neutral-300 hover:bg-white/5 hover:text-white', entry.disabled && 'pointer-events-none opacity-50'), "data-active": active || undefined, children: [active && jsx("span", { "aria-hidden": true, className: "absolute -left-2 top-1.5 bottom-1.5 w-[3px] rounded-full bg-sidebar-primary" }), jsx(Icon, { icon: entry.icon, size: 16 }), jsx("span", { className: "truncate", children: entry.label }), entry.badge !== undefined && (jsx("span", { className: "ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-destructive px-1.5 py-0.5 text-[10px] font-semibold text-white", children: entry.badge }))] }) }, entry.id));
                        }) })] }, group.section))) }), footer && jsx("div", { className: "mt-auto", children: footer })] })));
Root.displayName = 'Sidebar.Root';
const Sidebar = { Root };

const DefaultLink = ({ children, ...rest }) => (jsx("a", { ...rest, children: children }));
function LegacyShell({ title, navigationItems = [], LinkComponent = DefaultLink, headerContent, logo, collapsedLogo, currentPath = '', onNavigate, userRole, collapsed: collapsedProp, onToggleCollapsed, children, }) {
    const [openMenu, setOpenMenu] = React.useState(null);
    const [openSections, setOpenSections] = React.useState(() => new Set());
    const [hoveredSection, setHoveredSection] = React.useState(null);
    const toggleSection = (key) => setOpenSections((prev) => {
        const next = new Set(prev);
        if (next.has(key))
            next.delete(key);
        else
            next.add(key);
        return next;
    });
    const isControlled = collapsedProp !== undefined;
    const [collapsedState, setCollapsedState] = React.useState(false);
    const collapsed = isControlled ? collapsedProp : collapsedState;
    const toggleCollapsed = () => {
        onToggleCollapsed?.();
        if (!isControlled)
            setCollapsedState((c) => !c);
    };
    React.useEffect(() => {
        const match = navigationItems.find((item) => item.subItems && item.subItems.some((s) => currentPath.startsWith(s.href.split('?')[0] ?? '')));
        if (match)
            setOpenMenu(match.href);
    }, [currentPath, navigationItems]);
    const filtered = React.useMemo(() => {
        if (!userRole)
            return navigationItems;
        return navigationItems.filter((item) => !item.roles || item.roles.includes(userRole));
    }, [navigationItems, userRole]);
    const go = (href) => onNavigate?.(href);
    const sidebarLogo = collapsed ? collapsedLogo ?? logo : logo;
    const blocks = React.useMemo(() => {
        const out = [];
        let current = null;
        filtered.forEach((item, index) => {
            if (item.isGroupHeader) {
                current = { kind: 'section', header: item, index, items: [] };
                out.push(current);
            }
            else if (item.isSeparator) {
                current = null;
            }
            else if (current) {
                current.items.push({ item, index });
            }
            else {
                out.push({ kind: 'loose', item, index });
            }
        });
        return out;
    }, [filtered]);
    const activeSectionKey = React.useMemo(() => {
        for (const block of blocks) {
            if (block.kind !== 'section')
                continue;
            const match = block.items.some(({ item }) => {
                if (currentPath === item.href)
                    return true;
                return item.subItems?.some((s) => currentPath.startsWith(s.href.split('?')[0] ?? ''));
            });
            if (match)
                return block.header.id ?? block.header.text;
        }
        return null;
    }, [blocks, currentPath]);
    React.useEffect(() => {
        if (!activeSectionKey)
            return;
        setOpenSections((prev) => {
            if (prev.has(activeSectionKey))
                return prev;
            const next = new Set(prev);
            next.add(activeSectionKey);
            return next;
        });
    }, [activeSectionKey]);
    const renderItem = (item, i, hideIcon = false) => {
        if (item.isSeparator) {
            return jsx("div", { role: "separator", className: "my-1 mx-2 border-t border-white/10" }, item.id ?? `sep-${i}`);
        }
        if (item.disabled) {
            const disabledItem = (jsxs("div", { "aria-disabled": "true", "aria-label": collapsed ? item.text : undefined, className: cn('relative flex cursor-not-allowed items-center rounded-md py-1.5 text-sm text-neutral-500', collapsed ? 'justify-center px-2' : 'gap-2 px-2'), children: [item.icon, !collapsed && jsx("span", { className: "truncate", children: item.text }), !collapsed && item.badge && jsx("span", { className: "ml-auto flex-shrink-0", children: item.badge })] }));
            return collapsed ? (jsxs(Tooltip.Root, { children: [jsx(Tooltip.Trigger, { asChild: true, children: disabledItem }), jsx(Tooltip.Content, { side: "right", children: item.text })] }, item.id ?? item.href)) : (jsx(React.Fragment, { children: disabledItem }, item.id ?? item.href));
        }
        const parentActive = currentPath === item.href ||
            (item.subItems ? item.subItems.some((s) => currentPath.startsWith(s.href.split('?')[0] ?? '')) : false);
        const menuOpen = openMenu === item.href;
        if (item.subItems && item.subItems.length > 0) {
            if (collapsed) {
                return (jsx(CollapsedFlyout, { item: item, parentActive: parentActive, currentPath: currentPath, LinkComponent: LinkComponent, go: go }, item.id ?? item.href));
            }
            return (jsxs("div", { children: [jsxs("button", { type: "button", onClick: () => setOpenMenu(menuOpen ? null : item.href), className: cn('flex w-full items-center gap-2 rounded-md py-1.5 text-left text-sm transition-colors', hideIcon ? 'pl-9 pr-2' : 'px-2', parentActive
                            ? 'bg-sidebar-accent/15 font-medium text-white'
                            : 'text-neutral-300 hover:bg-white/5 hover:text-white'), children: [!hideIcon && item.icon, jsx("span", { className: "truncate", children: item.text }), jsx(ChevronDown, { className: cn('ml-auto h-4 w-4 transition-transform', menuOpen && 'rotate-180') })] }), jsx("div", { className: cn('grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out', menuOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'), children: jsx("div", { className: "min-h-0 overflow-hidden", children: jsx("ul", { className: "space-y-0.5 pl-9 pr-1 pt-1", children: item.subItems.map((sub) => {
                                    const subActive = currentPath === sub.href;
                                    return (jsx("li", { children: jsx(LinkComponent, { href: sub.href, onClick: () => go(sub.href), className: cn('block rounded-md px-2 py-1 text-sm transition-colors', subActive
                                                ? 'text-white font-medium'
                                                : 'text-neutral-400 hover:bg-white/5 hover:text-white'), children: sub.text }) }, sub.href + sub.text));
                                }) }) }) })] }, item.id ?? item.href));
        }
        const active = currentPath === item.href;
        const linkClassName = cn('relative flex items-center rounded-md py-1.5 text-sm transition-colors', collapsed ? 'justify-center px-2' : hideIcon ? 'gap-2 pl-9 pr-2' : 'gap-2 px-2', active
            ? 'bg-sidebar-accent/15 font-medium text-white before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-full before:bg-sidebar-primary'
            : 'text-neutral-300 hover:bg-white/5 hover:text-white');
        const linkContent = (jsxs(Fragment, { children: [!hideIcon && item.icon, !collapsed && jsx("span", { className: "truncate", children: item.text }), !collapsed && item.badge && jsx("span", { className: "ml-auto flex-shrink-0", children: item.badge })] }));
        const link = item.external ? (jsx("a", { href: item.href, target: "_blank", rel: "noopener noreferrer", "aria-label": collapsed ? item.text : undefined, className: linkClassName, children: linkContent })) : (jsx(LinkComponent, { href: item.href, onClick: () => go(item.href), "aria-label": collapsed ? item.text : undefined, className: linkClassName, children: linkContent }));
        return collapsed ? (jsxs(Tooltip.Root, { children: [jsx(Tooltip.Trigger, { asChild: true, children: link }), jsx(Tooltip.Content, { side: "right", children: item.text })] }, item.id ?? item.href)) : (jsx(React.Fragment, { children: link }, item.id ?? item.href));
    };
    return (jsx(Tooltip.Provider, { delayDuration: 0, children: jsxs("div", { className: "flex h-screen overflow-hidden bg-background text-foreground", children: [jsxs("aside", { className: cn('flex h-full shrink-0 flex-col gap-1 overflow-y-auto border-r border-sidebar-border bg-gradient-sidebar text-sidebar-foreground transition-[width] duration-200 ease-out', collapsed ? 'w-16' : 'w-56'), children: [sidebarLogo && (jsx("div", { className: cn('flex h-16 shrink-0 items-center justify-center', collapsed ? 'px-2' : 'px-5'), children: sidebarLogo })), jsx("nav", { className: cn('flex flex-col gap-0.5 pb-3', collapsed ? 'px-2' : 'px-3'), children: collapsed
                                ?
                                    blocks.map((block) => {
                                        if (block.kind === 'loose')
                                            return renderItem(block.item, block.index);
                                        const sectionKey = block.header.id ?? block.header.text;
                                        return (jsx("div", { className: "mt-2 first:mt-0", children: jsx(CollapsedSectionFlyout, { header: block.header, items: block.items.map(({ item }) => item), active: activeSectionKey === sectionKey, currentPath: currentPath, LinkComponent: LinkComponent, go: go }) }, `sec-${sectionKey}`));
                                    })
                                : blocks.map((block) => {
                                    if (block.kind === 'loose')
                                        return renderItem(block.item, block.index);
                                    const sectionKey = block.header.id ?? block.header.text;
                                    const open = openSections.has(sectionKey) || hoveredSection === sectionKey;
                                    const sectionActive = activeSectionKey === sectionKey;
                                    return (jsxs("div", { className: "mt-2 first:mt-0", onMouseEnter: () => setHoveredSection(sectionKey), onMouseLeave: () => setHoveredSection((cur) => (cur === sectionKey ? null : cur)), children: [jsxs("button", { type: "button", onClick: () => toggleSection(sectionKey), "aria-expanded": open, className: cn('flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors', sectionActive
                                                    ? 'font-medium text-white'
                                                    : 'text-neutral-300 hover:bg-white/5 hover:text-white'), children: [block.header.icon && (jsx("span", { className: "flex h-5 w-5 shrink-0 items-center justify-center text-neutral-400", children: block.header.icon })), jsx("span", { className: "truncate", children: block.header.text }), jsx(ChevronDown, { className: cn('ml-auto h-3.5 w-3.5 transition-transform', !open && '-rotate-90') })] }), jsx("div", { className: cn('grid transition-[grid-template-rows] duration-200 ease-out', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'), children: jsx("div", { className: "min-h-0 overflow-hidden", children: jsx("div", { className: "flex flex-col gap-0.5", children: block.items.map(({ item, index }) => renderItem(item, index, true)) }) }) })] }, `sec-${sectionKey}`));
                                }) }), jsx("div", { className: cn('mt-auto shrink-0 pb-3', collapsed ? 'px-2' : 'px-3'), children: jsxs("button", { type: "button", onClick: toggleCollapsed, "aria-label": collapsed ? 'Expand sidebar' : 'Collapse sidebar', "aria-expanded": !collapsed, className: cn('flex w-full items-center rounded-md py-1.5 text-sm text-neutral-300 transition-colors hover:bg-white/5 hover:text-white', collapsed ? 'justify-center px-2' : 'gap-2 px-2'), children: [collapsed ? jsx(PanelLeftOpen, { className: "h-5 w-5" }) : jsx(PanelLeftClose, { className: "h-5 w-5" }), !collapsed && jsx("span", { className: "truncate", children: "Collapse" })] }) })] }), jsxs("div", { className: "flex min-w-0 flex-1 flex-col", children: [jsxs("header", { className: "flex h-16 shrink-0 items-center border-b border-border bg-surface/90 px-6 backdrop-blur", children: [title && jsx("h1", { className: "mr-4 text-lg font-semibold", children: title }), headerContent && jsx("div", { className: "flex flex-1 items-center gap-4", children: headerContent })] }), jsx("main", { className: "min-w-0 flex-1 overflow-auto bg-gradient-page bg-background", children: children })] })] }) }));
}
function CollapsedFlyout({ item, parentActive, currentPath, LinkComponent, go }) {
    const [open, setOpen] = React.useState(false);
    const closeTimer = React.useRef(undefined);
    const openNow = () => {
        if (closeTimer.current)
            clearTimeout(closeTimer.current);
        setOpen(true);
    };
    const closeSoon = () => {
        closeTimer.current = setTimeout(() => setOpen(false), 120);
    };
    React.useEffect(() => () => { if (closeTimer.current)
        clearTimeout(closeTimer.current); }, []);
    return (jsxs(Popover.Root, { open: open, onOpenChange: setOpen, children: [jsx(Popover.Trigger, { asChild: true, children: jsx("button", { type: "button", onMouseEnter: openNow, onMouseLeave: closeSoon, "aria-label": item.text, className: cn('flex w-full items-center justify-center rounded-md px-2 py-1.5 text-sm transition-colors', parentActive
                        ? 'bg-sidebar-accent/15 font-medium text-white'
                        : 'text-neutral-300 hover:bg-white/5 hover:text-white'), children: item.icon }) }), jsxs(Popover.Content, { side: "right", align: "start", sideOffset: 8, onOpenAutoFocus: (e) => e.preventDefault(), onMouseEnter: openNow, onMouseLeave: closeSoon, className: "w-56 p-1", children: [jsx("div", { className: "px-2 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground", children: item.text }), jsx("ul", { className: "space-y-0.5", children: item.subItems?.map((sub) => {
                            const subActive = currentPath === sub.href;
                            return (jsx("li", { children: jsx(LinkComponent, { href: sub.href, onClick: () => {
                                        go(sub.href);
                                        setOpen(false);
                                    }, className: cn('block rounded-md px-2 py-1.5 text-sm transition-colors', subActive
                                        ? 'bg-primary-subtle font-medium text-primary'
                                        : 'text-surface-foreground hover:bg-muted'), children: sub.text }) }, sub.href + sub.text));
                        }) })] })] }));
}
function CollapsedSectionFlyout({ header, items, active, currentPath, LinkComponent, go }) {
    const [open, setOpen] = React.useState(false);
    const closeTimer = React.useRef(undefined);
    const openNow = () => {
        if (closeTimer.current)
            clearTimeout(closeTimer.current);
        setOpen(true);
    };
    const closeSoon = () => {
        closeTimer.current = setTimeout(() => setOpen(false), 120);
    };
    React.useEffect(() => () => { if (closeTimer.current)
        clearTimeout(closeTimer.current); }, []);
    const renderLink = (href, text, indented = false) => {
        const linkActive = currentPath === href;
        return (jsx("li", { children: jsx(LinkComponent, { href: href, onClick: () => {
                    go(href);
                    setOpen(false);
                }, className: cn('block rounded-md py-1.5 text-sm transition-colors', indented ? 'pl-5 pr-2' : 'px-2', linkActive
                    ? 'bg-primary-subtle font-medium text-primary'
                    : 'text-surface-foreground hover:bg-muted'), children: text }) }, href + text));
    };
    return (jsxs(Popover.Root, { open: open, onOpenChange: setOpen, children: [jsx(Popover.Trigger, { asChild: true, children: jsx("button", { type: "button", onMouseEnter: openNow, onMouseLeave: closeSoon, "aria-label": header.text, className: cn('flex w-full items-center justify-center rounded-md px-2 py-1.5 text-sm transition-colors', active
                        ? 'bg-sidebar-accent/15 font-medium text-white'
                        : 'text-neutral-300 hover:bg-white/5 hover:text-white'), children: header.icon }) }), jsxs(Popover.Content, { side: "right", align: "start", sideOffset: 8, onOpenAutoFocus: (e) => e.preventDefault(), onMouseEnter: openNow, onMouseLeave: closeSoon, className: "w-56 p-1", children: [jsx("div", { className: "px-2 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground", children: header.text }), jsx("ul", { className: "space-y-0.5", children: items.map((item) => item.subItems && item.subItems.length > 0
                            ? item.subItems.map((sub) => renderLink(sub.href, sub.text, true))
                            : renderLink(item.href, item.text)) })] })] }));
}
const AppShell = React.forwardRef((props, ref) => {
    if ('title' in props && props.title !== undefined) {
        return jsx(LegacyShell, { ...props });
    }
    const { className, sidebar, header, children, ...rest } = props;
    return (jsxs("div", { ref: ref, className: cn('flex h-screen overflow-hidden bg-background text-foreground', className), ...rest, children: [sidebar, jsxs("div", { className: "flex min-w-0 flex-1 flex-col", children: [header && (jsx("header", { className: "sticky top-0 z-sticky flex h-14 shrink-0 items-center justify-between border-b border-border bg-surface/90 px-6 backdrop-blur", children: header })), jsx("main", { className: "min-w-0 flex-1 overflow-auto bg-gradient-page bg-background", children: children })] })] }));
});
AppShell.displayName = 'AppShell';

const ResourceHero = React.forwardRef(({ icon: Icon, iconClassName, title, subtitle, badges, metric, actions, footer, className }, ref) => (jsxs("div", { ref: ref, className: cn('rounded-lg border border-border/60 bg-surface shadow-sm', className), children: [jsxs("div", { className: "flex flex-col gap-6 p-6 md:flex-row md:items-center md:justify-between", children: [jsxs("div", { className: "flex min-w-0 flex-1 items-start gap-4", children: [Icon && (jsx("div", { className: cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground', iconClassName), children: jsx(Icon, { className: "h-6 w-6" }) })), jsxs("div", { className: "min-w-0 flex-1", children: [jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [jsx("h1", { className: "truncate text-2xl font-bold tracking-tight text-foreground", children: title }), badges] }), subtitle && (jsx("div", { className: "mt-1 text-sm text-muted-foreground", children: subtitle }))] })] }), (metric || actions) && (jsxs("div", { className: "flex shrink-0 items-center gap-6", children: [metric && (jsxs("div", { className: "text-right", children: [jsx("div", { className: "text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground", children: metric.label }), jsx("div", { className: "mt-0.5 text-2xl font-bold tracking-tight text-foreground", children: metric.value }), metric.caption && (jsx("div", { className: "mt-0.5 text-xs font-medium", children: metric.caption }))] })), actions && jsx("div", { className: "flex items-center gap-2", children: actions })] }))] }), footer && jsx("div", { className: "px-6 pb-6", children: footer })] })));
ResourceHero.displayName = 'ResourceHero';

const CHART_COLORS = [
    'var(--primary)',
    'var(--success-default)',
    'var(--warning-default)',
    'var(--destructive-default)',
    'var(--accent)',
];
function ThemedTooltipContent({ active, payload, label }) {
    if (!active || !payload || payload.length === 0)
        return null;
    return (jsxs("div", { className: "rounded-md border border-border bg-surface p-2 text-xs text-surface-foreground shadow-md", children: [label != null && jsx("div", { className: "mb-1 font-semibold", children: label }), jsx("div", { className: "flex flex-col gap-0.5", children: payload.map((p, i) => (jsxs("div", { className: "flex items-center gap-2", children: [jsx("span", { className: "inline-block h-2 w-2 rounded-full", style: { background: String(p.color) } }), jsx("span", { className: "text-muted-foreground", children: p.name }), jsx("span", { className: "ml-auto font-mono", children: p.value })] }, i))) })] }));
}
const ThemedTooltip = (props) => (jsx(Tooltip$1, { content: jsx(ThemedTooltipContent, {}), cursor: { stroke: 'var(--border-strong)', strokeDasharray: '3 3' }, ...props }));
const ThemedGrid = (props) => (jsx(CartesianGrid, { stroke: "var(--border)", strokeDasharray: "3 3", vertical: false, ...props }));
const ThemedXAxis = (props) => (jsx(XAxis, { stroke: "var(--muted-foreground)", tick: { fill: 'var(--muted-foreground)', fontSize: 11 }, tickLine: false, axisLine: false, ...props }));
const ThemedYAxis = (props) => (jsx(YAxis, { stroke: "var(--muted-foreground)", tick: { fill: 'var(--muted-foreground)', fontSize: 11 }, tickLine: false, axisLine: false, ...props }));
const Chart = {
    Tooltip: ThemedTooltip,
    Grid: ThemedGrid,
    XAxis: ThemedXAxis,
    YAxis: ThemedYAxis,
};

const ThemeContext = React.createContext(null);
const STORAGE_KEY = 'synapse-theme';
const VALID_THEMES = ['light', 'dark'];
function ThemeProvider({ defaultTheme = 'light', storageKey = STORAGE_KEY, children, }) {
    const [theme, setThemeState] = React.useState(() => {
        const stored = typeof window === 'undefined'
            ? null
            : window.localStorage.getItem(storageKey);
        return (stored && VALID_THEMES.includes(stored)) ? stored : defaultTheme;
    });
    React.useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        window.localStorage.setItem(storageKey, theme);
    }, [theme, storageKey]);
    const setTheme = React.useCallback((next) => {
        setThemeState(next);
    }, []);
    const value = React.useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
    return jsx(ThemeContext.Provider, { value: value, children: children });
}
function useTheme() {
    const ctx = React.useContext(ThemeContext);
    if (!ctx)
        throw new Error('useTheme must be used within a <ThemeProvider>.');
    return ctx;
}
const themeInitScript = `(function(){try{var t=localStorage.getItem('${STORAGE_KEY}');if(t==='dark'||t==='light'){document.documentElement.setAttribute('data-theme',t);}else{document.documentElement.setAttribute('data-theme','light');}}catch(e){}})();`;

const THEMES = [
    { name: 'light', label: 'Light', icon: Sun },
    { name: 'dark', label: 'Dark', icon: Moon },
];
const ThemeSwitcher = React.forwardRef(({ className, ...rest }, ref) => {
    const { theme, setTheme } = useTheme();
    return (jsx("div", { ref: ref, role: "group", "aria-label": "Theme", className: cn('inline-flex items-center gap-1 rounded-lg border border-border bg-surface p-1', className), ...rest, children: THEMES.map(({ name, label, icon }) => {
            const active = name === theme;
            return (jsxs("button", { type: "button", "aria-label": label, "aria-pressed": active, onClick: () => setTheme(name), className: cn('inline-flex flex-1 items-center justify-center gap-2 rounded-md px-2.5 py-1 text-sm transition-colors', active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'), children: [jsx(Icon, { icon: icon, size: 14 }), jsx("span", { children: label })] }, name));
        }) }));
});
ThemeSwitcher.displayName = 'ThemeSwitcher';

const Active = ({ className, size = 20, ...rest }) => (jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", className: cn('text-primary', className), ...rest, children: [jsx("circle", { cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "2" }), jsx("circle", { cx: "12", cy: "12", r: "4", fill: "currentColor" })] }));
const Complete = ({ className, size = 20, ...rest }) => (jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", className: cn('text-success', className), ...rest, children: [jsx("circle", { cx: "12", cy: "12", r: "10", fill: "currentColor" }), jsx("path", { d: "M8 12l3 3 5-6", stroke: "white", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }));
const Pending = ({ className, size = 20, ...rest }) => (jsx("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", className: cn('text-muted-foreground', className), ...rest, children: jsx("circle", { cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "2", strokeDasharray: "3 3" }) }));
const Skipped = ({ className, size = 20, ...rest }) => (jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", className: cn('text-muted-foreground', className), ...rest, children: [jsx("circle", { cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "2" }), jsx("path", { d: "M8 8l8 8M16 8l-8 8", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" })] }));
const WorkflowIcons = { Active, Complete, Pending, Skipped };

export { AIInsightBanner, Accordion, AccordionContent, AccordionItem, AccordionTrigger, Alert, AlertCard, AlertDescription, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger, AlertTitle, AppShell, Avatar, AvatarFallback, AvatarImage, Badge, Breadcrumbs, Button, CHART_COLORS, Calendar, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Chart, Checkbox, Collapsible, CollapsibleContent, CollapsibleTrigger, ColumnSettingsMenu, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, ContextMenu, ContextMenuCheckboxItem, ContextMenuContent, ContextMenuGroup, ContextMenuItem, ContextMenuLabel, ContextMenuPortal, ContextMenuRadioGroup, ContextMenuRadioItem, ContextMenuSeparator, ContextMenuShortcut, ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger, ContextMenuTrigger, DataList, DataListItem, DataTable, DatePicker, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, EmptyState, EntityActions, ErrorState, FilterBar, Form, FormControl, FormDescription, FormDrawer, FormField, FormItem, FormLabel, FormMessage, Icon, IconButton, Input$1 as Input, Label$2 as Label, Menubar, MenubarContent, MenubarItem, MenubarMenu, MenubarSeparator, MenubarShortcut, MenubarTrigger, MetricRibbon, PageHeader, PageLayout, Pagination, Popover, PopoverAnchor, PopoverClose, PopoverContent, PopoverTrigger, Progress, RadioGroup$2 as RadioGroup, RadioGroupItem, ResourceHero, SPACING_BUTTON_GROUP_GAP, SPACING_CARD_PADDING, SPACING_FORM_FIELD, SPACING_HEADING_H1, SPACING_HEADING_H2, SPACING_HEADING_H3, SPACING_INFO_BOX_PADDING, SPACING_INLINE_GAP, SPACING_KPI_GRID_GAP, SPACING_KPI_GRID_GAP_PREMIUM, SPACING_PAGE_CONTAINER, SPACING_PAGE_SECTION, SPACING_PRESETS, SPACING_SECTION_ELEMENT, SPACING_STACK_VERTICAL, SPACING_TABLE_CELL, SPACING_TABLE_TO_PAGINATION, SPACING_WORKFLOW_STEPPER, ScrollArea, ScrollBar, Select, SelectContent, SelectGroup, SelectItem, SelectMultiValue, SelectTrigger, SelectValue, Separator$4 as Separator, Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetTitle, SheetTrigger, Sidebar, Skeleton, Slider, StatusPill, SummaryCardGrid, Switch, Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, ThemeProvider, ThemeSwitcher, Toast, ToastAction, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport, Toaster, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, WorkflowIcons, badgeStyles, buttonStyles, cardStyles$1 as cardStyles, cn, colors, darkTheme, duration, easing, fontSize, fontWeight, fonts, iconButtonStyles, inputStyles, keyframes, lightTheme, lineHeight, radius, semanticSpacing, shadow, spacing, themeInitScript, themes, toast, tracking, typography, useTheme, useToast, zIndex };
//# sourceMappingURL=index.js.map
